import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { TimeTagService } from '../services/timeTagService';
import { User, Class, ClassMember } from '../types/alarm';
import { boardService, NotebookCategory, type NoticeNoteField } from '../services/boardService';
import { userDataService } from '../services/userDataService';
import { defaultSetService } from '../services/defaultSetService';
import { RelatedStudentInfo } from '../types/relatedStudent';
import StudentTagSelectModal from '../components/StudentTagSelectModal';
import CachedProfileImage from '../components/CachedProfileImage';
import { TimeTagSelectModal } from '../components/TimeTagSelectModal';
import { CalendarComponent } from '../components/CalendarComponent';
import { TimeTag } from '../components/TimeTagSettingsModal';
import LicenseLimitModal from '../components/LicenseLimitModal';
import { checkBoardNoticeAccess, getLicenseLimits } from '../utils/licenseLimits';
import {
  MemberProfileDisplayMode,
  readMemberProfileDisplayMode,
  resolveMemberProfileSource
} from '../utils/memberProfileDisplay';
import { getOrderedMemberGroups } from '../utils/memberGroupOrder';

/** 노트북 잠금 비밀번호를 SHA-256 해시로 변환 (검증용) */
async function hashLockPassword(password: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(password));
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

interface BoardPageProps {
  user: User | null;
  currentClass: Class | null;
  currentMemberGroup?: string;
  onTimerModalOpen?: () => void;
  onDiceModalOpen?: () => void;
  onLicenseClick?: () => void;
}

const BoardContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: var(--color-board-bg);
  display: flex;
  flex-direction: column;
  color: var(--color-text-primary);
  padding-bottom: 64px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 50px 20px 12px 20px;
  width: 100%;
  position: relative;
  
  @media (max-width: 800px) {
    padding: 46px 20px 12px 20px;
  }
  
  @media (max-width: 600px) {
    padding: 46px 16px 12px 16px;
  }
`;

const ButtonContentRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  
  @media (max-width: 800px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const ButtonContainerDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
  margin-top: 12px;
`;

const DateTimeButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  z-index: 1;
  
  @media (max-width: 800px) {
    justify-content: center;
    width: 100%;
    gap: 8px;
  }
  
  @media (max-width: 600px) {
    gap: 3px;
  }
`;

const CircularButtonGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  position: absolute;
  right: 0;
  
  @media (max-width: 800px) {
    position: static;
    width: 100%;
    justify-content: center;
  }
  
  @media (max-width: 600px) {
    gap: 16px;
  }
`;

/* 원형 버튼 호버 시 아이콘 사라지고 텍스트가 버튼 가운데 표시 */
const BoardCircularTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  /* 호버 시 아이콘 숨김 */
  &:hover button img {
    opacity: 0;
  }

  /* 호버 시 버튼 가운데에 텍스트 표시 */
  &[data-tooltip]::after {
    content: attr(data-tooltip);
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 11px;
    font-weight: 800;
    color: var(--color-accent-strong);
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
  }

  :root:not([data-theme='light']) &[data-tooltip]::after {
    color: #ffffff;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const CircularButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    border-color: var(--color-text-muted);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  img {
    width: 26px;
    height: 26px;
    transition: opacity 0.15s ease;
    /* 라이트모드: 메인 테마 초록(#10504A) 근사 */
    filter: invert(18%) sepia(25%) saturate(1386%) hue-rotate(129deg) brightness(88%) contrast(98%);
  }
  
  /* 다크모드: 아이콘을 흰색으로 */
  :root:not([data-theme='light']) & img {
    filter: brightness(0) invert(1);
  }
  
  @media (max-width: 800px) {
    width: 42px;
    height: 42px;
    
    img {
      width: 22px;
      height: 22px;
    }
  }
  
  @media (max-width: 600px) {
    width: 40px;
    height: 40px;
    
    img {
      width: 20px;
      height: 20px;
    }
  }
`;

const DateDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 160px;
  padding: 8px 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  height: 48px;
  box-sizing: border-box;
  flex-shrink: 0;
  
  @media (max-width: 800px) {
    flex: 1;
    width: auto;
    min-width: 0;
  }
  
  @media (max-width: 420px) {
    padding: 4px 6px;
    gap: 3px;
    border-radius: 6px;
  }
  
  @media (max-width: 360px) {
    padding: 2px 4px;
    gap: 2px;
    font-size: clamp(8px, 2vw, 14px);
  }

  &:hover {
    border-color: rgba(var(--color-accent-rgb), 0.3);
  }
  
  /* 다크모드: 호버 시 테두리 색상을 회색으로 */
  :root:not([data-theme='light']) &:hover {
    border-color: #505050;
  }
`;

const DateDisplayText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  font-size: 12px;
  
  @media (max-width: 420px) {
    font-size: clamp(10px, 2.5vw, 12px);
  }
`;

const DateYearRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  padding: 0;
  margin: 0;
  line-height: 1;
  
  @media (max-width: 420px) {
    font-size: clamp(12px, 3vw, 16px);
  }
`;

const DateMonthDayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-gray);
  padding: 0;
  margin: 0;
  line-height: 1;
  
  @media (max-width: 420px) {
    font-size: clamp(10px, 2.5vw, 14px);
    gap: 2px;
  }
`;

const DatePart = styled.span`
  cursor: pointer;
  padding: 2px 2px;
  border-radius: 4px;
  transition: all 0.2s ease;
  user-select: none;
  font-size: 14px;

  &:hover {
    background-color: rgba(var(--color-accent-rgb), 0.1);
    color: #10504A;
  }

  &:active {
    background-color: rgba(var(--color-accent-rgb), 0.2);
  }
  
  /* 다크모드: 호버 시 아이콘과 텍스트 색상을 회색으로 */
  :root:not([data-theme='light']) &:hover {
    background-color: rgba(128, 128, 128, 0.2);
    color: #9a9a9a;
  }
  
  @media (max-width: 420px) {
    font-size: clamp(10px, 2.5vw, 14px);
    padding: 1px 1px;
  }
`;

const DateInput = styled.input<{ $fieldType?: 'year' | 'month' | 'day' }>`
  border: 2px solid #10504A;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  background: var(--color-surface);
  outline: none;
  width: ${props => props.$fieldType === 'year' ? '60px' : '32px'};
  text-align: center;

  &:focus {
    border-color: #0D4039;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.2);
  }
`;

const CalendarIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;

  &:hover {
    background-color: rgba(var(--color-accent-rgb), 0.1);
    color: #10504A;
  }

  &:active {
    background-color: rgba(var(--color-accent-rgb), 0.2);
  }
  
  /* 다크모드: 호버 시 아이콘 색상을 회색으로 */
  :root:not([data-theme='light']) &:hover {
    background-color: rgba(128, 128, 128, 0.2);
    color: #9a9a9a;
  }

  svg {
    width: 32px;
    height: 32px;
  }
  
  @media (max-width: 360px) {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    
    svg {
      width: 28px;
      height: 28px;
    }
  }
`;

const CalendarModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2004;
`;

const CalendarModalContent = styled.div`
  background: var(--color-surface);
  border: var(--modal-shell-border);
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  z-index: 2005;
  box-shadow: var(--modal-shadow);
`;

const TimeTagSelectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s ease;
  width: 160px;
  justify-content: flex-start;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 48px;
  box-sizing: border-box;
  
  @media (max-width: 420px) {
    gap: 14px;
  }
  flex-shrink: 0;
  
  @media (max-width: 800px) {
    flex: 1;
    width: auto;
    min-width: 0;
  }
  
  @media (max-width: 420px) {
    padding: 4px 6px;
    gap: 3px;
    border-radius: 6px;
  }
  
  @media (max-width: 360px) {
    padding: 2px 4px;
    gap: 2px;
    font-size: clamp(8px, 2vw, 14px);
  }

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    border-color: rgba(var(--color-accent-rgb), 0.3);
  }
`;

const TimeTagIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--color-text-primary);
  border: 3px solid var(--color-text-primary);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  
  @media (max-width: 360px) {
    width: 28px;
    height: 28px;
    font-size: 10px;
    border-width: 2px;
  }
`;

const TimeTagInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  
  @media (max-width: 360px) {
    gap: 1px;
  }
`;

const TimeTagName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 420px) {
    font-size: clamp(12px, 3vw, 16px);
  }
`;

const TimeTagTime = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-gray);
  margin: 0;
  padding: 0;
  line-height: 1;
  
  @media (max-width: 420px) {
    font-size: clamp(10px, 2.5vw, 14px);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--color-border);
  
  h3 {
    margin: 0;
    color: var(--color-accent-text);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--color-text-gray);
  
  &:hover {
    color: var(--color-text-primary);
  }
`;

const NotebookFormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 8px 20px 20px 20px;
  gap: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  user-select: none;
  
  textarea {
    user-select: text !important;
    -webkit-user-select: text !important;
    -moz-user-select: text !important;
    -ms-user-select: text !important;
  }
`;

const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
  flex-wrap: wrap;
  width: 100%;
  position: relative;

  @media (max-width: 800px) {
    width: 100%;
    justify-content: center;
    flex-direction: column;
    gap: 12px;
    align-items: center;
  }
`;

const CategoryButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex: none;
  justify-content: flex-start;

  @media (max-width: 800px) {
    justify-content: center;
    order: 1;
  }
`;

const NavButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 800px) {
    order: 2;
  }
`;

const CategoryNavButton = styled.button`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  max-width: 36px;
  max-height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.45);
  color: white;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  box-sizing: border-box;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.45);
    }
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    max-width: 32px;
    max-height: 32px;
    font-size: 14px;
  }
`;

const CategoryListButton = styled(CategoryNavButton)`
  border-radius: 12px;
  overflow: hidden;

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
    filter: invert(1);
  }
`;

const EmptyCategoryBlock = styled.div`
  padding: 16px;
  border: 1.5px dashed rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  text-align: center;
  font-weight: 500;
`;

const SpringLines = styled.div<{ $isActive: boolean }>`
  width: 8px;
  min-height: 36px;
  height: 36px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 6px 0;
  margin-right: -4px;
  flex-shrink: 0;
  align-self: stretch;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    min-height: 32px;
    height: 32px;
    padding: 4px 0;
  }
`;

const SpringLine = styled.div<{ $isActive: boolean }>`
  width: 100%;
  height: 1.5px;
  background: ${props => props.$isActive ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 0.75px;
  transition: background 0.2s ease;
`;

const CategoryButton = styled.button<{ $isActive: boolean; $isDisabled?: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${props => props.$isActive ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.45)'};
  background: ${props => props.$isActive ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  color: ${props => props.$isDisabled ? 'rgba(255, 255, 255, 0.4)' : 'white'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  min-height: 36px;
  height: 36px;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    min-height: 32px;
    height: 32px;
    padding: 4px 12px;
    font-size: 12px;
    gap: 6px;
  }
  
  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    
    @media (max-width: 768px) {
      width: 14px;
      height: 14px;
    }
  }
`;

const CategoryButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  
  &:hover {
    ${SpringLine} {
      background: rgba(255, 255, 255, 0.5) !important;
    }
    
    ${CategoryButton} {
      background: rgba(255, 255, 255, 0.15) !important;
      border-color: rgba(255, 255, 255, 0.5) !important;
    }
  }
  
  /* 활성화된 버튼은 호버 시에도 흰색 유지 */
  &[data-active="true"]:hover {
    ${SpringLine} {
      background: rgba(255, 255, 255, 1) !important;
    }
    
    ${CategoryButton} {
      border-color: rgba(255, 255, 255, 1) !important;
    }
  }
`;

const RelatedStudentButton = styled.button`
  padding: 10px 20px 10px 16px;
  border: 1.5px dashed rgba(255, 255, 255, 0.5);
  background: transparent;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-height: 44px;
  max-width: 100%;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  overflow: hidden;
  position: relative;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.7);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 6px 20px 6px 12px;
    min-height: 38px;
    font-size: 13px;
  }
`;

const RelatedStudentScrollContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  overflow-y: hidden;
  flex: 1;
  min-width: 0;
  width: 100%;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
  
  /* 스크롤 영역에서 드래그 가능하게 */
  -webkit-overflow-scrolling: touch;
`;

const StudentTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.45);
  white-space: nowrap;
  flex-shrink: 0;
`;

const AddStudentTag = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const GroupNameLabel = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  margin-right: 4px;
  opacity: 0.8;
`;

const NoteTextArea = styled.textarea`
  flex: 1;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: white;
  font-size: 20px;
  font-family: inherit;
  resize: none;
  outline: none !important;
  line-height: 1.6;
  min-height: 200px;
  user-select: text !important;
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  -ms-user-select: text !important;
  -webkit-touch-callout: default !important;
  box-shadow: none !important;
  cursor: text;
  pointer-events: auto;
  -webkit-user-drag: none;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &::selection {
    background: rgba(255, 255, 255, 0.3);
    color: white;
  }
  
  &::-moz-selection {
    background: rgba(255, 255, 255, 0.3);
    color: white;
  }
  
  &:focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &:focus-visible {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &:active {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
`;

const ActionButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.35);

  @media (max-width: 800px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  /* 데스크탑에서 전체공지의 경우 오른쪽 정렬 */
  @media (min-width: 769px) {
    justify-content: flex-end;
  }
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  background: transparent;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  min-height: 44px;
  width: 120px;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 6px 16px;
    min-height: 38px;
    font-size: 13px;
  }
`;

const FontSizeButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 800px) {
    width: 100%;
    gap: 8px;

    ${ActionButton} {
      flex: 1;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    gap: 8px;

    ${ActionButton} {
      flex: 1;
    }
  }
`;

const SaveButton = styled.button<{ disabled?: boolean }>`
  padding: 10px 20px;
  border: none;
  background: ${props => props.disabled ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  min-height: 44px;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  
  @media (min-width: 769px) {
    /* 데스크탑에서는 FontSizeButtonRow 바로 다음에 배치 */
    margin-left: 0;
  }
  
  @media (max-width: 800px) {
    /* 800px 이하에서 SaveAndNewButtonRow 내부에서 등분 */
    flex: 1;
    margin-left: 0;
    padding: 6px 16px;
    min-height: 38px;
    font-size: 13px;
  }

  @media (max-width: 768px) {
    /* 모바일에서는 별도 행으로 표시 */
    width: 100%;
    margin-left: 0;
    padding: 6px 16px;
    min-height: 38px;
    font-size: 13px;
  }
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    /* 모바일에서는 SaveAndNewButtonRow 내부에서 등분 */
    flex: 1;
    margin-left: 0;
    padding: 6px 16px;
    min-height: 38px;
    font-size: 13px;
  }
`;

const NewNoteButton = styled.button`
  padding: 10px 20px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  background: transparent;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  min-height: 44px;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  
  @media (min-width: 801px) {
    margin-left: 0;
  }

  @media (max-width: 800px) {
    /* 800px 이하에서 SaveAndNewButtonRow 내부에서 등분 */
    flex: 1;
    margin-left: 0;
    padding: 6px 16px;
    min-height: 38px;
    font-size: 13px;
  }

  @media (max-width: 768px) {
    /* 모바일에서는 SaveAndNewButtonRow 내부에서 등분 */
    flex: 1;
    margin-left: 0;
    padding: 6px 16px;
    min-height: 38px;
    font-size: 13px;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SaveAndNewButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 800px) {
    /* 800px 이하에서 전체 너비로 등분 */
    width: 100%;
    gap: 8px;

    ${SaveButton}, ${NewNoteButton} {
      flex: 1;
    }
  }

  @media (max-width: 768px) {
    /* 모바일에서는 전체 너비로 등분 */
    width: 100%;
    gap: 8px;

    ${SaveButton}, ${NewNoteButton} {
      flex: 1;
    }
  }
`;

// 일괄 노트 작성 모달 스타일
const BulkIndividualNoticeModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BulkIndividualNoticeModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
`;

const BulkIndividualNoticeModalContent = styled.div`
  background: var(--color-surface);
  border: var(--modal-shell-border);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  z-index: 2001;
  box-shadow: var(--modal-shadow);
`;

const BulkIndividualNoticeFormGroup = styled.div`
  margin-bottom: 20px;
`;

const BulkIndividualNoticeTextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  background: var(--color-student-block-element-bg);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const BulkIndividualNoticeModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: stretch;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--color-border);
  
  button {
    flex: 1;
  }
`;

const BulkIndividualNoticeCloseButton = styled.button`
  padding: 10px 20px;
  border: 2px solid var(--color-border);
  background: transparent;
  color: var(--color-text-primary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-student-block-element-bg);
    border-color: var(--color-text-muted);
  }
`;

const BulkIndividualNoticeApplyButton = styled.button`
  padding: 10px 20px;
  border: none;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-accent-hover);
  }
`;

// 노트 목록 모달 스타일
const NoteListModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 20px;
  z-index: 2006;
`;

const NoteListModalContent = styled.div`
  background: var(--color-surface);
  border: var(--modal-shell-border);
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 800px;
  position: relative;
  z-index: 2007;
  box-shadow: var(--modal-shadow);
  display: flex;
  flex-direction: column;
  margin: auto;
`;

// 공지 노트(페이지 내 섹션) 스타일 - 노트북과 동일한 스타일 사용
const NoticeFormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 8px 20px 20px 20px;
  gap: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  user-select: none;
  
  textarea {
    user-select: text !important;
    -webkit-user-select: text !important;
    -moz-user-select: text !important;
    -ms-user-select: text !important;
  }
`;

// 개별공지(학생별) 입력 UI - 포인트 조회 페이지(데이터 입력/학생 노트 입력 모드)와 동일한 그리드/블럭 구조
const IndividualNoticeStudentsGrid = styled.div<{ $alignment?: 'left' | 'center' }>`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
  justify-content: ${props => props.$alignment === 'center' ? 'center' : 'flex-start'};
  width: 100%;
  background: transparent;
  border-radius: 8px;
  
  & > * {
    flex: 0 0 calc((100% - 32px - 64px) / 5);
    min-width: 0;
    box-sizing: border-box;
  }
  
  @media (max-width: 1200px) {
    & > * {
      flex: 0 0 calc((100% - 32px - 48px) / 4);
    }
  }
  
  @media (max-width: 900px) {
    & > * {
      flex: 0 0 calc((100% - 32px - 32px) / 3);
    }
  }
  
  @media (max-width: 600px) {
    & > * {
      flex: 0 0 calc((100% - 32px - 16px) / 2);
    }
  }
  
  @media (max-width: 400px) {
    & > * {
      flex: 0 0 calc(100% - 32px);
    }
  }
`;

// 자리 모드용 동적 그리드
const IndividualNoticeSeatGrid = styled.div<{ $gridTemplateColumns?: string; $gridTemplateRows?: string }>`
  display: grid;
  grid-template-columns: ${props => props.$gridTemplateColumns || 'repeat(auto-fill, minmax(260px, 1fr))'};
  grid-template-rows: ${props => props.$gridTemplateRows || 'auto'};
  gap: 2px;
  padding: 4px;
  justify-items: stretch;
  align-items: stretch;
  align-content: start;
  width: 100%;
  min-width: 1440px;
  /* 내용(사용된 row 수)에 따라 높이가 자연스럽게 줄어들도록 aspect-ratio 제거 */
  height: fit-content;
`;

// 번호/자리 토글 버튼용 스타일 컴포넌트들
const ViewModeToggleButton = styled.div<{ $isSeatMode: boolean; $isDisabled?: boolean }>`
  display: flex;
  background: ${props => props.$isDisabled ? 'rgba(var(--color-accent-rgb), 0.3)' : 'var(--color-accent-strong)'};
  border-radius: 13px;
  padding: 2px;
  position: relative;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  height: 26px;
  min-width: 120px;
  width: auto;
  
  /* 라이트 모드: 50% 흰색 테두리 */
  :root[data-theme='light'] & {
    border: 1px solid rgba(255, 255, 255, 0.5);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isSeatMode ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: ${props => props.$isDisabled ? 'rgba(255, 255, 255, 0.5)' : 'var(--color-surface)'};
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }
`;

const ViewModeToggleOption = styled.div<{ $isActive: boolean; $isDisabled?: boolean }>`
  flex: 1;
  padding: 2px 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => {
    if (props.$isDisabled) {
      return props.$isActive ? 'rgba(var(--color-accent-rgb), 0.4)' : 'rgba(255, 255, 255, 0.6)';
    }
    return props.$isActive ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)';
  }};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  
  @media (max-width: 600px) {
    padding: 2px 10px;
    font-size: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 2px 8px;
    font-size: 10px;
  }
`;

const ViewModeToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 16px;
  width: 100%;
  margin-bottom: 4px;
`;

// 자리 모드용 스크롤 영역 (ChatPage 참고)
const SeatModeScrollArea = styled.div`
  width: 100%;
  min-width: 0;
  background: transparent;
  border-radius: 8px;
  /* 상/하단 빈 행 trim 이후에도 시각적 여백을 유지 */
  padding: 16px 2px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  /* 가로 스크롤바를 아주 컴팩트하게 (흰색 + 불투명도) */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(255, 255, 255, 0.25) transparent; /* Firefox: thumb / track */

  &::-webkit-scrollbar {
    height: 6px; /* horizontal scrollbar height */
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.22);
    border-radius: 999px;
  }

  &:hover::-webkit-scrollbar-thumb {
    background-color: var(--color-block-border);
  }
`;

const SeatModeGridWrapper = styled.div`
  min-width: 1440px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
`;

const RotateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const RotateButton = styled.button`
  width: 60px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  /* 라이트 모드: 포인트 조회 페이지와 동일한 스타일 (균형감 맞춤) */
  :root[data-theme='light'] & {
    border: 1px solid rgba(255, 255, 255, 0.45);
    background: transparent;
    color: white;
    border-radius: 6px;
  }

  :root[data-theme='light'] &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }

  :root[data-theme='light'] &:active {
    transform: none;
  }

  /* 다크 모드: 현재 스타일 유지 */
  :root:not([data-theme='light']) & {
    &:hover {
      border-color: var(--color-border);
      background: var(--color-grid-alignment-container);
    }

    &:active {
      transform: none;
    }
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const RotateIconWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: var(--color-grid-alignment-active-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  /* 라이트 모드: 흰색 블럭 배경만 80% 투명 */
  :root[data-theme='light'] & {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const RotateArrowIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transform: scale(1);
  transition: opacity 0.3s ease, transform 0.3s ease;

  svg {
    width: 22px;
    height: 22px;
  }

  ${RotateButton}:hover & {
    opacity: 0;
    transform: scale(0.8);
  }
`;

const RotateNumberIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: currentColor;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.3s ease, transform 0.3s ease;

  ${RotateButton}:hover & {
    opacity: 1;
    transform: scale(1);
  }
`;

// 그리드 정렬 토글 컨테이너
const GridAlignmentContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
`;

// 그리드 정렬 토글 버튼
const GridAlignmentToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 2px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  /* 라이트 모드: 180도 버튼과 동일한 스타일 (흰색에 불투명도) */
  :root[data-theme='light'] & {
    border: 1px solid rgba(255, 255, 255, 0.45);
    background: transparent;
    color: white;
  }

  :root[data-theme='light'] &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }

  :root[data-theme='light'] &:active {
    transform: none;
  }

  /* 다크 모드: 현재 스타일 유지 */
  :root:not([data-theme='light']) & {
    &:hover {
      border-color: var(--color-border);
      background: var(--color-grid-alignment-container);
    }

    &:active {
      transform: none;
    }
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 그리드 정렬 옵션
const GridAlignmentOption = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.$isActive ? 'var(--color-grid-alignment-active-bg)' : 'transparent'};
  color: ${props => props.$isActive ? 'var(--color-grid-alignment-active-icon)' : 'var(--color-grid-alignment-icon)'};
  box-shadow: ${props => props.$isActive ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'};
  transition: all 0.2s ease;

  /* 라이트 모드: 180도 버튼과 동일한 스타일 (활성 옵션은 흰색 블럭 배경만 80% 투명) */
  :root[data-theme='light'] & {
    color: white;
    background: ${props => props.$isActive ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
    box-shadow: ${props => props.$isActive ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'};
  }
`;

const IndividualNoticeStudentCard = styled.div<{ $hasData?: boolean; $isSelected?: boolean }>`
  /* 라이트 모드: 흰색 배경에 투명도 10% */
  background: rgba(255, 255, 255, 0.1);
  
  /* 다크 모드: 기존 로직 */
  :root:not([data-theme='light']) & {
    background: ${props =>
      props.$hasData
        ? 'var(--color-student-card-data-bg)'
        : 'transparent'};
  }
  
  border: 2px solid ${props => {
    // 선택된 경우 (다크 모드에서만 메인테마 초록, 라이트 모드는 라이트 모드 스타일에서 처리)
    if (props.$isSelected) {
      // 다크 모드: 메인테마 초록 (라이트 모드는 아래에서 처리)
      return 'var(--color-accent-strong)';
    }
    // 선택되지 않은 경우: 기본값
    return props.$hasData
      ? 'var(--color-student-card-data-border)'
      : 'var(--color-border)';
  }};
  
  /* 라이트 모드: 데이터 입력된 블럭은 테두리 흰색 80%, 데이터 없으면 조금 더 연하게 */
  :root[data-theme='light'] & {
    border-color: ${props => {
      if (props.$isSelected) return 'rgba(255, 255, 255, 0.8)';
      if (props.$hasData) return 'rgba(255, 255, 255, 0.8)';
      return 'var(--color-block-border)';
    }};
  }
  
  /* 다크 모드: 데이터 입력된 블럭·선택된 경우 테두리 흰색 80%, 데이터 없으면 테두리 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    border-color: ${props => {
      if (props.$isSelected) return 'rgba(255, 255, 255, 0.8)';
      if (props.$hasData) return 'rgba(255, 255, 255, 0.8)';
      return 'var(--color-block-border)';
    }};
  }
  
  border-radius: 8px;
  padding: 8px;
  cursor: default;
  transition: all 0.2s;
  height: 120px;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    /* 호버 시 테두리 색은 변하지 않음 (현재 border-color 유지) */
  }
`;

const IndividualNoticeNoteCardLayout = styled.div`
  display: grid;
  grid-template-columns: 70px 1fr;
  grid-template-rows: auto 1fr;
  gap: 8px;
  height: 100%;
`;

const IndividualNoticeStudentHeader = styled.div`
  grid-row: 1 / 2;
  grid-column: 1 / 2;
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
`;

const IndividualNoticeStudentNumber = styled.span`
  font-size: 16px;
  color: var(--color-text-gray);
  font-weight: 700;
  flex-shrink: 0;
  
  /* 라이트 모드: 더 연한 회색 */
  :root[data-theme='light'] & {
    color: #bbb;
  }
`;

const IndividualNoticeProfileImageArea = styled.div`
  width: 70px;
  min-width: 70px;
  max-width: 70px;
  height: 70px;
  min-height: 70px;
  max-height: 70px;
  aspect-ratio: 1;
  background: transparent;
  border: 2px solid #000000;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  grid-row: 2 / 3;
  grid-column: 1 / 2;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  z-index: 10;
  pointer-events: auto;

  &:hover {
    transform: scale(1.05);
  }
`;

const SelectionIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const IndividualNoticeStudentName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  
  /* 라이트 모드: 흰색 */
  :root[data-theme='light'] & {
    color: #ffffff;
  }
`;

const IndividualNoticeStudentTextArea = styled.textarea`
  width: 100%;
  padding: 8px 8px 6px 8px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  line-height: 1.4;
  text-align: left;
  vertical-align: top;
  min-height: 64px;
  /* 라이트 모드: 흰색 배경에 투명도 10% */
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-text-primary);
  overflow-y: auto;
  display: block;
  grid-row: 1 / 3;
  grid-column: 2 / 3;
  height: 100%;

  /* 라이트 모드: 텍스트 흰색, 테두리 흰색에 투명도 35% (데이터 없음 블럭과 동일) */
  :root[data-theme='light'] & {
    color: #ffffff;
    border-color: var(--color-block-border);
  }

  /* 다크 모드: 배경 + 테두리 블럭과 동일하게 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    background: var(--color-student-block-element-bg);
    border-color: var(--color-block-border);
  }

  &:focus {
    border-color: var(--color-accent-strong);
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
  
  /* 라이트 모드: placeholder 흰색에 투명도 60% */
  :root[data-theme='light'] &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 개별공지: 학생 선택/목록 모달
const StudentPickList = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  padding-bottom: 16px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const StudentPickItem = styled.button`
  width: 100%;
  text-align: left;
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--color-text-primary);
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-sizing: border-box;

  :root[data-theme='light'] & {
    &:hover {
      border-color: var(--color-accent-strong);
      box-shadow: 0 10px 18px rgba(0, 0, 0, 0.08);
    }
  }

  :root:not([data-theme='light']) & {
    &:hover {
      border-color: rgba(255, 255, 255, 0.85);
      box-shadow: 0 10px 18px rgba(255, 255, 255, 0.08);
    }
  }
`;

const StudentPickItemContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StudentPickItemNumber = styled.span`
  font-size: 13px;
  color: var(--color-text-gray);
  line-height: 1.3;
`;

const StudentPickItemName = styled.span`
  font-size: 14px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-all;
`;

const StudentNoticeCountBadge = styled.div`
  background: var(--color-accent-strong);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  flex-shrink: 0;
  min-width: 28px;
  text-align: center;
  line-height: 1.2;
  
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 0.9);
    color: var(--color-accent-strong);
  }
`;

// 전체공지 노트 날짜 표시 필드
const NoticeDateDisplay = styled.div`
  padding: 0;
  margin: 0;
  text-align: right;
  color: white;
  font-size: 40px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 2px;
  user-select: none;
  flex-shrink: 0;

  /* 데스크탑: 같은 행에 표시 (화면 오른쪽 정렬) */
  &.desktop-date {
    display: block;
    position: absolute;
    right: 20px;
    transform: none;

    @media (max-width: 800px) {
      display: none;
    }
  }

  /* 모바일: 같은 행에 포함 (중앙 정렬) */
  &.mobile-date {
    display: none;
    width: 100%;
    font-size: 30px;
    padding: 0;
    margin: 0;
    text-align: center;

    @media (max-width: 800px) {
      order: 3;
      display: block;
    }

    @media (max-width: 768px) {
      /* 768px에서도 유지 */
    }
  }
`;

const NoteListModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 16px; /* 하단 여백 추가 (그림자 표현용) */
  /* flex: 1 제거 → 모달 높이가 내용만큼 늘어나고, 오버레이 스크롤로 탐색 */
`;

const NoteListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  h3 {
    margin: 0;
    font-size: 20px;
    
    /* 라이트 모드: 메인테마 초록, 다크 모드: 흰색 */
    :root[data-theme='light'] & {
      color: var(--color-accent-strong);
    }
    
    :root:not([data-theme='light']) & {
      color: rgba(255, 255, 255, 0.9);
    }
  }
`;

const NoteListModalActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NoteListModalActionButton = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }

  svg {
    width: 20px;
    height: 20px;
    stroke: #6c757d;
  }

  :root:not([data-theme='light']) & {
    border-color: rgba(255, 255, 255, 0.45);
    background: var(--color-panel);

    &:hover {
      background: var(--color-border);
      border-color: rgba(255, 255, 255, 0.5);
    }
    svg {
      stroke: rgba(255, 255, 255, 0.85);
    }
  }
`;

const IndividualNoticePdfHeader = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--color-text-primary);
  padding-bottom: 8px;
`;

const NoteListItem = styled.div<{ $isDeleted?: boolean; $expanded?: boolean }>`
  padding: 12px 16px;
  border: 1px solid ${props => props.$isDeleted ? 'var(--color-text-muted)' : 'var(--color-border)'};
  border-radius: 8px;
  background: ${props => props.$isDeleted ? 'var(--color-border)' : 'var(--color-surface)'};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  /* 라이트 모드: 기본 그림자 */
  :root[data-theme='light'] & {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  /* 다크 모드: 흰색 그림자 */
  :root:not([data-theme='light']) & {
    box-shadow: 0 2px 4px rgba(255, 255, 255, 0.1);
  }
  
  ${props => props.$isDeleted && `
    opacity: 0.7;
    * {
      color: #6c757d !important;
    }
  `}
  
  &:hover {
    /* 라이트 모드: 메인테마 초록 테두리 */
    :root[data-theme='light'] & {
      border-color: ${props => props.$isDeleted ? 'var(--color-text-muted)' : 'var(--color-accent-strong)'};
    background: ${props => props.$isDeleted ? 'var(--color-border)' : 'var(--color-panel)'};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
    
    /* 다크 모드: 흰색 테두리 및 흰색 그림자 */
    :root:not([data-theme='light']) & {
      border-color: ${props => props.$isDeleted ? 'var(--color-text-muted)' : 'rgba(255, 255, 255, 0.8)'};
      background: ${props => props.$isDeleted ? 'var(--color-border)' : 'var(--color-panel)'};
      box-shadow: 0 4px 8px rgba(255, 255, 255, 0.15);
    }
  }
`;

const NoteListItemDeleteIcon = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.9);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const TrashIcon = styled.svg`
  width: 14px;
  height: 14px;
  stroke: var(--color-text-muted);
  fill: none;
  transition: stroke 0.2s ease;
  
  ${NoteListItemDeleteIcon}:hover:not(:disabled) & {
    stroke: var(--color-text-muted);
  }
`;

const NoteListItemEditIcon = styled.button`
  position: absolute;
  top: 8px;
  right: 32px;
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.9);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const EditIcon = styled.svg`
  width: 14px;
  height: 14px;
  stroke: var(--color-text-muted);
  fill: none;
  transition: stroke 0.2s ease;
  
  ${NoteListItemEditIcon}:hover:not(:disabled) & {
    stroke: var(--color-accent-strong);
  }
`;

const NoteListItemHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
  padding-right: 50px; /* 휴지통 아이콘 + 수정 아이콘 공간 확보 */
`;

const NoteListItemHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const NoteListItemDate = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: left;
`;

const NoticeScopeBadge = styled.span<{ $scope: 'all' | 'group' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid var(--color-border);
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  white-space: nowrap;
`;

const NoteListItemTimeTag = styled.div`
  font-size: 12px;
  color: #10504A;
  padding: 3px 6px;
  background: rgba(var(--color-accent-rgb), 0.15);
  border-radius: 4px;
  white-space: nowrap;
`;

const NoteListItemHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
`;

const NoteListItemStudents = styled.div`
  font-size: 12px;
  color: var(--color-text-gray);
  white-space: nowrap;
`;

const NoteListItemContent = styled.div<{ $expanded?: boolean }>`
  font-size: 14px;
  color: var(--color-text-primary);
  line-height: 1.5;
  text-align: left;
  white-space: pre-wrap;
  /* 목록에서 모든 텍스트가 보이도록 말줄임 제거 */
`;

const NoteListItemFull = styled.div`
  font-size: 14px;
  color: var(--color-text-primary);
  line-height: 1.5;
  text-align: left;
  white-space: pre-wrap;
  margin-top: 8px;
`;

const NoteListItemEditTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 14px;
  line-height: 1.5;
  font-family: inherit;
  resize: vertical;
  margin-top: 8px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
`;

const NoteListItemEditButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${props => props.disabled ? 'var(--color-border)' : '#10504A'};
  background: ${props => props.disabled ? 'var(--color-panel)' : '#10504A'};
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'white'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  white-space: nowrap;
  margin-top: 12px;
  width: 100%;
  
  &:hover:not(:disabled) {
    background: #0D4039;
    border-color: #0D4039;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  /* 다크모드: 활성화 상태에서도 회색 계열로 */
  :root:not([data-theme='light']) & {
    border: 1px solid ${props => props.disabled ? 'var(--color-border)' : '#505050'};
    background: ${props => props.disabled ? 'var(--color-panel)' : '#505050'};
    color: ${props => props.disabled ? 'var(--color-text-muted)' : 'white'};
    
    &:hover:not(:disabled) {
      background: #606060;
      border-color: #606060;
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
  position: relative;
`;

const PaginationButtonsGroup = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const DeleteButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${props => props.disabled ? 'var(--color-border)' : '#10504A'};
  background: ${props => props.disabled ? 'var(--color-panel)' : '#10504A'};
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'white'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  white-space: nowrap;
  margin-left: auto;
  
  &:hover:not(:disabled) {
    background: #0D4039;
    border-color: #0D4039;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  /* 다크모드: 배경색을 회색으로 */
  :root:not([data-theme='light']) &:not(:disabled) {
    background: #505050;
    border-color: #505050;
  }
  
  /* 다크모드: 호버 시 배경색을 밝은 회색으로 */
  :root:not([data-theme='light']) &:hover:not(:disabled) {
    background: #606060;
    border-color: #606060;
  }
`;

// 삭제 확인 모달 스타일
const DeleteConfirmModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2007;
`;

const DeleteConfirmModalContent = styled.div`
  background: var(--color-surface);
  border: var(--modal-shell-border);
  border-radius: 12px;
  padding: 0;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-large);
`;

const DeleteConfirmModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--color-border);
  
  h3 {
    margin: 0;
    font-size: 18px;
    
    :root[data-theme='light'] & {
      color: var(--color-accent-strong);
    }
    
    :root:not([data-theme='light']) & {
      color: rgba(255, 255, 255, 0.9);
    }
  }
`;

const DeleteConfirmModalBody = styled.div`
  padding: 20px;
  
  p {
    margin: 0;
    color: var(--color-text-primary);
    line-height: 1.5;
  }
`;

const DeleteConfirmModalFooter = styled.div`
  display: flex;
  justify-content: stretch;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid var(--color-border);
  
  button {
    flex: 1;
  }
`;

const DeleteConfirmCancelButton = styled.button`
  padding: 10px 20px;
  border: 2px solid var(--color-border);
  background: transparent;
  color: var(--color-text-primary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
  }
`;

const DeleteConfirmDeleteButton = styled.button`
  padding: 10px 20px;
  border: none;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #0D4039;
  }
  
  /* 다크모드: 배경색을 회색으로 */
  :root:not([data-theme='light']) & {
    background: #505050;
  }
  
  /* 다크모드: 호버 시 배경색을 밝은 회색으로 */
  :root:not([data-theme='light']) &:hover {
    background: #606060;
  }
`;

// 노트북 잠금 해제용 계정 암호 입력 모달
const NotebookLockModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2008;
`;

const NotebookLockModalContent = styled.div`
  background: var(--color-surface);
  border: var(--modal-shell-border);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-large);
`;

const NotebookLockModalTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  color: var(--color-text-primary);
`;

const NotebookLockModalDesc = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const NotebookLockModalInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 16px;
  color: var(--color-text-primary);
  background: var(--color-surface);
  box-sizing: border-box;
  margin-bottom: 8px;
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const NotebookLockModalError = styled.p`
  margin: 0 0 12px 0;
  font-size: 13px;
  color: #c0392b;
`;

const NotebookLockModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const NotebookLockModalButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 10px 20px;
  border: 2px solid var(--color-border);
  background: ${props => props.$primary ? 'var(--color-accent-strong)' : 'transparent'};
  color: ${props => props.$primary ? 'white' : 'var(--color-text-primary)'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) {
    background: ${props => props.$primary ? '#0D4039' : 'var(--color-panel)'};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PaginationButton = styled.button<{ disabled?: boolean }>`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  background: ${props => props.disabled ? 'var(--color-surface-highlight)' : 'var(--color-surface)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  
  /* 라이트 모드: 메인테마 초록 */
  html[data-theme='light'] & {
    border: 2px solid ${props => props.disabled ? 'var(--color-border)' : 'var(--color-accent-strong)'};
    color: ${props => props.disabled ? '#bdbdbd' : 'var(--color-accent-strong)'};
    
    &:hover:not(:disabled) {
      background: rgba(var(--color-accent-rgb), 0.1);
      border-color: var(--color-accent-strong);
      transform: scale(1.05);
    }
  }
  
  /* 다크 모드: 흰색 */
  html:not([data-theme='light']) & {
    border: 2px solid ${props => props.disabled ? 'var(--color-border)' : 'rgba(255, 255, 255, 0.8)'};
    color: ${props => props.disabled ? '#bdbdbd' : 'rgba(255, 255, 255, 0.9)'};
    
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.8);
      color: rgba(255, 255, 255, 0.9);
      transform: scale(1.05);
    }
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover:not(:disabled) svg {
    transform: translateX(0);
  }
`;

const PaginationInfo = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  min-width: 60px;
  text-align: center;
`;

const EmptyNoteList = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
`;

// 주사위 섹션 스타일
const DiceFormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 8px 20px 20px 20px;
  gap: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  user-select: none;
`;

const NumberInputRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 12px;
    flex-wrap: nowrap;
  }
  
  @media (max-width: 375px) {
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
`;

const NumberInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: auto;
    flex: 1;
    min-width: 0;
  }
  
  @media (max-width: 375px) {
    width: 100%;
    flex: none;
  }
`;

const NumberInputWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 8px;
  width: auto;
  min-width: 200px;
  border: 1.5px dashed rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  padding: 6px 10px;
  background: transparent;
  min-height: 36px;
  height: 36px;
  box-sizing: border-box;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.7);
  }
  
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: auto 1fr auto;
    flex: 1;
    width: auto;
    min-width: 0;
    min-height: 32px;
    height: 32px;
    padding: 4px 8px;
    gap: 4px;
  }
`;

const NumberInputLabel = styled.span`
  font-size: 14px;
  color: white;
  font-weight: 500;
  white-space: nowrap;
`;

const NumberInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const NumberInput = styled.input`
  width: 100%;
  max-width: 80px;
  min-width: 50px;
  border: none;
  background: transparent;
  color: white;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  outline: none;
  padding: 0;
  margin: 0 auto;
  justify-self: center;
  
  @media (max-width: 768px) {
    max-width: 60px;
    min-width: 40px;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &:focus-visible {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  -moz-appearance: textfield;
`;

const IconButton = styled.button`
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  &:active {
    transform: scale(0.9);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  img {
    width: 14px;
    height: 14px;
    filter: brightness(0) invert(1);
  }
  
  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
    min-width: 22px;
    min-height: 22px;
    
    img {
      width: 12px;
      height: 12px;
    }
  }
`;

const ResetIcon = styled.img`
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
  opacity: 0.9;
  transition: opacity 0.2s ease;
  
  ${IconButton}:hover & {
    opacity: 1;
  }
`;

const IncrementButton = styled.button`
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: transparent;
  color: white;
  font-size: 16px;
  font-weight: bold;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  &:active {
    transform: scale(0.9);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
    min-width: 22px;
    min-height: 22px;
    font-size: 14px;
  }
`;

const DiceActionButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  
  ${ActionButton} {
    .button-text-full {
      display: inline;
    }
    
    .button-text-short {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: row;
    gap: 8px;
    flex-wrap: nowrap;
    
    ${ActionButton} {
      flex: 1;
      min-width: 0;
    }
  }
  
  @media (max-width: 375px) {
    ${ActionButton} {
      .button-text-full {
        display: none;
      }
      
      .button-text-short {
        display: inline;
      }
    }
  }
`;

const DiceTabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ResultSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.35);
  min-height: 200px;
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  flex-wrap: wrap;
  position: relative;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
  
  @media (max-width: 375px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ResultHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  
  @media (max-width: 375px) {
    width: 100%;
    order: 1;
    justify-content: space-between;
  }
`;

const ResultHeaderCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  
  @media (max-width: 375px) {
    position: static;
    transform: none;
    width: 100%;
    order: 2;
    margin-top: 8px;
  }
`;

const ResultHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
  flex-shrink: 0;
  
  @media (max-width: 375px) {
    display: none;
  }
`;

const ResultHeaderRightMobile = styled.div`
  display: none;
  flex-shrink: 0;
  
  @media (max-width: 375px) {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

const ContinuousInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1.5px dashed rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  padding: 6px 10px;
  background: transparent;
  min-height: 36px;
  height: 36px;
  box-sizing: border-box;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.7);
  }
  
  @media (max-width: 768px) {
    min-height: 32px;
    height: 32px;
    padding: 4px 8px;
  }
`;

const ContinuousInput = styled.input`
  width: 25px;
  border: none;
  background: transparent;
  color: white;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  outline: none;
  padding: 0;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &:focus-visible {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  -moz-appearance: textfield;
`;

const ContinuousLabel = styled.span`
  font-size: 14px;
  color: white;
  font-weight: 500;
  white-space: nowrap;
`;

const RemainingCount = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
`;

const ResetResultButton = styled.button`
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 6px;
  background: transparent;
  color: white;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 32px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ResultDiceButton = styled.button`
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  img {
    width: 28px;
    height: 28px;
    filter: brightness(0) invert(1);
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    
    img {
      width: 24px;
      height: 24px;
    }
  }
`;

const ResultContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  flex: 1;
  width: 100%;
`;

const NumberResultsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px;
  width: 100%;
  max-width: calc(4 * min(280px, 20vw) + 3 * 16px); /* 4열 최대 */
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  
  @media (max-width: 1200px) {
    max-width: calc(3 * min(240px, 22vw) + 2 * 16px); /* 3열 최대 */
  }
  
  @media (max-width: 900px) {
    max-width: calc(3 * min(200px, 25vw) + 2 * 16px); /* 3열 최대 */
  }
  
  @media (max-width: 768px) {
    max-width: calc(3 * min(180px, 30vw) + 2 * 12px); /* 3열 최대 */
    gap: 12px;
    padding: 16px;
  }
  
  @media (max-width: 550px) {
    max-width: calc(2 * min(160px, 40vw) + 1 * 12px); /* 2열 최대 */
  }
  
  @media (max-width: 480px) {
    max-width: calc(2 * min(160px, 40vw) + 1 * 12px); /* 2열 최대 */
  }
`;

const NumberResultItem = styled.div`
  font-size: clamp(48px, 6vw, 96px);
  font-weight: bold;
  color: white;
  text-align: center;
  padding: 24px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  width: min(280px, 20vw);
  height: min(280px, 20vw);
  min-width: min(280px, 20vw);
  min-height: min(280px, 20vw);
  max-width: min(280px, 20vw);
  max-height: min(280px, 20vw);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  
  @media (max-width: 1200px) {
    width: min(240px, 22vw);
    height: min(240px, 22vw);
    min-width: min(240px, 22vw);
    min-height: min(240px, 22vw);
    max-width: min(240px, 22vw);
    max-height: min(240px, 22vw);
    font-size: clamp(44px, 5.5vw, 80px);
  }
  
  @media (max-width: 900px) {
    width: min(200px, 25vw);
    height: min(200px, 25vw);
    min-width: min(200px, 25vw);
    min-height: min(200px, 25vw);
    max-width: min(200px, 25vw);
    max-height: min(200px, 25vw);
    font-size: clamp(40px, 5vw, 72px);
  }
  
  @media (max-width: 768px) {
    font-size: clamp(36px, 4.5vw, 64px);
    padding: 20px;
    width: min(180px, 30vw);
    height: min(180px, 30vw);
    min-width: min(180px, 30vw);
    min-height: min(180px, 30vw);
    max-width: min(180px, 30vw);
    max-height: min(180px, 30vw);
  }
  
  @media (max-width: 550px) {
    width: min(160px, 40vw);
    height: min(160px, 40vw);
    min-width: min(160px, 40vw);
    min-height: min(160px, 40vw);
    max-width: min(160px, 40vw);
    max-height: min(160px, 40vw);
    font-size: clamp(32px, 4vw, 56px);
  }
`;

const ProfileResultCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 0;
  border: none;
  background: transparent;
  width: min(280px, 20vw);
  flex-shrink: 0;
  box-sizing: border-box;
  
  @media (max-width: 1200px) {
    width: min(240px, 22vw);
  }
  
  @media (max-width: 900px) {
    width: min(200px, 25vw);
  }
  
  @media (max-width: 768px) {
    width: min(180px, 30vw);
  }
  
  @media (max-width: 550px) {
    width: min(160px, 40vw);
  }
`;

const ProfileResultsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(16px, 2vw, 32px);
  padding: clamp(16px, 2vw, 24px);
  width: 100%;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  
  @media (min-width: 1400px) {
    gap: 32px;
    padding: 24px;
  }
  
  @media (max-width: 1200px) {
    gap: clamp(16px, 2.2vw, 28px);
    padding: clamp(16px, 2.2vw, 22px);
  }
  
  @media (max-width: 900px) {
    gap: clamp(14px, 2.5vw, 24px);
    padding: clamp(14px, 2.5vw, 20px);
  }
  
  @media (max-width: 768px) {
    gap: clamp(12px, 3vw, 20px);
    padding: clamp(12px, 3vw, 18px);
  }
  
  @media (max-width: 550px) {
    gap: clamp(10px, 4vw, 16px);
    padding: clamp(10px, 4vw, 16px);
  }

`;

const ProfileScrollBottomSpacer = styled.div`
  width: 100%;
  height: 300px;
  flex-shrink: 0;
`;

const ProfileResultCardImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 16px;
  overflow: hidden;
  border: 5px solid #000000;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 1200px) {
    border-radius: 14px;
    border-width: 4px;
  }
  
  @media (max-width: 900px) {
    border-radius: 12px;
    border-width: 3.5px;
  }
  
  @media (max-width: 768px) {
    border-radius: 10px;
    border-width: 3px;
  }
  
  @media (max-width: 550px) {
    border-radius: 8px;
    border-width: 2.5px;
  }
`;

const ProfileResultCardInfo = styled.div`
  text-align: center;
  color: white;
  font-size: clamp(24px, 2.4vw, 40px);
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  max-width: 100%;
  line-height: 1.3;
  
  @media (min-width: 1400px) {
    font-size: 40px;
  }
  
  @media (max-width: 1200px) {
    font-size: clamp(22px, 2.6vw, 36px);
  }
  
  @media (max-width: 900px) {
    font-size: clamp(20px, 3vw, 30px);
  }
  
  @media (max-width: 768px) {
    font-size: clamp(18px, 3.6vw, 26px);
  }
  
  @media (max-width: 550px) {
    font-size: clamp(16px, 4.8vw, 24px);
  }
`;

const TeamResult = styled.div<{ $isMeasuring: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-items: center;
  opacity: ${props => props.$isMeasuring ? 0 : 1};
  visibility: ${props => props.$isMeasuring ? 'hidden' : 'visible'};
`;

const TeamBlock = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-radius: 8px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  width: max-content;
  max-width: 100%;
  box-sizing: border-box;
`;

const TeamTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin-bottom: 12px;
  text-align: center;
`;

const TeamMembersGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  align-items: flex-start;
`;

const TeamMemberCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(6px, 0.8vw, 10px);
  padding: clamp(10px, 1.2vw, 14px);
  border: none;
  border-radius: 8px;
  background: transparent;
  transition: all 0.2s ease;
  width: min(150px, 13vw);
  min-width: min(150px, 13vw);
  max-width: min(150px, 13vw);
  box-sizing: border-box;
  
  @media (min-width: 1400px) {
    width: 150px;
    min-width: 150px;
    max-width: 150px;
  }
  
  @media (max-width: 1200px) {
    width: min(135px, 14vw);
    min-width: min(135px, 14vw);
    max-width: min(135px, 14vw);
  }
  
  @media (max-width: 900px) {
    width: min(120px, 16vw);
    min-width: min(120px, 16vw);
    max-width: min(120px, 16vw);
  }
  
  @media (max-width: 768px) {
    width: min(105px, 18vw);
    min-width: min(105px, 18vw);
    max-width: min(105px, 18vw);
  }
  
  @media (max-width: 550px) {
    width: min(90px, 22vw);
    min-width: min(90px, 22vw);
    max-width: min(90px, 22vw);
  }
`;

const TeamMemberImage = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 6px;
  overflow: hidden;
  border: 3px solid #000000;
  flex-shrink: 0;
  
  img,
  > * {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0;
  }
  
  @media (min-width: 1400px) {
    border-radius: 8px;
    border-width: 3px;
  }
  
  @media (max-width: 1200px) {
    border-radius: 6px;
    border-width: 2.5px;
  }
  
  @media (max-width: 900px) {
    border-radius: 5px;
    border-width: 2px;
  }
  
  @media (max-width: 768px) {
    border-radius: 4px;
    border-width: 1.5px;
  }
  
  @media (max-width: 550px) {
    border-radius: 3px;
    border-width: 1.5px;
  }
`;

const TeamMemberInfo = styled.div`
  text-align: center;
  color: white;
  font-size: clamp(16px, 1.6vw, 26px);
  font-weight: 800;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  
  @media (min-width: 1400px) {
    font-size: 24px;
  }
  
  @media (max-width: 1200px) {
    font-size: clamp(15px, 1.7vw, 24px);
  }
  
  @media (max-width: 900px) {
    font-size: clamp(14px, 2vw, 22px);
  }
  
  @media (max-width: 768px) {
    font-size: clamp(13px, 2.2vw, 20px);
  }
  
  @media (max-width: 550px) {
    font-size: clamp(12px, 2.6vw, 18px);
  }
`;

// 팀 수 입력 모달
const TeamCountModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
`;

const TeamCountModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-shell-border);
`;

const TeamCountModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h3 {
    margin: 0;
    color: var(--color-accent-strong);
    font-size: 20px;
  }
`;

const CloseModalButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-size: 24px;
  line-height: 1;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const TeamCountInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const TeamCountLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const TeamCountInput = styled.input`
  padding: 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s ease;
  text-align: center;
  
  &:focus {
    border-color: var(--color-accent-strong);
  }
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  -moz-appearance: textfield;
`;

const TeamCountButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
`;

const TeamCountIncrementButton = styled.button`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border: 2px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-accent-strong);
    color: var(--color-accent-strong);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TeamCountIconButton = styled.button`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border: 2px solid var(--color-border);
  background: var(--color-surface);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-accent-strong);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  img {
    width: 16px;
    height: 16px;
  }
`;

const TeamCountButtonRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const TeamCountButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: ${props => props.$variant === 'primary' ? 'none' : '2px solid var(--color-border)'};
  background: ${props => props.$variant === 'primary' ? 'var(--color-accent-strong)' : 'transparent'};
  color: ${props => props.$variant === 'primary' ? 'white' : 'var(--color-text-primary)'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$variant === 'primary' ? '#0D4039' : 'var(--color-panel)'};
    border-color: ${props => props.$variant === 'primary' ? '#0D4039' : 'var(--color-border)'};
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const BoardPage: React.FC<BoardPageProps> = ({ user, currentClass, currentMemberGroup, onTimerModalOpen, onDiceModalOpen, onLicenseClick }) => {
  // 타임 태그 관련 상태
  const [selectedTimeTag, setSelectedTimeTag] = useState<any>(null);
  const [availableTimeTags, setAvailableTimeTags] = useState<any[]>([]);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<string>(() =>
    ['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]
  );
  
  // 행동체크 포인트용 날짜 선택 (오늘 날짜로 초기화)
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // YYYY-MM-DD 형식
  });

  // 달력 모달 상태
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  
  // 타임 태그 선택 모달
  const [showTimeTagSelectModal, setShowTimeTagSelectModal] = useState<boolean>(false);

  // 노트북 관련 상태
  const [showNotebookForm, setShowNotebookForm] = useState<boolean>(false);
  const [categories, setCategories] = useState<NotebookCategory[]>([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('알림장');
  const [classMembers, setClassMembers] = useState<ClassMember[]>([]);
  const [memberProfileDisplayMode, setMemberProfileDisplayMode] = useState<MemberProfileDisplayMode>('svg');
  const [isClassMembersLoaded, setIsClassMembersLoaded] = useState<boolean>(false);
  const [relatedStudents, setRelatedStudents] = useState<RelatedStudentInfo[]>([]);
  const [showStudentTagModal, setShowStudentTagModal] = useState<boolean>(false);
  const [noteContent, setNoteContent] = useState<string>('');
  // 글자 크기는 localStorage에서 로드 (기본값 20)
  const [fontSize, setFontSize] = useState<number>(() => {
    const saved = localStorage.getItem('notebook-font-size');
    return saved ? parseInt(saved, 10) : 20;
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  // 현재 편집 중인 노트의 원본 데이터 (변경사항 비교용)
  const [originalNoteContent, setOriginalNoteContent] = useState<string>('');
  const [originalNoteDate, setOriginalNoteDate] = useState<string>('');

  // 공지 노트 관련 상태
  const [showNoticeForm, setShowNoticeForm] = useState<boolean>(false);
  const [noticeTab, setNoticeTab] = useState<NoticeNoteField>('GeneralNotice');
  // 전체공지(모든 그룹 동시 기록) vs 그룹공지(현재 선택 그룹에만 기록)
  const [generalNoticeScope, setGeneralNoticeScope] = useState<'all' | 'group'>('all');
  const [noticeAllContent, setNoticeAllContent] = useState<string>('');
  // 전체공지노트 글자 크기는 localStorage에서 로드 (기본값 20)
  const [noticeAllFontSize, setNoticeAllFontSize] = useState<number>(() => {
    const saved = localStorage.getItem('noticeAllFontSize');
    return saved ? parseInt(saved, 10) : 20;
  });
  // 개별공지는 글자크기 버튼을 숨기므로 고정값(10) 사용
  const [noticeIndFontSize, setNoticeIndFontSize] = useState<number>(10);
  const [isNoticeLoading] = useState<boolean>(false);
  const [isNoticeSaving, setIsNoticeSaving] = useState<boolean>(false);
  const [editingNoticeId, setEditingNoticeId] = useState<string | null>(null);
  const [allNoticeNotes, setAllNoticeNotes] = useState<any[]>([]);
  // 현재 편집 중인 노트의 원본 데이터 (변경사항 비교용) - 개별 값으로 분리하여 깜박임 방지
  const [originalNoticeContent, setOriginalNoticeContent] = useState<string>('');
  const [originalNoticeDate, setOriginalNoticeDate] = useState<string>('');

  // editingNoticeId를 loadNoticeNotes dependency에 두면 callback identity가 계속 바뀌며
  // 전체공지노트가 없을 때 "불러오는중..." / placeholder가 반복 깜박이는 현상이 생길 수 있어 ref로 분리
  const editingNoticeIdRef = useRef<string | null>(null);
  const noticeAllContentRef = useRef<string>('');
  // 중복 실행 방지를 위한 ref
  const isLoadingNoticeNotesRef = useRef<boolean>(false);
  // 전체공지 로드 플래그 (한 번만 로드하도록)
  const hasLoadedNoticeNotesRef = useRef<boolean>(false);

  const normalizedCurrentMemberGroup = useMemo(() => {
    const g = (currentMemberGroup || 'A').toString().trim().toUpperCase();
    return g.length > 0 ? g : 'A';
  }, [currentMemberGroup]);

  const availableMemberGroups = useMemo(() => {
    return getOrderedMemberGroups(currentClass?.memberGroups, currentClass?.memberGroupOrder);
  }, [currentClass?.memberGroups, currentClass?.memberGroupOrder]);

  useEffect(() => {
    editingNoticeIdRef.current = editingNoticeId;
  }, [editingNoticeId]);

  useEffect(() => {
    noticeAllContentRef.current = noticeAllContent;
  }, [noticeAllContent]);

  // 개별공지(학생별) 임시 입력값
  const [individualNoticeDrafts, setIndividualNoticeDrafts] = useState<Record<string, string>>({});
  
  // 개별공지 수정 모드: 날짜별 조회로 로드한 경우
  const [editingIndividualNoticeNotes, setEditingIndividualNoticeNotes] = useState<Array<{
    studentId: string;
    timestamp: number;
    originalContent: string;
  }>>([]);

  // 라이센스 제한 모달 상태
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');

  // 노트북 잠금 카테고리 열기용 비밀번호 모달 상태
  const [showNotebookLockModal, setShowNotebookLockModal] = useState(false);
  const [notebookLockCategory, setNotebookLockCategory] = useState<NotebookCategory | null>(null);
  const [notebookLockPassword, setNotebookLockPassword] = useState('');
  const [notebookLockError, setNotebookLockError] = useState('');
  const [notebookLockSubmitting, setNotebookLockSubmitting] = useState(false);

  // 자리 모드 상태
  const [viewMode, setViewMode] = useState<'number' | 'seat'>('number');
  const [seatArrangementData, setSeatArrangementData] = useState<any>(null);
  const [seatArrangementError, setSeatArrangementError] = useState<string>('');
  const [isViewModeRestored, setIsViewModeRestored] = useState<boolean>(false);
  const [isSeatRotated, setIsSeatRotated] = useState<boolean>(false);
  
  // 그리드 정렬 상태 (번호 모드에서만 적용)
  const [gridAlignment, setGridAlignment] = useState<'left' | 'center'>('center');

  // localStorage 키 생성 함수
  const getStorageKey = useCallback((key: string) => {
    return currentClass ? `boardPage_${key}_${currentClass.id}` : null;
  }, [currentClass]);

  // currentClass가 변경되면 viewMode 복원 플래그 리셋
  useEffect(() => {
    setIsViewModeRestored(false);
  }, [currentClass?.id]);

  // 초기 로드 시 viewMode 및 gridAlignment 복원
  useEffect(() => {
    if (!currentClass || isViewModeRestored) return;
    
    const storageKey = getStorageKey('settings');
    if (!storageKey) return;
    
    try {
      const savedSettings = localStorage.getItem(storageKey);
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        if (settings.viewMode === 'number' || settings.viewMode === 'seat') {
          setViewMode(settings.viewMode);
          console.log('✅ BoardPage viewMode 복원:', settings.viewMode);
        }
        if (settings.gridAlignment === 'left' || settings.gridAlignment === 'center') {
          setGridAlignment(settings.gridAlignment);
          console.log('✅ BoardPage gridAlignment 복원:', settings.gridAlignment);
        }
      }
      setIsViewModeRestored(true);
    } catch (error) {
      console.error('❌ BoardPage viewMode 복원 실패:', error);
      setIsViewModeRestored(true);
    }
  }, [currentClass, isViewModeRestored, getStorageKey]);

  // viewMode 변경 시 localStorage에 저장
  useEffect(() => {
    if (!currentClass || !isViewModeRestored) return;
    
    const storageKey = getStorageKey('settings');
    if (!storageKey) return;
    
    try {
      const existingSettings = localStorage.getItem(storageKey);
      const settings = existingSettings ? JSON.parse(existingSettings) : {};
      settings.viewMode = viewMode;
      localStorage.setItem(storageKey, JSON.stringify(settings));
      console.log('💾 BoardPage viewMode 저장:', viewMode);
    } catch (error) {
      console.error('❌ localStorage에 viewMode 저장 실패:', error);
    }
  }, [viewMode, currentClass, isViewModeRestored, getStorageKey]);

  // gridAlignment 변경 시 localStorage에 저장
  useEffect(() => {
    if (!currentClass || !isViewModeRestored) return;
    
    const storageKey = getStorageKey('settings');
    if (!storageKey) return;
    
    try {
      const existingSettings = localStorage.getItem(storageKey);
      const settings = existingSettings ? JSON.parse(existingSettings) : {};
      settings.gridAlignment = gridAlignment;
      localStorage.setItem(storageKey, JSON.stringify(settings));
      console.log('💾 BoardPage gridAlignment 저장:', gridAlignment);
    } catch (error) {
      console.error('❌ localStorage에 gridAlignment 저장 실패:', error);
    }
  }, [gridAlignment, currentClass, isViewModeRestored, getStorageKey]);

  // 과제 체크 모달에서 메모 전송 이벤트 수신 및 localStorage에서 대기 중인 메모 확인
  useEffect(() => {
    if (!user || !currentClass) return;
    
    const storageKey = `pendingMemosToBoard_${user.id}_${currentClass.id}`;
    
    // localStorage에서 대기 중인 메모 확인 및 적용
    const checkPendingMemos = () => {
      const pendingMemosStr = localStorage.getItem(storageKey);
      if (pendingMemosStr) {
        try {
          const pendingMemos = JSON.parse(pendingMemosStr);
          console.log('📥 localStorage에서 대기 중인 메모 발견:', pendingMemos);
          
          setIndividualNoticeDrafts(prev => {
            const updated = { ...prev };
            Object.entries(pendingMemos).forEach(([studentId, memo]: [string, any]) => {
              console.log(`📝 학생 ${studentId} 메모 추가 (localStorage):`, memo);
              if (updated[studentId] && updated[studentId].trim()) {
                updated[studentId] = `${updated[studentId]} / ${memo}`;
              } else {
                updated[studentId] = memo;
              }
            });
            console.log('📝 업데이트된 individualNoticeDrafts:', updated);
            return updated;
          });
          
          // 적용 후 localStorage에서 제거
          localStorage.removeItem(storageKey);
        } catch (e) {
          console.error('대기 중인 메모 파싱 실패:', e);
          localStorage.removeItem(storageKey);
        }
      }
    };
    
    // 이벤트 핸들러
    const handleSendMemosToBoard = (event: Event) => {
      const customEvent = event as CustomEvent<Record<string, string>>;
      const memos = customEvent.detail;
      
      console.log('📥 보드 페이지에서 메모 수신 (이벤트):', memos);
      console.log('📥 현재 classMembers:', classMembers.map(m => ({ id: m.id, name: m.name, number: m.number })));
      console.log('📥 수신된 메모의 studentId 목록:', Object.keys(memos));
      
      // 기존 텍스트가 있으면 " / " 표시 후 누적 추가
      setIndividualNoticeDrafts(prev => {
        const updated = { ...prev };
        let appliedCount = 0;
        
        Object.entries(memos).forEach(([studentId, memo]) => {
          // classMembers에 해당 studentId가 있는지 확인
          const member = classMembers.find(m => m.id === studentId);
          if (member) {
            console.log(`📝 학생 ${studentId} (${member.name}) 메모 추가:`, memo);
            if (updated[studentId] && updated[studentId].trim()) {
              updated[studentId] = `${updated[studentId]} / ${memo}`;
            } else {
              updated[studentId] = memo;
            }
            appliedCount++;
          } else {
            console.warn(`⚠️ studentId ${studentId}에 해당하는 classMember를 찾을 수 없음`);
          }
        });
        
        console.log(`📝 업데이트된 individualNoticeDrafts (${appliedCount}개 적용):`, updated);
        return updated;
      });
    };

    // 초기 로드 시 대기 중인 메모 확인 (보드 상태 복원 후에 실행되도록 약간의 지연)
    // 보드 상태 복원이 먼저 실행되도록 하기 위해 setTimeout 사용
    const timeoutId = setTimeout(() => {
      checkPendingMemos();
    }, 200);
    
    // 이벤트 리스너 등록
    window.addEventListener('sendMemosToBoard', handleSendMemosToBoard);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('sendMemosToBoard', handleSendMemosToBoard);
    };
  }, [user, currentClass, currentMemberGroup, classMembers]);

  // 개별공지: 학생 선택 상태
  const [selectedIndividualStudents, setSelectedIndividualStudents] = useState<Set<string>>(new Set());

  // 개별공지: 일괄 노트 작성 모달 상태
  const [showBulkIndividualNoticeModal, setShowBulkIndividualNoticeModal] = useState<boolean>(false);
  const [bulkIndividualNoticeContent, setBulkIndividualNoticeContent] = useState<string>('');

  // 개별공지: 학생 선택/학생별 목록 모달
  const [showIndividualStudentPickModal, setShowIndividualStudentPickModal] = useState<boolean>(false);
  const [showIndividualNoticeListModal, setShowIndividualNoticeListModal] = useState<boolean>(false);
  const [selectedIndividualStudent, setSelectedIndividualStudent] = useState<ClassMember | null>(null);
  const [individualNoticeList, setIndividualNoticeList] = useState<any[]>([]);
  const [individualNoticePage, setIndividualNoticePage] = useState<number>(1);
  const [expandedIndividualNoticeId, setExpandedIndividualNoticeId] = useState<string | null>(null);
  const [deletedIndividualNoticeIds, setDeletedIndividualNoticeIds] = useState<Set<string>>(new Set());
  // 수정 모드 전환 전 원래 확장 상태 저장
  const [originalExpandedStateBeforeEdit, setOriginalExpandedStateBeforeEdit] = useState<string | null>(null);
  const individualNoticeListModalBodyRef = useRef<HTMLDivElement>(null);
  const [isExportingIndividualNotice, setIsExportingIndividualNotice] = useState(false);
  const individualNoticeExportContainerRef = useRef<HTMLDivElement>(null);

  // 개별공지 모달이 열려있을 때 배경 페이지 스크롤 막기
  useEffect(() => {
    const isModalOpen = showIndividualStudentPickModal || showIndividualNoticeListModal;
    
    if (isModalOpen) {
      // 모달이 열려있을 때 body 스크롤 막기
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;
      
      // 스크롤바 너비 계산 (스크롤바가 있을 때 레이아웃 시프트 방지)
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      return () => {
        // 모달이 닫힐 때 원래 상태로 복원
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [showIndividualStudentPickModal, showIndividualNoticeListModal]);
  const [editingIndividualNoticeItemId, setEditingIndividualNoticeItemId] = useState<string | null>(null);
  const [editingIndividualNoticeContent, setEditingIndividualNoticeContent] = useState<string>('');
  
  // 개별공지 모달 탭 상태: 'date' (날짜별 조회) | 'student' (개인별 조회)
  const [individualNoticeViewMode, setIndividualNoticeViewMode] = useState<'date' | 'student'>('date');
  
  // 날짜별 개별공지 목록
  const [dateBasedIndividualNotices, setDateBasedIndividualNotices] = useState<Array<{
    date: string;
    dateFormatted: string;
    groupKey: string;
    createdAtMillis: number;
    notes: Array<{
      id: string;
      content: string;
      date: string;
      studentId: string;
      studentName?: string;
      createdAt: any;
      updatedAt: any;
    }>;
  }>>([]);
  const [currentDateBasedIndex, setCurrentDateBasedIndex] = useState<number>(0);
  const [deletedDateBasedNoticeKeys, setDeletedDateBasedNoticeKeys] = useState<Set<string>>(new Set());
  const [showDeleteDateBasedConfirmModal, setShowDeleteDateBasedConfirmModal] = useState<boolean>(false);
  // Firestore Timestamp / {seconds} 형태 모두 지원해서 createdAt millis를 안정적으로 얻기
  const getCreatedAtMillis = useCallback((createdAt: any): number | null => {
    if (!createdAt) return null;
    try {
      if (typeof createdAt?.toMillis === 'function') return createdAt.toMillis();
      if (typeof createdAt?.toDate === 'function') return createdAt.toDate().getTime();
      if (typeof createdAt?.seconds === 'number') return createdAt.seconds * 1000;
      if (typeof createdAt?._seconds === 'number') return createdAt._seconds * 1000;
      return null;
    } catch {
      return null;
    }
  }, []);

  const [showDeleteIndividualNoticeConfirmModal, setShowDeleteIndividualNoticeConfirmModal] = useState<boolean>(false);
  const [studentNoticeCounts, setStudentNoticeCounts] = useState<Record<string, number>>({});
  
  // 노트 목록 모달 관련 상태
  const [showNoteListModal, setShowNoteListModal] = useState<boolean>(false);
  const [noteListCategory, setNoteListCategory] = useState<string>('');
  const [allNotes, setAllNotes] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentNoteIndex, setCurrentNoteIndex] = useState<number>(0); // 현재 카테고리 내 노트 인덱스
  const [deletedNoteIds, setDeletedNoteIds] = useState<Set<string>>(new Set());
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean>(false);
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);
  const NOTES_PER_PAGE = 5;
  
  // 타이머 모달 상태
  
  // 주사위 섹션 관련 상태
  const [showDiceForm, setShowDiceForm] = useState<boolean>(false);
  const [selectedDiceTab, setSelectedDiceTab] = useState<'number' | 'profile' | 'team' | null>(null);
  const [startNumber, setStartNumber] = useState<number>(1);
  const [endNumber, setEndNumber] = useState<number>(30);
  const [isDiceInitialized, setIsDiceInitialized] = useState<boolean>(false);
  
  // 번호 뽑기 독립 상태
  const [numberDrawnNumbers, setNumberDrawnNumbers] = useState<Set<number>>(new Set());
  const [numberResult, setNumberResult] = useState<number | null>(null);
  const [numberResults, setNumberResults] = useState<number[]>([]); // 연속 뽑기 결과
  
  // 토스트 메시지 상태
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [continuousCount, setContinuousCount] = useState<number>(1); // 연속 뽑기 개수
  
  // 프로필 뽑기 독립 상태
  const [profileDrawnNumbers, setProfileDrawnNumbers] = useState<Set<number>>(new Set());
  const [profileResult, setProfileResult] = useState<ClassMember | null>(null);
  const [profileResults, setProfileResults] = useState<ClassMember[]>([]); // 연속 뽑기 결과
  const [profileAvailableMembers, setProfileAvailableMembers] = useState<ClassMember[]>([]);
  
  // 팀 나누기 독립 상태
  const [teamResult, setTeamResult] = useState<ClassMember[][] | null>(null);
  const [showTeamCountModal, setShowTeamCountModal] = useState<boolean>(false);
  const [teamCount, setTeamCount] = useState<number>(2);
  const [maxTeamBlockWidth, setMaxTeamBlockWidth] = useState<number | null>(null);
  const teamBlockRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [isTeamBlockMeasuring, setIsTeamBlockMeasuring] = useState<boolean>(false);

  const timeTagService = useMemo(() => {
    return user && currentClass
      ? TimeTagService
      : null;
  }, [user, currentClass]);

  // 타임 태그 포맷/파싱 헬퍼 (PointsTablePage와 동일 규칙)
  const formatTimeTagLabel = (timeTag: TimeTag, day: string) => {
    const dayLabel = day || '전체';
    return `[${dayLabel}] ${timeTag.name}`;
  };

  const parseTimeTagLabel = (label?: string): { day: string; name: string } => {
    const defaultResult = { day: '일', name: '' };
    if (!label) return defaultResult;

    const trimmed = label.trim();
    const bracketMatch = trimmed.match(/^\[(.+?)\]\s*(.+)$/);
    if (bracketMatch) {
      const day = bracketMatch[1];
      // 하위 호환: '일반'은 '일'로 매핑
      const dayCandidates = ['일반', '일', '월', '화', '수', '목', '금', '토'];
      const mappedDay = day === '일반' ? '일' : (dayCandidates.includes(day) ? day : '일');
      return { day: mappedDay, name: bracketMatch[2] };
    }

    const parts = trimmed.split(' ');
    const dayCandidates = ['일반', '일', '월', '화', '수', '목', '금', '토'];
    if (parts.length >= 2 && dayCandidates.includes(parts[0])) {
      const day = parts[0] === '일반' ? '일' : parts[0];
      return { day, name: parts.slice(1).join(' ').trim() };
    }

    return { day: '일', name: trimmed };
  };

  // 현재 시간에 맞는 타임 태그 자동 선택 함수
  const selectCurrentTimeTag = useCallback((): boolean => {
    const currentDayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()];
    if (availableTimeTags.length === 0) {
      setSelectedTimeTag(null);
      setSelectedDayOfWeek(currentDayOfWeek);
      return false;
    }
    
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    const findMatch = (day: string) => availableTimeTags.find(tag => {
      const days = Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek : [tag.dayOfWeek];
      return days.includes(day) && currentTime >= tag.startTime && currentTime <= tag.endTime;
    });
    
    // 현재 요일에 맞는 타임 태그 찾기 (전체 요일 태그: dayOfWeek에 모든 요일 포함 시 해당)
    let matchedTimeTag = findMatch(currentDayOfWeek);
    const matchedDay = currentDayOfWeek;
    
    if (!matchedTimeTag) {
      setSelectedTimeTag(null);
      setSelectedDayOfWeek(currentDayOfWeek);
      console.log('❌ 현재 시각에 맞는 타임 태그 없음');
      return false;
    } else {
      setSelectedTimeTag(matchedTimeTag);
      setSelectedDayOfWeek(matchedDay);
      console.log('✅ 자동 선택된 타임 태그:', matchedTimeTag.name, matchedDay);
      return true;
    }
  }, [availableTimeTags]);

  // 보드 페이지 상태 저장 키 생성 (useCallback 사용)
  const getBoardStateKeyMemo = useCallback((userId: string, classId: string) => {
    return `boardState_${userId}_${classId}`;
  }, []);

  // 복원 중 플래그
  const [isRestoring, setIsRestoring] = useState<boolean>(false);

  // user와 currentClass가 있을 때 즉시 기본 상태 복원 (초기 로드 시)
  useEffect(() => {
    if (!user || !currentClass) return;
    
    setIsRestoring(true);
    try {
      const key = getBoardStateKeyMemo(user.id, currentClass.id);
      const saved = localStorage.getItem(key);
      if (saved) {
        const state = JSON.parse(saved);
        console.log('🔄 보드 상태 복원 중:', state);
        
        // 즉시 복원 가능한 상태들
        if (state.showNotebookForm !== undefined) {
          setShowNotebookForm(state.showNotebookForm);
          console.log('✅ 노트북 폼 복원:', state.showNotebookForm);
        }
        if (state.showNoticeForm !== undefined) {
          setShowNoticeForm(state.showNoticeForm);
          console.log('✅ 공지 폼 복원:', state.showNoticeForm);
        }
        if (state.noticeTab !== undefined) {
          setNoticeTab(state.noticeTab);
          console.log('✅ 공지 탭 복원:', state.noticeTab);
        }
        if (state.individualNoticeDrafts !== undefined) {
          // pendingMemos와 병합 (보드 상태 복원 전에 pendingMemos 확인)
          const pendingMemosKey = `pendingMemosToBoard_${user.id}_${currentClass.id}`;
          const pendingMemosStr = localStorage.getItem(pendingMemosKey);
          let mergedDrafts = { ...state.individualNoticeDrafts };
          
          if (pendingMemosStr) {
            try {
              const pendingMemos = JSON.parse(pendingMemosStr);
              console.log('📥 보드 상태 복원 중 - pendingMemos 발견:', pendingMemos);
              // pendingMemos와 병합 (기존 텍스트가 있으면 " / " 표시 후 누적)
              Object.entries(pendingMemos).forEach(([studentId, memo]: [string, any]) => {
                if (mergedDrafts[studentId] && mergedDrafts[studentId].trim()) {
                  mergedDrafts[studentId] = `${mergedDrafts[studentId]} / ${memo}`;
                } else {
                  mergedDrafts[studentId] = memo;
                }
              });
              // 병합 후 localStorage에서 제거
              localStorage.removeItem(pendingMemosKey);
            } catch (e) {
              console.error('pendingMemos 파싱 실패:', e);
            }
          }
          
          setIndividualNoticeDrafts(mergedDrafts);
          console.log('✅ 개별공지 입력값 복원:', Object.keys(mergedDrafts).length, '명');
        } else {
          // individualNoticeDrafts가 없어도 pendingMemos 확인
          const pendingMemosKey = `pendingMemosToBoard_${user.id}_${currentClass.id}`;
          const pendingMemosStr = localStorage.getItem(pendingMemosKey);
          if (pendingMemosStr) {
            try {
              const pendingMemos = JSON.parse(pendingMemosStr);
              console.log('📥 보드 상태 복원 중 - pendingMemos 발견 (individualNoticeDrafts 없음):', pendingMemos);
              setIndividualNoticeDrafts(pendingMemos);
              localStorage.removeItem(pendingMemosKey);
              console.log('✅ 개별공지 입력값 복원 (pendingMemos만):', Object.keys(pendingMemos).length, '명');
            } catch (e) {
              console.error('pendingMemos 파싱 실패:', e);
            }
          }
        }
        if (state.showDiceForm !== undefined) {
          setShowDiceForm(state.showDiceForm);
          console.log('✅ 주사위 폼 복원:', state.showDiceForm);
        }
        if (state.selectedDiceTab !== undefined) {
          setSelectedDiceTab(state.selectedDiceTab);
        }
        if (state.startNumber !== undefined) {
          setStartNumber(state.startNumber);
          setPrevStartNumber(state.startNumber); // 복원 시 이전 값도 함께 설정
        }
        if (state.endNumber !== undefined) {
          setEndNumber(state.endNumber);
          setPrevEndNumber(state.endNumber); // 복원 시 이전 값도 함께 설정
        }
        if (state.selectedDate !== undefined) {
          setSelectedDate(state.selectedDate);
        }
        if (state.selectedCategory !== undefined) {
          setSelectedCategory(state.selectedCategory);
        }
        if (state.continuousCount !== undefined) {
          setContinuousCount(state.continuousCount);
        }
        
        // 뽑기 진행 상태 복원
        if (state.numberDrawnNumbers !== undefined && Array.isArray(state.numberDrawnNumbers) && state.numberDrawnNumbers.length > 0) {
          setNumberDrawnNumbers(new Set(state.numberDrawnNumbers));
          console.log('✅ 번호 뽑은 목록 복원:', state.numberDrawnNumbers.length, '개');
        }
        if (state.numberResults !== undefined && Array.isArray(state.numberResults)) {
          if (state.numberResults.length > 0) {
            setNumberResults(state.numberResults);
            // 단일 결과도 설정
            if (state.numberResults.length === 1) {
              setNumberResult(state.numberResults[0]);
            } else {
              setNumberResult(null);
            }
            console.log('✅ 번호 뽑기 결과 복원:', state.numberResults.length, '개');
          } else {
            // 빈 배열이면 초기화
            setNumberResults([]);
            setNumberResult(null);
          }
        }
        // profileDrawnNumbers는 profileResults 복원 시 함께 업데이트되므로 여기서는 복원하지 않음
        // (profileResults가 없으면 복원)
        if (state.profileResults === undefined || !Array.isArray(state.profileResults) || state.profileResults.length === 0) {
          if (state.profileDrawnNumbers !== undefined && Array.isArray(state.profileDrawnNumbers) && state.profileDrawnNumbers.length > 0) {
            setProfileDrawnNumbers(new Set(state.profileDrawnNumbers));
            console.log('✅ 프로필 뽑은 목록 복원 (결과 없음):', state.profileDrawnNumbers.length, '개');
          }
        }
      } else {
        console.log('ℹ️ 저장된 보드 상태 없음 - 초기 상태로 공지-전체공지 열기');
        // 저장된 상태가 없으면 초기 상태로 공지-전체공지 열기
        setShowNoticeForm(true);
        setNoticeTab('GeneralNotice');
      }
    } catch (error) {
      console.error('❌ 기본 상태 복원 실패:', error);
    } finally {
      // 복원 완료 후 약간의 지연을 두고 플래그 해제
      setTimeout(() => {
        setIsRestoring(false);
      }, 1000);
    }
  }, [user, currentClass, getBoardStateKeyMemo]); // user와 currentClass가 변경될 때만 실행

  // 보드 페이지 상태 저장 (로컬 스토리지만 사용, 서버 통신 없음)
  const saveBoardState = useCallback(() => {
    if (!user || !currentClass) return;
    
    try {
      // profileResult가 있으면 profileResults에 포함 (중복 제거)
      const allProfileResults = profileResult 
        ? [profileResult, ...profileResults.filter(m => m.id !== profileResult.id)]
        : profileResults;
      
      const state = {
        showNotebookForm,
        showNoticeForm,
        noticeTab,
        showDiceForm,
        selectedDiceTab,
        startNumber,
        endNumber,
        selectedDate,
        selectedTimeTagId: selectedTimeTag?.id || null,
        selectedCategory,
        // 뽑기 진행 상태 저장
        numberDrawnNumbers: Array.from(numberDrawnNumbers),
        numberResults,
        profileDrawnNumbers: Array.from(profileDrawnNumbers),
        // profileResult와 profileResults 모두 저장
        profileResults: allProfileResults.map(m => ({ id: m.id, number: m.number, name: m.name })), // 필요한 필드만 저장
        profileResult: profileResult ? { id: profileResult.id, number: profileResult.number, name: profileResult.name } : null, // 단일 결과도 저장
        teamResult: teamResult ? teamResult.map(team => team.map(m => ({ id: m.id, number: m.number, name: m.name }))) : null,
        continuousCount,
        individualNoticeDrafts, // 개별공지 입력값 저장
      };
      const key = getBoardStateKeyMemo(user.id, currentClass.id);
      localStorage.setItem(key, JSON.stringify(state));
      console.log('💾 보드 상태 저장:', { 
        profileResult: state.profileResult, 
        profileResults: state.profileResults, 
        profileResultsLength: state.profileResults?.length || 0,
        teamResult: state.teamResult,
        profileDrawnNumbers: state.profileDrawnNumbers?.length || 0
      });
    } catch (error) {
      console.error('❌ 보드 상태 저장 실패:', error);
    }
  }, [user, currentClass, showNotebookForm, showNoticeForm, noticeTab, showDiceForm, selectedDiceTab, startNumber, endNumber, selectedDate, selectedTimeTag, selectedCategory, numberDrawnNumbers, numberResults, profileDrawnNumbers, profileResult, profileResults, teamResult, continuousCount, individualNoticeDrafts, getBoardStateKeyMemo]);

  // classMembers가 로드된 후 profileResults와 teamResult 복원 (중복 제거)

  // 복원 중인지 추적하는 플래그
  const [isRestoringState, setIsRestoringState] = useState<boolean>(false);
  
  // classMembers가 로드된 후 profileResults와 teamResult 복원
  const [profileResultsRestored, setProfileResultsRestored] = useState<boolean>(false);
  const [teamResultRestored, setTeamResultRestored] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!user?.id || !currentClass?.id) return;
      try {
        const mode = await defaultSetService.loadMemberProfileDisplayMode(user.id, currentClass.id);
        if (mounted) setMemberProfileDisplayMode(mode);
      } catch {
        if (mounted) setMemberProfileDisplayMode(readMemberProfileDisplayMode(user.id, currentClass.id));
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user?.id, currentClass?.id]);

  useEffect(() => {
    const handleDisplayModeChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ userId: string; classId: string; mode: MemberProfileDisplayMode }>;
      if (!customEvent.detail) return;
      if (customEvent.detail.userId === user?.id && customEvent.detail.classId === currentClass?.id) {
        setMemberProfileDisplayMode(customEvent.detail.mode);
      }
    };
    window.addEventListener('memberProfileDisplayModeChanged', handleDisplayModeChanged);
    return () => {
      window.removeEventListener('memberProfileDisplayModeChanged', handleDisplayModeChanged);
    };
  }, [user?.id, currentClass?.id]);
  
  // 상태 변경 시 자동 저장 (복원 중이 아닐 때만, 그리고 주요 데이터 복원이 완료된 후)
  useEffect(() => {
    if (user && currentClass && !isRestoringState && !isRestoring && profileResultsRestored && teamResultRestored) {
      saveBoardState();
    }
  }, [user, currentClass, showNotebookForm, showNoticeForm, noticeTab, showDiceForm, selectedDiceTab, startNumber, endNumber, selectedDate, selectedTimeTag, selectedCategory, numberDrawnNumbers, numberResults, profileDrawnNumbers, profileResult, profileResults, teamResult, continuousCount, individualNoticeDrafts, isRestoringState, isRestoring, profileResultsRestored, teamResultRestored, saveBoardState]);
  
  // profileResults 복원
  useEffect(() => {
    if (user && currentClass && isClassMembersLoaded && !profileResultsRestored) {
      const key = getBoardStateKeyMemo(user.id, currentClass.id);
      const saved = localStorage.getItem(key);
      console.log('🔄 프로필 결과 복원 조건 체크:', { 
        user: !!user, 
        currentClass: !!currentClass, 
        isClassMembersLoaded,
        classMembersLength: classMembers.length, 
        profileResultsRestored,
        saved: !!saved 
      });
      if (saved) {
        try {
          const state = JSON.parse(saved);
          console.log('🔄 프로필 결과 복원 시도:', {
            profileResult: state.profileResult,
            profileResults: state.profileResults,
            profileResultsLength: state.profileResults?.length || 0,
            profileDrawnNumbers: state.profileDrawnNumbers?.length || 0
          });
          
          // profileResult와 profileResults 복원 (둘 다 확인)
          let restoredProfileResult: ClassMember | null = null;
          let restoredProfileResults: ClassMember[] = [];
          let allRestoredNumbers: number[] = [];
          
          // 1. profileResult 복원 (단일 결과)
          if (state.profileResult && typeof state.profileResult === 'object' && state.profileResult.id) {
            console.log('📋 저장된 단일 프로필 결과:', state.profileResult);
            const member = classMembers.find(m => m.id === state.profileResult.id);
            if (member) {
              restoredProfileResult = member;
              const num = parseInt(member.number || '0');
              if (!isNaN(num) && num > 0) allRestoredNumbers.push(num);
              console.log('✅ 단일 프로필 결과 복원:', member.name);
            } else {
              console.warn('⚠️ 단일 프로필 멤버를 찾을 수 없음:', state.profileResult.id);
            }
          }
          
          // 2. profileResults 복원 (연속 결과)
          if (state.profileResults !== undefined && Array.isArray(state.profileResults)) {
            console.log('📋 저장된 프로필 결과 배열:', state.profileResults);
            if (state.profileResults.length > 0) {
              restoredProfileResults = state.profileResults.map((saved: any) => {
                const member = classMembers.find(m => m.id === saved.id);
                if (!member) {
                  console.warn('⚠️ 멤버를 찾을 수 없음:', saved.id, saved);
                }
                return member || null;
              }).filter((m: any) => m !== null) as ClassMember[];
              
              // profileResult가 있으면 profileResults에서 제외 (중복 방지)
              if (restoredProfileResult) {
                restoredProfileResults = restoredProfileResults.filter(m => m.id !== restoredProfileResult!.id);
              }
              
              restoredProfileResults.forEach(m => {
                const num = parseInt(m.number || '0');
                if (!isNaN(num) && num > 0) allRestoredNumbers.push(num);
              });
              
              console.log('✅ 프로필 결과 배열 복원:', restoredProfileResults.length, '개');
            }
          }
          
          // 3. 복원된 결과가 있으면 상태 업데이트
          if (restoredProfileResult || restoredProfileResults.length > 0) {
            setIsRestoringState(true); // 복원 시작
            
            if (restoredProfileResult) {
              setProfileResult(restoredProfileResult);
            } else {
              setProfileResult(null);
            }
            setProfileResults(restoredProfileResults);
            
            console.log('📋 복원된 모든 프로필 번호들:', allRestoredNumbers);
            
            // 저장된 profileDrawnNumbers 가져오기
            const savedDrawnNumbers: Set<number> = state.profileDrawnNumbers && Array.isArray(state.profileDrawnNumbers) 
              ? new Set(state.profileDrawnNumbers.map((n: any) => parseInt(n)).filter((n: any) => !isNaN(n) && typeof n === 'number'))
              : new Set<number>();
            
            // 병합 (저장된 번호 + 복원된 결과 번호)
            const mergedNumbers = new Set<number>([...Array.from(savedDrawnNumbers), ...allRestoredNumbers]);
            console.log('📋 저장된 뽑은 번호들:', Array.from(savedDrawnNumbers));
            console.log('✅ 프로필 뽑은 목록 병합:', Array.from(mergedNumbers));
            setProfileDrawnNumbers(mergedNumbers);
            
            const totalCount = (restoredProfileResult ? 1 : 0) + restoredProfileResults.length;
            console.log('✅ 프로필 뽑기 결과 복원 완료:', totalCount, '개 (단일:', restoredProfileResult ? '1' : '0', ', 연속:', restoredProfileResults.length, ')');
            
            // 복원 완료 후 약간의 지연을 두고 플래그 해제 (상태 업데이트가 완료될 시간을 줌)
            setTimeout(() => {
              setIsRestoringState(false);
            }, 100);
          } else {
            console.log('ℹ️ 복원할 프로필 결과가 없음');
          }
          
          setProfileResultsRestored(true);
        } catch (error) {
          console.error('❌ 프로필 결과 복원 실패:', error);
          setProfileResultsRestored(true);
        }
      } else {
        setProfileResultsRestored(true);
      }
    }
  }, [user, currentClass, classMembers, isClassMembersLoaded, profileResultsRestored, getBoardStateKeyMemo]);

  // teamResult 복원
  useEffect(() => {
    if (user && currentClass && isClassMembersLoaded && !teamResultRestored) {
      const key = getBoardStateKeyMemo(user.id, currentClass.id);
      const saved = localStorage.getItem(key);
      if (saved) {
        try {
          const state = JSON.parse(saved);
          console.log('🔄 팀 결과 복원 시도:', state);
          
          if (state.teamResult !== undefined && state.teamResult !== null && Array.isArray(state.teamResult)) {
            console.log('📋 저장된 팀 결과:', state.teamResult);
            if (state.teamResult.length > 0) {
              const restored = state.teamResult.map((team: any[]) => {
                return team.map((saved: any) => {
                  const member = classMembers.find(m => m.id === saved.id);
                  if (!member) {
                    console.warn('⚠️ 팀 멤버를 찾을 수 없음:', saved.id);
                  }
                  return member || null;
                }).filter((m: any) => m !== null) as ClassMember[];
              }).filter((team: ClassMember[]) => team.length > 0) as ClassMember[][];
              console.log('📋 복원된 팀 결과:', restored.length, '팀', restored);
              if (restored.length > 0) {
                setIsRestoringState(true); // 복원 시작
                setTeamResult(restored);
                console.log('✅ 팀 나누기 결과 복원 완료:', restored.length, '팀');
                // 복원 완료 후 약간의 지연을 두고 플래그 해제
                setTimeout(() => {
                  setIsRestoringState(false);
                }, 100);
              } else {
                console.warn('⚠️ 복원된 팀 결과가 없음 (classMembers에서 찾을 수 없음)');
              }
            } else {
              console.log('ℹ️ 저장된 팀 결과가 빈 배열');
            }
            setTeamResultRestored(true);
          } else {
            console.log('ℹ️ 저장된 팀 결과가 없음');
            setTeamResultRestored(true);
          }
        } catch (error) {
          console.error('❌ 팀 결과 복원 실패:', error);
          setTeamResultRestored(true);
        }
      } else {
        setTeamResultRestored(true);
      }
    }
  }, [user, currentClass, classMembers, isClassMembersLoaded, teamResultRestored, getBoardStateKeyMemo]);
  
  // user나 currentClass가 변경되면 복원 플래그 리셋
  useEffect(() => {
    setProfileResultsRestored(false);
    setTeamResultRestored(false);
    setIsRestoringState(false);
    setIsClassMembersLoaded(false);
  }, [user, currentClass]);

  useEffect(() => {
    if (!user || !currentClass) return;

      setCategoriesLoaded(false); // 카테고리 로딩 상태 초기화
    setIsClassMembersLoaded(false);

    const loadAllData = async () => {
      try {
        if (timeTagService) {
          const timeTags = await timeTagService.getTimeTags(user.id, currentClass.id);
          setAvailableTimeTags(timeTags);
        }
      } catch (error) {
        console.error('타임 태그 로드 실패:', error);
      }
    
    try {
      const cats = await boardService.getCategories(user.id, currentClass.id);
      setCategories(cats);
      if (cats.length > 0) {
        const userLimits = getLicenseLimits(user.userType);
        const maxCategories = userLimits.maxNoteCategories || 5;
        const selectedCatIndex = cats.findIndex(c => c.name === selectedCategory);
        const isSelectedDisabled = selectedCatIndex >= maxCategories;
        if (!cats.find(c => c.name === selectedCategory) || isSelectedDisabled) {
          const firstEnabledCategory = cats.find((_, index) => index < maxCategories);
          setSelectedCategory(firstEnabledCategory ? firstEnabledCategory.name : cats[0]?.name || '');
        }
      } else {
        setSelectedCategory('');
      }
    } catch (error) {
      console.error('❌ 카테고리 로드 실패:', error);
      } finally {
        setCategoriesLoaded(true);
      }

      try {
        const members = await userDataService.loadClassMembers(user.id, currentClass.id, currentMemberGroup);
        setClassMembers(members);
      } catch (error) {
        console.error('❌ 클래스 멤버 로드 실패:', error);
      } finally {
        setIsClassMembersLoaded(true);
      }
    };

    loadAllData();
  }, [user, currentClass, currentMemberGroup, selectedCategory, timeTagService]);

  // availableTimeTags 로드 시 현재 시각에 맞는 타임태그 자동 선택 (없으면 선택 안됨)
  useEffect(() => {
    if (availableTimeTags.length > 0 && user && currentClass) {
      selectCurrentTimeTag();
    } else if (availableTimeTags.length === 0) {
      setSelectedTimeTag(null);
      setSelectedDayOfWeek(['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]);
    }
  }, [availableTimeTags, selectCurrentTimeTag, user, currentClass]);

  // 🎯 자리 배치 데이터 로드
  const loadSeatArrangementData = useCallback(async () => {
    if (!user || !currentClass) {
      setSeatArrangementError('클래스 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      const { ClassMembersPartitionService } = await import('../services/classMembersPartitionService');
      
      console.log('🎯 [BoardPage 자리배치] 자리 배치 데이터 로드 시작');
      
      // 현재 선택된 멤버 그룹의 ClassMembers 파티션에서 멤버 로드
      const partitionService = new ClassMembersPartitionService(user.id, currentClass.id, currentMemberGroup);
      const members = await partitionService.loadAllMembers();

      if (members.length === 0) {
        console.log('❌ [BoardPage 자리배치] 멤버가 없음');
        setSeatArrangementError('자리배치를 먼저 해주세요');
        setSeatArrangementData(null);
        return;
      }
      
      // 현재 월에 해당하는 자리 배치 찾기
      const currentDate = new Date();
      const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      
      // 자리 배치 데이터가 있는 월들 찾기
      const availableMonths = new Set<string>();
      members.forEach((member: any) => {
        if (member.seatPositions && typeof member.seatPositions === 'object') {
          Object.keys(member.seatPositions).forEach((month: string) => {
            availableMonths.add(month);
          });
        }
      });

      let selectedMonth = currentMonth;
      
      // 현재 월이 없으면 가장 최근 월 선택
      if (!availableMonths.has(currentMonth)) {
        const sortedMonths = Array.from(availableMonths).sort().reverse();
        if (sortedMonths.length === 0) {
          console.log('❌ [BoardPage 자리배치] 자리 배치 데이터가 전혀 없음');
          setSeatArrangementError('자리배치를 먼저 해주세요');
          setSeatArrangementData(null);
          return;
        }
        selectedMonth = sortedMonths[0];
      }

      // 선택된 월의 자리 배치 데이터 추출
      const seatData = classMembers.map((member: ClassMember) => {
        const memberData = members.find((m: any) => m.id === member.id);
        const seatPosition = memberData?.seatPositions?.[selectedMonth];
        
        return {
          studentId: member.id,
          name: member.name || '이름 없음',
          number: member.number,
          seatRow: seatPosition?.row || 0,
          seatCol: seatPosition?.col || 0,
          profileImage: member.profileImage,
        };
      });

      console.log('✅ [BoardPage 자리배치] 자리 배치 데이터 로드 완료:', seatData.length, '명');
      setSeatArrangementData(seatData);
      setSeatArrangementError('');
    } catch (error) {
      console.error('❌ [BoardPage 자리배치] 자리 배치 데이터 로드 실패:', error);
      setSeatArrangementError('자리배치를 먼저 해주세요');
      setSeatArrangementData(null);
    }
  }, [user, currentClass, currentMemberGroup, classMembers]);

  // 자리 모드로 전환 시 자리배치 데이터 로드
  useEffect(() => {
    if (viewMode === 'seat' && classMembers.length > 0 && !seatArrangementData && !seatArrangementError) {
      loadSeatArrangementData().catch((error) => {
        console.error('❌ [BoardPage] 자리배치 데이터 로드 실패:', error);
      });
    }
  }, [viewMode, classMembers.length, seatArrangementData, seatArrangementError, loadSeatArrangementData]);

  // 주사위 섹션 기능 함수들
  const handleStartNumberChange = (value: number) => {
    const newValue = Math.max(1, Math.min(999, value));
    setStartNumber(newValue);
    if (newValue > endNumber) {
      setEndNumber(newValue);
    }
  };

  const handleEndNumberChange = (value: number) => {
    const newValue = Math.max(startNumber, Math.min(999, value));
    setEndNumber(newValue);
  };

  const handleResetStartNumber = () => {
    setStartNumber(1);
  };

  const handleResetEndNumber = () => {
    if (classMembers.length > 0) {
      const maxNumber = Math.max(
        ...classMembers
          .map(m => parseInt(m.number || '0'))
          .filter(num => !isNaN(num) && num > 0)
      );
      if (maxNumber > 0) {
        setEndNumber(maxNumber);
      } else {
        setEndNumber(30);
      }
    } else {
      setEndNumber(30);
    }
  };

  const handleIncrementStart = () => {
    handleStartNumberChange(startNumber + 1);
  };

  const handleDecrementStart = () => {
    handleStartNumberChange(startNumber - 1);
  };

  const handleIncrementEnd = () => {
    handleEndNumberChange(endNumber + 1);
  };

  const handleDecrementEnd = () => {
    handleEndNumberChange(endNumber - 1);
  };

  // 프로필 뽑기용 사용 가능한 멤버 목록 업데이트 (탭 변경 시 또는 번호 범위 변경 시)
  useEffect(() => {
    if (classMembers.length > 0) {
      // selectedDiceTab이 'profile'이거나 복원을 위해 항상 업데이트
      const matchingMembers = classMembers.filter(m => {
        const memberNumber = parseInt(m.number || '0');
        return memberNumber >= startNumber && memberNumber <= endNumber;
      });
      setProfileAvailableMembers(matchingMembers);
    }
  }, [classMembers, startNumber, endNumber]);

  // 번호 범위 변경 시 프로필 뽑기 결과 초기화 (복원 중이 아닐 때만, 그리고 사용자가 직접 변경한 경우만)
  const [prevStartNumber, setPrevStartNumber] = useState<number>(1);
  const [prevEndNumber, setPrevEndNumber] = useState<number>(20);
  
  useEffect(() => {
    // 복원 중이 아니고, 프로필 결과 복원도 완료된 후에만 초기화
    // 그리고 실제로 번호 범위가 변경된 경우만 (복원으로 인한 변경이 아닌 경우)
    const isRangeChanged = prevStartNumber !== startNumber || prevEndNumber !== endNumber;
    
    if (!isRestoring && !isRestoringState && profileResultsRestored && isRangeChanged && selectedDiceTab === 'profile' && (profileResult !== null || profileDrawnNumbers.size > 0 || profileResults.length > 0)) {
      console.log('🔄 번호 범위 변경으로 프로필 뽑기 결과 초기화', { prevStartNumber, startNumber, prevEndNumber, endNumber });
      setProfileResult(null);
      setProfileDrawnNumbers(new Set());
      setProfileResults([]);
    }
    
    // 이전 값 업데이트 (복원 완료 후에만)
    if (profileResultsRestored && !isRestoring && !isRestoringState) {
      setPrevStartNumber(startNumber);
      setPrevEndNumber(endNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startNumber, endNumber, isRestoring, isRestoringState, profileResultsRestored]);

  // 보드 페이지 처음 로드 시 시작/끝 숫자 초기화 (한 번만)
  useEffect(() => {
    if (!isDiceInitialized && classMembers.length > 0) {
      // 시작 번호를 1로 설정
      setStartNumber(1);
      
      // 끝 번호를 클래스 멤버의 최대 번호로 설정
      const maxNumber = Math.max(
        ...classMembers
          .map(m => parseInt(m.number || '0'))
          .filter(num => !isNaN(num) && num > 0)
      );
      
      if (maxNumber > 0) {
        setEndNumber(maxNumber);
      }
      
      setIsDiceInitialized(true);
    }
  }, [classMembers, isDiceInitialized]);

  // 팀 블록들의 최대 너비 계산 및 적용
  useEffect(() => {
    if (teamResult && teamResult.length > 0) {
      // 측정 시작 - 블록을 숨김
      setIsTeamBlockMeasuring(true);
      // 먼저 maxTeamBlockWidth를 null로 설정하여 각 블록의 자연스러운 너비를 측정할 수 있게 함
      setMaxTeamBlockWidth(null);
      
      // 약간의 지연 후 모든 ref가 설정되고 DOM이 업데이트된 후 계산
      const timer = setTimeout(() => {
        // 먼저 모든 블록의 width 스타일을 제거하여 자연스러운 너비 측정
        teamBlockRefs.current.forEach(ref => {
          if (ref) {
            ref.style.width = '';
          }
        });
        
        // DOM 업데이트를 기다린 후 자연스러운 너비 측정
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const widths = teamBlockRefs.current
              .filter(ref => ref !== null && ref !== undefined)
              .map(ref => {
                if (!ref) return 0;
                // offsetWidth를 사용하여 실제 렌더링된 너비 측정
                return ref.offsetWidth || ref.scrollWidth || 0;
              });
            
            if (widths.length > 0 && widths.every(w => w > 0)) {
              const maxWidth = Math.max(...widths);
              setMaxTeamBlockWidth(maxWidth);
              // 너비 설정 후 다음 프레임에서 표시하여 깜박임 방지
              requestAnimationFrame(() => {
                setIsTeamBlockMeasuring(false);
              });
            } else {
              setIsTeamBlockMeasuring(false);
            }
          });
        });
      }, 200);

      return () => clearTimeout(timer);
    } else {
      setMaxTeamBlockWidth(null);
      setIsTeamBlockMeasuring(false);
    }
  }, [teamResult]);

  // 번호 뽑기용 사용 가능한 번호 목록 계산
  const getAvailableNumbersForNumber = useCallback(() => {
    const available: number[] = [];
    for (let i = startNumber; i <= endNumber; i++) {
      if (!numberDrawnNumbers.has(i)) {
        available.push(i);
      }
    }
    return available;
  }, [startNumber, endNumber, numberDrawnNumbers]);

  // 프로필 뽑기용 사용 가능한 멤버 목록 계산
  const getAvailableMembersForProfile = useCallback(() => {
    return profileAvailableMembers.filter(m => {
      const memberNumber = parseInt(m.number || '0');
      return !profileDrawnNumbers.has(memberNumber);
    });
  }, [profileAvailableMembers, profileDrawnNumbers]);

  // 번호 뽑기 실행 (연속 뽑기 지원)
  const handleDrawNumber = () => {
    const available = getAvailableNumbersForNumber();
    if (available.length === 0) {
      showToastMessage('뽑을 수 있는 번호가 없습니다. 초기화 후 다시 시도해주세요.');
      return;
    }
    
    const count = Math.min(continuousCount, available.length);
    if (count <= 0) {
      alert('연속 뽑기 개수를 1 이상으로 설정해주세요.');
      return;
    }
    
    // 연속으로 뽑기
    const drawnNumbers: number[] = [];
    const newDrawnSet = new Set(numberDrawnNumbers);
    
    for (let i = 0; i < count; i++) {
      if (available.filter(n => !newDrawnSet.has(n)).length === 0) break;
      
      const remaining = available.filter(n => !newDrawnSet.has(n));
      const randomIndex = Math.floor(Math.random() * remaining.length);
      const drawnNumber = remaining[randomIndex];
      
      drawnNumbers.push(drawnNumber);
      newDrawnSet.add(drawnNumber);
    }
    
    setNumberDrawnNumbers(newDrawnSet);
    
    if (count === 1) {
      setNumberResult(drawnNumbers[0]);
      setNumberResults([]);
    } else {
      setNumberResult(null);
      setNumberResults(drawnNumbers);
    }
  };

  // 토스트 메시지 표시 함수
  const showToastMessage = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 1500);
  }, []);

  // 프로필 뽑기 실행 (연속 뽑기 지원)
  const handleDrawProfile = () => {
    const available = getAvailableMembersForProfile();
    if (available.length === 0) {
      showToastMessage('뽑을 수 있는 멤버가 없습니다. 초기화 후 다시 시도해주세요.');
      return;
    }
    
    const count = Math.min(continuousCount, available.length);
    if (count <= 0) {
      alert('연속 뽑기 개수를 1 이상으로 설정해주세요.');
      return;
    }
    
    // 연속으로 뽑기
    const drawnMembers: ClassMember[] = [];
    const newDrawnSet = new Set(profileDrawnNumbers);
    
    for (let i = 0; i < count; i++) {
      const remaining = available.filter(m => {
        const memberNumber = parseInt(m.number || '0');
        return !newDrawnSet.has(memberNumber);
      });
      
      if (remaining.length === 0) break;
      
      const randomIndex = Math.floor(Math.random() * remaining.length);
      const selectedMember = remaining[randomIndex];
      const selectedNumber = parseInt(selectedMember.number || '0');
      
      drawnMembers.push(selectedMember);
      newDrawnSet.add(selectedNumber);
    }
    
    setProfileDrawnNumbers(newDrawnSet);
    
    if (count === 1) {
      setProfileResult(drawnMembers[0]);
      setProfileResults([]);
    } else {
      setProfileResult(null);
      setProfileResults(drawnMembers);
    }
  };

  // 최대 팀 수 계산 (전체 인원수의 절반)
  const maxTeamCount = useMemo(() => {
    const matchingMembers = classMembers.filter(m => {
      const memberNumber = parseInt(m.number || '0');
      return memberNumber >= startNumber && memberNumber <= endNumber;
    });
    return Math.max(2, Math.floor(matchingMembers.length / 2));
  }, [classMembers, startNumber, endNumber]);

  // 팀 수 증가
  const handleIncrementTeamCount = () => {
    if (teamCount < maxTeamCount) {
      setTeamCount(teamCount + 1);
    }
  };

  // 팀 수 감소
  const handleDecrementTeamCount = () => {
    if (teamCount > 2) {
      setTeamCount(teamCount - 1);
    }
  };

  // 팀 수 초기화
  const handleResetTeamCount = () => {
    setTeamCount(2);
  };

  // 팀 나누기 실행
  const handleDivideTeams = () => {
    const matchingMembers = classMembers.filter(m => {
      const memberNumber = parseInt(m.number || '0');
      return memberNumber >= startNumber && memberNumber <= endNumber;
    });
    
    if (matchingMembers.length === 0) {
      alert('해당 번호 범위에 등록된 멤버가 없습니다.');
      return;
    }
    
    const currentMaxTeamCount = Math.max(2, Math.floor(matchingMembers.length / 2));
    if (teamCount < 2 || teamCount > currentMaxTeamCount) {
      alert(`팀 수는 2개 이상 ${currentMaxTeamCount}개 이하여야 합니다.`);
      return;
    }
    
    // 멤버 목록을 섞기
    const shuffled = [...matchingMembers].sort(() => Math.random() - 0.5);
    
    // 팀별로 나누기
    const teams: ClassMember[][] = Array(teamCount).fill(null).map(() => []);
    shuffled.forEach((member, index) => {
      teams[index % teamCount].push(member);
    });
    
    setTeamResult(teams);
    setShowTeamCountModal(false);
  };

  // 번호 뽑기 결과 초기화
  const handleResetNumberResult = () => {
    setNumberDrawnNumbers(new Set());
    setNumberResult(null);
    setNumberResults([]);
  };

  // 프로필 뽑기 결과 초기화
  const handleResetProfileResult = () => {
    setProfileDrawnNumbers(new Set());
    setProfileResult(null);
    setProfileResults([]);
  };

  // 연속 뽑기 개수 변경
  const handleContinuousCountChange = (value: number) => {
    const newValue = Math.max(1, Math.min(100, value));
    setContinuousCount(newValue);
  };

  const handleIncrementContinuous = () => {
    handleContinuousCountChange(continuousCount + 1);
  };

  const handleDecrementContinuous = () => {
    handleContinuousCountChange(continuousCount - 1);
  };

  // 팀 나누기 결과 초기화
  const handleResetTeamResult = () => {
    setTeamResult(null);
  };

  // 남은 수 계산 (번호 뽑기용)
  const numberRemainingCount = useMemo(() => {
    return getAvailableNumbersForNumber().length;
  }, [getAvailableNumbersForNumber]);

  // 남은 수 계산 (프로필 뽑기용)
  const profileRemainingCount = useMemo(() => {
    return getAvailableMembersForProfile().length;
  }, [getAvailableMembersForProfile]);


  // 메모 저장/수정
  const handleSaveNote = async () => {
    if (!user || !currentClass) return;
    
    if (!noteContent.trim()) {
      showToastMessage('메모 내용을 입력해주세요.');
      return;
    }

    if (!selectedCategory || categories.length === 0) {
      showToastMessage('노트북 카테고리를 먼저 생성해 주세요.');
      return;
    }

    setIsSaving(true);
    try {
      const category = categories.find(cat => cat.name === selectedCategory);
      const categoryId = category?.id || selectedCategory;
      const isEdit = !!editingNoteId;

      if (editingNoteId) {
        // 수정하기
        await boardService.updateNote(user.id, currentClass.id, editingNoteId, {
          content: noteContent.trim(),
          relatedStudents,
          date: selectedDate,
          timeTagId: selectedTimeTag?.id,
          timeTagName: selectedTimeTag ? formatTimeTagLabel(selectedTimeTag as TimeTag, selectedDayOfWeek) : undefined
        });
        // 목록 즉시 반영: 해당 카테고리 노트 다시 로드
        const notes = await boardService.loadRecentNotesForCategory(user.id, currentClass.id, categoryId || '');
        setAllNotes(notes);
        const idx = notes.findIndex((n: any) => n.id === editingNoteId);
        setCurrentNoteIndex(idx >= 0 ? idx : 0);
      } else {
        // 저장하기 (새 노트)
        const savedNoteId = await boardService.saveNote(user.id, currentClass.id, {
          content: noteContent.trim(),
          relatedStudents,
          date: selectedDate,
          timeTagId: selectedTimeTag?.id,
          timeTagName: selectedTimeTag ? formatTimeTagLabel(selectedTimeTag as TimeTag, selectedDayOfWeek) : undefined
        }, categoryId || '');

        setEditingNoteId(savedNoteId);
        setOriginalNoteContent(noteContent.trim());
        setOriginalNoteDate(selectedDate);

        // 목록 즉시 반영: 해당 카테고리 노트 다시 로드 (새 노트가 맨 위에 표시됨)
        const notes = await boardService.loadRecentNotesForCategory(user.id, currentClass.id, categoryId || '');
        setAllNotes(notes);
        setCurrentNoteIndex(0); // 방금 저장한 노트가 최신이므로 첫 번째

        boardService.updateCategoryCache(user.id, currentClass.id, categoryId || '').catch(error => {
          console.error('백그라운드 캐시 업데이트 실패:', error);
        });
      }

      showToastMessage(`메모가 ${isEdit ? '수정' : '저장'}되었습니다.`);
    } catch (error) {
      console.error('❌ 메모 저장/수정 실패:', error);
      showToastMessage(`메모 저장에 실패했습니다.`);
    } finally {
      setIsSaving(false);
    }
  };

  // 글자 크기 조절 (localStorage에 저장)
  const handleIncreaseFontSize = () => {
    setFontSize(prev => {
      const newSize = Math.min(prev + 2, 64);
      localStorage.setItem('notebook-font-size', newSize.toString());
      return newSize;
    });
  };

  const handleDecreaseFontSize = () => {
    setFontSize(prev => {
      const newSize = Math.max(prev - 2, 12);
      localStorage.setItem('notebook-font-size', newSize.toString());
      return newSize;
    });
  };

  // 공지 노트를 보드에 로드
  const loadNoticeToBoard = useCallback((note: any) => {
    if (noticeTab === 'GeneralNotice') {
      setNoticeAllContent(note.content || '');
      // fontSize는 localStorage에서 로드한 값 사용 (노트 데이터에서 로드하지 않음)
      setEditingNoticeId(note.id);
      // 원본 데이터 저장 (변경사항 비교용) - 개별 값으로 분리하여 깜박임 방지
      setOriginalNoticeContent(note.content || '');
      setOriginalNoticeDate(note.date || '');
      // 날짜도 함께 설정
      if (note.date) {
        setSelectedDate(note.date);
      }
    }
  }, [noticeTab]);

  // 전체공지노트 로드 (단순화: 최근 공지가 있으면 로드, 없으면 새 노트 모드)
  const loadNoticeNotes = useCallback(async () => {
    if (!user || !currentClass) return;

    // 중복 실행 방지: 이미 로딩 중이면 return
    if (isLoadingNoticeNotesRef.current) {
      return;
    }

    isLoadingNoticeNotesRef.current = true;

    try {
      const notes = await boardService.getGeneralNoticeNotes(user.id, currentClass.id, normalizedCurrentMemberGroup);
      
      // getCreatedAtMillis를 함수 내부에서 정의하여 dependency 제거
      const getCreatedAtMillisLocal = (createdAt: any): number | null => {
        if (!createdAt) return null;
        try {
          if (typeof createdAt?.toMillis === 'function') return createdAt.toMillis();
          if (typeof createdAt?.toDate === 'function') return createdAt.toDate().getTime();
          if (typeof createdAt?.seconds === 'number') return createdAt.seconds * 1000;
          if (typeof createdAt?._seconds === 'number') return createdAt._seconds * 1000;
          return null;
        } catch {
          return null;
        }
      };
      
      const convertedNotes = notes.map((note) => ({
        id: `general-notice-${getCreatedAtMillisLocal(note.createdAt) ?? Date.now()}`,
        content: note.content,
        categoryId: '전체공지노트',
        noticeScope: (note as any).scope || 'all',
        relatedStudentIds: [],
        date: note.date || '',
        timeTagName: undefined,
        fontSize: note.fontSize,
        createdAt: note.createdAt,
        updatedAt: note.updatedAt,
      }));

      // 최신 노트가 앞으로 오도록 정렬
      convertedNotes.sort((a, b) => {
        const am = getCreatedAtMillisLocal(a.createdAt) ?? 0;
        const bm = getCreatedAtMillisLocal(b.createdAt) ?? 0;
        return bm - am;
      });

      setAllNoticeNotes(convertedNotes);

      // 스코프 전환(전체/그룹) 시에는 항상 해당 스코프의 최근 공지를 다시 로드해야 함.
      const currentEditingId = editingNoticeIdRef.current;
      const currentContent = noticeAllContentRef.current;

      const currentEditingNote = currentEditingId
        ? convertedNotes.find((n: any) => n.id === currentEditingId)
        : null;
      const isEditingScopeMismatch =
        !!currentEditingNote && (currentEditingNote.noticeScope || 'all') !== generalNoticeScope;

      const shouldAutoLoad =
        // 기존 조건: 편집 중이 아니고 입력이 비어있을 때
        (!currentEditingId && currentContent.trim() === '') ||
        // 또는 스코프가 바뀌어서 현재 편집 중인 노트가 다른 스코프일 때
        isEditingScopeMismatch;

      if (shouldAutoLoad) {
        const scopedLatest = convertedNotes.find((n: any) => (n.noticeScope || 'all') === generalNoticeScope);
        const latestNote = scopedLatest || convertedNotes[0] || null;

        if (latestNote) {
          setNoticeAllContent(latestNote.content || '');
          setEditingNoticeId(latestNote.id);
          editingNoticeIdRef.current = latestNote.id;
          setOriginalNoticeContent(latestNote.content || '');
          setOriginalNoticeDate(latestNote.date || '');
          if (latestNote.date) {
            setSelectedDate(latestNote.date);
          }
        } else {
          // 공지가 없으면 새 노트 모드
          setEditingNoticeId(null);
          editingNoticeIdRef.current = null as any;
          setNoticeAllContent('');
          setOriginalNoticeContent('');
          setOriginalNoticeDate('');
        }
      }
    } catch (error) {
      console.error('❌ 공지 노트 로드 실패:', error);
      showToastMessage('공지 노트를 불러오는데 실패했습니다.');
    } finally {
      isLoadingNoticeNotesRef.current = false;
    }
  }, [user, currentClass, showToastMessage, normalizedCurrentMemberGroup, generalNoticeScope]);

  const loadNoticeNotesRef = useRef<typeof loadNoticeNotes | null>(null);
  
  // loadNoticeNotes를 ref에 저장
  useEffect(() => {
    loadNoticeNotesRef.current = loadNoticeNotes;
  }, [loadNoticeNotes]);

  // 전체공지/그룹공지 전환 시: 해당 스코프 공지를 다시 로드하여
  // "저장하기/수정하기" 상태가 서로 섞이지 않도록 보장
  useEffect(() => {
    if (!showNoticeForm) return;
    if (noticeTab !== 'GeneralNotice') return;
    if (loadNoticeNotesRef.current) {
      loadNoticeNotesRef.current();
    }
  }, [generalNoticeScope, showNoticeForm, noticeTab]);

  // 멤버 그룹/클래스 변경 시 공지 로드 플래그 리셋 (그룹 전환 시 즉시 재로드 되도록)
  useEffect(() => {
    hasLoadedNoticeNotesRef.current = false;
    // 개별공지(날짜별 목록)도 그룹별로 완전히 분리되므로, 그룹 전환 시 목록을 비워서 재로드 유도
    setDateBasedIndividualNotices([]);
    setCurrentDateBasedIndex(0);
    setDeletedDateBasedNoticeKeys(new Set());
  }, [normalizedCurrentMemberGroup, currentClass?.id]);

  useEffect(() => {
    if (!showNoticeForm) {
      // 폼이 닫히면 플래그 리셋
      hasLoadedNoticeNotesRef.current = false;
      return;
    }
    // 개별공지노트 탭에서는 전체공지노트 로딩을 돌리지 않음
    if (noticeTab !== 'GeneralNotice') {
      hasLoadedNoticeNotesRef.current = false;
      return;
    }
    
    // 이미 로드했으면 다시 로드하지 않음
    if (hasLoadedNoticeNotesRef.current) {
      return;
    }
    
    // 중복 실행 방지: 이미 로딩 중이면 return
    if (isLoadingNoticeNotesRef.current) {
      return;
    }
    
    // 한 번만 로드
    hasLoadedNoticeNotesRef.current = true;
    if (loadNoticeNotesRef.current) {
      loadNoticeNotesRef.current();
    }
  }, [showNoticeForm, noticeTab, user, currentClass]);

  const handleNoticeIncreaseFontSize = () => {
    if (noticeTab === 'GeneralNotice') {
      setNoticeAllFontSize(prev => {
        const newSize = Math.min(prev + 2, 64);
        localStorage.setItem('noticeAllFontSize', newSize.toString());
        return newSize;
      });
    } else {
      setNoticeIndFontSize(prev => Math.min(prev + 2, 64));
    }
  };

  const handleNoticeDecreaseFontSize = () => {
    if (noticeTab === 'GeneralNotice') {
      setNoticeAllFontSize(prev => {
        const newSize = Math.max(prev - 2, 12);
        localStorage.setItem('noticeAllFontSize', newSize.toString());
        return newSize;
      });
    } else {
      setNoticeIndFontSize(prev => Math.max(prev - 2, 12));
    }
  };

  const sortNoticesByDateDesc = useCallback((notes: any[]) => {
    const getCreatedAtMillis = (n: any) => {
      const c = n?.createdAt;
      return typeof c?.toMillis === 'function'
        ? c.toMillis()
        : typeof c === 'number'
          ? c
          : 0;
    };
    return [...notes].sort((a, b) => {
      const da = (a?.date || '').toString();
      const db = (b?.date || '').toString();
      if (da !== db) return db.localeCompare(da); // YYYY-MM-DD
      return getCreatedAtMillis(b) - getCreatedAtMillis(a);
    });
  }, []);

  const openGeneralNoticeListModal = useCallback(async (scope: 'all' | 'group') => {
    if (!user || !currentClass) return;
    try {
      const nextNoteListCategory = scope === 'group' ? '그룹공지노트' : '전체공지노트';
      setNoteListCategory(nextNoteListCategory);
      const notes = await boardService.getGeneralNoticeNotes(user.id, currentClass.id, normalizedCurrentMemberGroup);
      const filteredNotes = notes.filter((note) => ((note as any).scope || 'all') === scope);
      const convertedNotes = sortNoticesByDateDesc(
        filteredNotes.map((note) => ({
          id: `general-notice-${getCreatedAtMillis(note.createdAt) ?? Date.now()}`,
          content: note.content,
          categoryId: nextNoteListCategory,
          noticeScope: (note as any).scope || 'all',
          relatedStudentIds: [],
          date: note.date || '',
          timeTagName: undefined,
          // fontSize는 UI에서 localStorage로 관리하므로 매핑하지 않음
          createdAt: note.createdAt,
          updatedAt: note.updatedAt,
        }))
      );
      setAllNotes(convertedNotes);
      setCurrentPage(1);
      setDeletedNoteIds(new Set());
      setExpandedNoteId(null); // 모달 열 때 확장 상태 초기화
      setShowNoteListModal(true);
    } catch (error) {
      console.error('❌ 공지 노트 목록 로드 실패:', error);
      alert(scope === 'group' ? '그룹공지 노트 목록을 불러오는데 실패했습니다.' : '전체공지 노트 목록을 불러오는데 실패했습니다.');
    }
  }, [user, currentClass, sortNoticesByDateDesc, getCreatedAtMillis, normalizedCurrentMemberGroup]);

  // 날짜별 개별공지 목록 로드
  const loadDateBasedIndividualNotices = useCallback(async () => {
    if (!user || !currentClass) return;
    try {
      const allNotes = await boardService.getAllIndividualNoticeNotes(user.id, currentClass.id, normalizedCurrentMemberGroup);
      
      // 날짜 + 저장 시간(초 단위)별로 그룹화
      // 같은 날짜에 같은 초에 저장된 것들을 하나의 그룹으로 묶음
      const dateTimeMap = new Map<string, Array<{
        id: string;
        content: string;
        date: string;
        studentId: string;
        studentName?: string;
        createdAt: any;
        updatedAt: any;
      }>>();
      
      allNotes.forEach((note) => {
        const date = note.date || '';
        const createdAtMillis = getCreatedAtMillis(note.createdAt) ?? Date.now();
        // 날짜 + 밀리초 단위 시간으로 그룹 키 생성
        // 같은 밀리초에 저장된 것들을 하나로 묶음 (한 번의 저장 작업은 같은 타임스탬프를 사용)
        const groupKey = `${date}_${createdAtMillis}`; // 밀리초 단위
        
        if (!dateTimeMap.has(groupKey)) {
          dateTimeMap.set(groupKey, []);
        }
        dateTimeMap.get(groupKey)!.push({
          id: `individual-notice-${note.studentId}-${createdAtMillis}`,
          content: note.content,
          date: note.date,
          studentId: note.studentId,
          studentName: note.studentName,
          createdAt: note.createdAt,
          updatedAt: note.updatedAt,
        });
      });
      
      // 날짜 + 시간별로 정렬 (최신이 위로)
      const dateBasedList = Array.from(dateTimeMap.entries())
        .map(([groupKey, notes]) => {
          // 첫 번째 노트의 날짜와 시간 정보 사용
          const firstNote = notes[0];
          const date = firstNote.date || '';
          const createdAtMillis = getCreatedAtMillis(firstNote.createdAt) ?? Date.now();
          const dateObj = date ? new Date(date) : new Date(createdAtMillis);
          const timeObj = new Date(createdAtMillis);
          
          // 날짜 포맷
          const dateFormatted = date ? 
            `${dateObj.getFullYear()}. ${String(dateObj.getMonth() + 1).padStart(2, '0')}. ${String(dateObj.getDate()).padStart(2, '0')}.` : 
            '날짜 없음';
          
          // 시간 포맷 추가
          const timeFormatted = `${String(timeObj.getHours()).padStart(2, '0')}:${String(timeObj.getMinutes()).padStart(2, '0')}`;
          
          return {
            date,
            dateFormatted: `${dateFormatted} ${timeFormatted}`,
            groupKey,
            createdAtMillis,
            notes: notes.sort((a, b) => {
              const aMillis = getCreatedAtMillis(a.createdAt) ?? 0;
              const bMillis = getCreatedAtMillis(b.createdAt) ?? 0;
              return bMillis - aMillis; // 최신이 위로
            }),
          };
        })
        .sort((a, b) => {
          // 먼저 날짜로 정렬 (최신 날짜가 위로)
          if (a.date && b.date) {
            const aDate = new Date(a.date);
            const bDate = new Date(b.date);
            const dateDiff = bDate.getTime() - aDate.getTime();
            if (dateDiff !== 0) return dateDiff;
          }
          // 같은 날짜면 저장 시간으로 정렬 (최신이 위로)
          return b.createdAtMillis - a.createdAtMillis;
        });
      
      setDateBasedIndividualNotices(dateBasedList);
      setCurrentDateBasedIndex(0);
      setDeletedDateBasedNoticeKeys(new Set()); // 목록 로드 시 삭제 표시 초기화
      return dateBasedList;
    } catch (error) {
      console.error('❌ 날짜별 개별공지 목록 로드 실패:', error);
      return [];
    }
  }, [user, currentClass, getCreatedAtMillis, normalizedCurrentMemberGroup]);

  // 개별공지 탭이 활성화될 때 날짜별 목록 자동 로드
  useEffect(() => {
    if (noticeTab === 'IndividualNotice' && user && currentClass && dateBasedIndividualNotices.length === 0) {
      loadDateBasedIndividualNotices();
    }
  }, [noticeTab, user, currentClass, dateBasedIndividualNotices.length, loadDateBasedIndividualNotices]);

  // 날짜별 개별공지를 보드 페이지에 로드
  const loadDateBasedNoticeToBoard = useCallback((dateNotice: {
    date: string;
    dateFormatted: string;
    notes: Array<{
      id: string;
      content: string;
      date: string;
      studentId: string;
      studentName?: string;
      createdAt: any;
      updatedAt: any;
    }>;
  }, replaceMode: boolean = false) => {
    // 날짜별 공지의 모든 학생별 메모를 individualNoticeDrafts에 로드
    const newDrafts: Record<string, string> = {};
    const editingNotes: Array<{
      studentId: string;
      timestamp: number;
      originalContent: string;
    }> = [];
    
    dateNotice.notes.forEach((note) => {
      if (note.studentId && note.content) {
        const timestamp = getCreatedAtMillis(note.createdAt) ?? Date.now();
        
        // 수정 모드 추적을 위한 정보 저장
        editingNotes.push({
          studentId: note.studentId,
          timestamp,
          originalContent: note.content,
        });
        
        // replaceMode가 true이면 덮어쓰기, false이면 기존 메모에 추가
        if (replaceMode) {
          newDrafts[note.studentId] = note.content;
        } else {
          // 기존 메모가 있으면 " / "로 구분하여 추가
          if (newDrafts[note.studentId] && newDrafts[note.studentId].trim()) {
            newDrafts[note.studentId] = `${newDrafts[note.studentId]} / ${note.content}`;
          } else {
            newDrafts[note.studentId] = note.content;
          }
        }
      }
    });
    
    setIndividualNoticeDrafts(prev => {
      if (replaceMode) {
        // replaceMode면 기존 메모를 완전히 대체
        return newDrafts;
      } else {
        // 기존 메모에 추가
        const updated = { ...prev };
        Object.entries(newDrafts).forEach(([studentId, memo]) => {
          if (updated[studentId] && updated[studentId].trim()) {
            updated[studentId] = `${updated[studentId]} / ${memo}`;
          } else {
            updated[studentId] = memo;
          }
        });
        return updated;
      }
    });
    
    // 수정 모드 정보 저장
    setEditingIndividualNoticeNotes(editingNotes);
    
    // 날짜 설정 (dateNotice.date가 있으면 사용, 없으면 첫 번째 노트의 날짜 사용)
    if (dateNotice.date) {
      setSelectedDate(dateNotice.date);
    } else if (dateNotice.notes.length > 0 && dateNotice.notes[0].date) {
      setSelectedDate(dateNotice.notes[0].date);
    }
    
    // 모달이 열려있을 때만 닫기
    if (showIndividualStudentPickModal) {
      setShowIndividualStudentPickModal(false);
    }
  }, [getCreatedAtMillis, showIndividualStudentPickModal]);

  // 개별공지: 학생 선택 모달 열기
  const openIndividualStudentPickModal = useCallback(async () => {
    setShowIndividualStudentPickModal(true);
    setIndividualNoticeViewMode('date'); // 기본값: 날짜별 조회
    await loadDateBasedIndividualNotices(); // 날짜별 목록 로드
    
    // 각 학생의 노트 수 로드
    if (user && currentClass && classMembers.length > 0) {
      try {
        const counts: Record<string, number> = {};
        await Promise.all(
          classMembers.map(async (member) => {
            try {
              const notes = await boardService.getIndividualNoticeNotesForStudent(
                user.id,
                currentClass.id,
                member.id,
                normalizedCurrentMemberGroup
              );
              counts[member.id] = notes.length;
            } catch (error) {
              console.error(`❌ ${member.name} 노트 수 로드 실패:`, error);
              counts[member.id] = 0;
            }
          })
        );
        setStudentNoticeCounts(counts);
      } catch (error) {
        console.error('❌ 학생 노트 수 로드 실패:', error);
      }
    }
  }, [user, currentClass, classMembers, loadDateBasedIndividualNotices, normalizedCurrentMemberGroup]);

  // 노트북 노트 변경사항 확인 (fontSize는 제외)
  const hasNoteChanges = useMemo(() => {
    if (!editingNoteId) return true; // 새 노트는 항상 변경사항 있음
    const currentNote = allNotes.find(n => n.id === editingNoteId);
    const currentRelatedStudents = currentNote?.relatedStudents || [];
    return (
      noteContent !== originalNoteContent ||
      selectedDate !== originalNoteDate ||
      JSON.stringify([...relatedStudents].sort()) !== JSON.stringify([...currentRelatedStudents].sort())
    );
  }, [editingNoteId, noteContent, originalNoteContent, selectedDate, originalNoteDate, relatedStudents, allNotes]);

  const loadIndividualNoticesForStudent = useCallback(async (student: ClassMember) => {
    if (!user || !currentClass) return;
    try {
      setSelectedIndividualStudent(student);
      const notes = await boardService.getIndividualNoticeNotesForStudent(
        user.id,
        currentClass.id,
        student.id,
        normalizedCurrentMemberGroup
      );
      const convertedNotes = sortNoticesByDateDesc(
        notes.map((note) => ({
          id: `individual-notice-${student.id}-${getCreatedAtMillis(note.createdAt) ?? Date.now()}`,
          content: note.content,
          categoryId: '개별공지노트',
          relatedStudentIds: [],
          date: note.date || '',
          timeTagName: undefined,
          // fontSize는 UI에서 localStorage로 관리하므로 매핑하지 않음
          createdAt: note.createdAt,
          updatedAt: note.updatedAt,
        }))
      );
      setIndividualNoticeList(convertedNotes);
      setIndividualNoticePage(1);
      setExpandedIndividualNoticeId(null); // 모달 열 때 확장 상태 초기화
      setDeletedIndividualNoticeIds(new Set()); // 모달 열 때 삭제 표시 초기화
      setEditingIndividualNoticeItemId(null); // 모달 열 때 수정 모드 초기화
      setEditingIndividualNoticeContent(''); // 모달 열 때 수정 내용 초기화
      setShowIndividualNoticeListModal(true);
    } catch (error) {
      console.error('❌ 개별공지 노트 목록 로드 실패:', error);
      alert('개별공지 노트 목록을 불러오는데 실패했습니다.');
    }
  }, [user, currentClass, sortNoticesByDateDesc, getCreatedAtMillis, normalizedCurrentMemberGroup]);

  // 전체공지노트 변경사항 확인

  const hasGeneralNoticeChanges = useMemo(() => {
    if (noticeTab !== 'GeneralNotice' || !editingNoticeId || editingNoticeId === 'new') return false;
    return (
      noticeAllContent !== originalNoticeContent ||
      selectedDate !== originalNoticeDate
    );
  }, [noticeTab, noticeAllContent, selectedDate, originalNoticeContent, originalNoticeDate, editingNoticeId]);

  const handleSaveNotice = useCallback(async () => {
    if (!user || !currentClass) return;
    
    // 프리 플랜 체크
    const noticeCheck = checkBoardNoticeAccess(user);
    if (!noticeCheck.allowed) {
      setLicenseLimitMessage(noticeCheck.message || '');
      setShowLicenseLimitModal(true);
      return;
    }
    
    setIsNoticeSaving(true);
    try {
      // 보드페이지의 날짜블럭 설정값 사용
      if (noticeTab === 'GeneralNotice') {
        // editingNoticeId가 있으면 기존 노트를 업데이트, 없으면 새로 생성
        let createdAtTimestamp: number | null = null;
        if (editingNoticeId && editingNoticeId !== 'new') {
          // editingNoticeId에서 timestamp 추출 (형식: "general-notice-{timestamp}")
          const match = editingNoticeId.match(/general-notice-(\d+)/);
          if (match) {
            createdAtTimestamp = parseInt(match[1], 10);
          }
        }
        
        const createdAtMillisToUse = createdAtTimestamp ?? Date.now();
        const targetGroups =
          generalNoticeScope === 'all' ? availableMemberGroups : [normalizedCurrentMemberGroup];

        for (const g of targetGroups) {
          await boardService.saveNoticeNote(
            user.id,
            currentClass.id,
            noticeTab,
            noticeAllContent,
            selectedDate,
            createdAtMillisToUse,
            g,
            generalNoticeScope
          );
        }
        
        // 저장된 노트를 찾아서 로드 (내용과 날짜로 매칭)
        // 가장 최근 노트 중에서 내용과 날짜가 일치하는 것을 찾음
        const notes = await boardService.getGeneralNoticeNotes(user.id, currentClass.id, normalizedCurrentMemberGroup);
        const convertedNotes = notes.map((note) => ({
          id: `general-notice-${getCreatedAtMillis(note.createdAt) ?? Date.now()}`,
          content: note.content,
          categoryId: '전체공지노트',
          noticeScope: (note as any).scope || 'all',
          relatedStudentIds: [],
          date: note.date || '',
          timeTagName: undefined,
          // fontSize는 UI에서 localStorage로 관리하므로 매핑하지 않음
          createdAt: note.createdAt,
          updatedAt: note.updatedAt,
        }));
        
        // 최신 노트가 앞으로 오도록 정렬
        convertedNotes.sort((a, b) => {
          const am = getCreatedAtMillis(a.createdAt) ?? 0;
          const bm = getCreatedAtMillis(b.createdAt) ?? 0;
          return bm - am;
        });
        
        setAllNoticeNotes(convertedNotes);
        
        // 저장된 노트 찾기: createdAt(=createdAtMillisToUse) + scope로 매칭
        let savedNote = convertedNotes.find((note) => {
          const ts = getCreatedAtMillis(note.createdAt) ?? null;
          return ts === createdAtMillisToUse && (note as any).noticeScope === generalNoticeScope;
        });
        
        // 매칭이 안되면 가장 최근 노트를 사용
        if (!savedNote && convertedNotes.length > 0) {
          savedNote = convertedNotes[0];
        }
        
        if (savedNote) {
          // 저장된 노트를 로드하여 수정 모드로 전환
          // loadNoticeToBoard를 직접 호출하여 editingNoticeId를 설정
          setNoticeAllContent(savedNote.content || '');
          // fontSize는 localStorage에서 로드한 값 사용 (노트 데이터에서 로드하지 않음)
          setEditingNoticeId(savedNote.id);
          // editingNoticeIdRef도 업데이트하여 loadNoticeNotes가 자동 로드하지 않도록 함
          editingNoticeIdRef.current = savedNote.id;
          setOriginalNoticeContent(savedNote.content || '');
          setOriginalNoticeDate(savedNote.date || '');
          if (savedNote.date) {
            setSelectedDate(savedNote.date);
          }
        } else {
          // 찾지 못하면 초기화 (안전장치)
          setNoticeAllContent('');
          // fontSize는 localStorage에서 로드한 값 유지
          setEditingNoticeId(null);
          setOriginalNoticeContent('');
          setOriginalNoticeDate('');
        }
        
        showToastMessage(
          generalNoticeScope === 'all'
            ? '전체공지가 모든 그룹에 저장되었습니다.'
            : `그룹공지가 ${normalizedCurrentMemberGroup}그룹에 저장되었습니다.`
        );
        return;
      }

      // IndividualNotice: 학생별로 각각 저장
      const entries = Object.entries(individualNoticeDrafts || {}).filter(([, v]) => (v || '').trim() !== '');
      if (entries.length === 0) {
        showToastMessage('개별 공지 내용을 입력한 학생이 없습니다.');
        return;
      }

      // 수정 모드인지 확인 (날짜별 조회로 로드한 경우)
      const isEditMode = editingIndividualNoticeNotes.length > 0;

      if (isEditMode) {
        // 수정 모드: 기존 노트 수정
        // 수정 전 그룹 키 저장 (나중에 찾기 위해)
        const currentGroupKey = dateBasedIndividualNotices[currentDateBasedIndex]?.groupKey;
        
        // 모든 학생의 수정/추가 정보를 배열로 변환
        const updates = Array.from(entries).map(([studentId, content]) => {
          const editingNote = editingIndividualNoticeNotes.find(n => n.studentId === studentId);
          const member = classMembers.find((m: any) => m.id === studentId);
          
          if (editingNote) {
            // 기존 노트 수정
            return {
              studentId,
              studentName: member?.name || undefined,
              timestamp: editingNote.timestamp, // 수정 모드
              content,
              fontSize: noticeIndFontSize,
              date: selectedDate,
            };
          } else {
            // 새 노트 추가 (기존에 없던 학생)
            return {
              studentId,
              studentName: member?.name || undefined,
              // timestamp 없음 = 새로 추가
              content,
              fontSize: noticeIndFontSize,
              date: selectedDate,
            };
          }
        });

        // 여러 학생의 노트를 한 번에 수정/추가 (문서를 한 번만 읽고 한 번만 업데이트)
        await boardService.updateMultipleIndividualNoticeNotes(
          user.id,
          currentClass.id,
          updates,
          normalizedCurrentMemberGroup
        );
        
        showToastMessage('개별 공지 노트가 수정되었습니다.');
        
        // 수정 후 날짜별 목록을 다시 로드하고 수정된 그룹을 다시 로드
        const updatedNotices = await loadDateBasedIndividualNotices();
        if (updatedNotices && updatedNotices.length > 0 && currentGroupKey) {
          // 수정된 그룹 찾기 (groupKey로 매칭)
          const updatedGroup = updatedNotices.find(group => group.groupKey === currentGroupKey);
          if (updatedGroup) {
            // 수정된 그룹을 다시 로드하여 수정 모드 유지
            loadDateBasedNoticeToBoard(updatedGroup, true);
            const groupIndex = updatedNotices.findIndex(g => g.groupKey === updatedGroup.groupKey);
            if (groupIndex !== -1) {
              setCurrentDateBasedIndex(groupIndex);
            }
          } else {
            // 그룹을 찾지 못하면 현재 인덱스의 그룹을 다시 로드
            if (currentDateBasedIndex < updatedNotices.length) {
              loadDateBasedNoticeToBoard(updatedNotices[currentDateBasedIndex], true);
            }
          }
        }
      } else {
        // 새로 저장
        // 한 번의 저장 작업으로 묶기 위해 저장 시작 시점의 타임스탬프를 생성
        // 모든 항목이 같은 타임스탬프를 사용하도록 함
        const saveBatchTimestamp = Date.now();

        // 모든 학생의 노트 정보를 배열로 변환
        const notesToSave = Array.from(entries).map(([studentId, content]) => {
          const member = classMembers.find((m: any) => m.id === studentId);
          return {
            studentId,
            studentName: member?.name || undefined,
            content,
            fontSize: noticeIndFontSize,
            date: selectedDate,
          };
        });

        // 여러 학생의 노트를 한 번에 저장 (문서를 한 번만 읽고 한 번만 업데이트)
        await boardService.saveMultipleIndividualNoticeNotes(
          user.id,
          currentClass.id,
          notesToSave,
          saveBatchTimestamp, // 같은 저장 배치임을 나타내는 타임스탬프 전달
          normalizedCurrentMemberGroup
        );
        
        showToastMessage('개별 공지 노트가 저장되었습니다.');
        
        // 저장 후 방금 저장한 데이터를 로드하여 수정 모드로 전환
        // 날짜별 목록을 다시 로드
        const updatedNotices = await loadDateBasedIndividualNotices();
        if (updatedNotices && updatedNotices.length > 0) {
          // saveBatchTimestamp와 일치하는 그룹 찾기
          // Firestore에 저장될 때 약간의 지연이 있을 수 있으므로 1초 이내 차이 허용
          const savedGroup = updatedNotices.find(group => 
            Math.abs(group.createdAtMillis - saveBatchTimestamp) < 2000 // 2초 이내 차이 허용
          );
          
          if (savedGroup) {
            // 방금 저장한 그룹을 로드하여 수정 모드로 전환
            loadDateBasedNoticeToBoard(savedGroup, true);
            // 인덱스도 업데이트
            const groupIndex = updatedNotices.findIndex(g => g.groupKey === savedGroup.groupKey);
            if (groupIndex !== -1) {
              setCurrentDateBasedIndex(groupIndex);
            }
          } else {
            // 매칭이 안되면 가장 최근 그룹을 사용 (방금 저장한 것이므로)
            loadDateBasedNoticeToBoard(updatedNotices[0], true);
            setCurrentDateBasedIndex(0);
          }
        }
      }
    } catch (error) {
      console.error('❌ 공지 노트 저장 실패:', error);
      showToastMessage('공지 노트 저장에 실패했습니다.');
    } finally {
      setIsNoticeSaving(false);
    }
  }, [user, currentClass, noticeTab, noticeAllContent, noticeIndFontSize, selectedDate, individualNoticeDrafts, classMembers, showToastMessage, getCreatedAtMillis, editingIndividualNoticeNotes, loadDateBasedIndividualNotices, loadDateBasedNoticeToBoard, dateBasedIndividualNotices, currentDateBasedIndex, editingNoticeId, generalNoticeScope, availableMemberGroups, normalizedCurrentMemberGroup]);

  // 개별공지: 학생 선택/해제
  const handleIndividualStudentSelection = useCallback((studentId: string) => {
    setSelectedIndividualStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  }, []);

  // 전체 선택
  const handleSelectAllIndividualStudents = useCallback(() => {
    const allStudentIds = new Set(classMembers.map(m => m.id));
    setSelectedIndividualStudents(allStudentIds);
  }, [classMembers]);

  // 선택 취소
  const handleDeselectAllIndividualStudents = useCallback(() => {
    setSelectedIndividualStudents(new Set());
  }, []);

  // 일괄 노트 작성 모달 열기
  const handleOpenBulkIndividualNoticeModal = useCallback(() => {
    if (selectedIndividualStudents.size === 0) {
      alert('학생을 선택해주세요.');
      return;
    }
    setBulkIndividualNoticeContent('');
    setShowBulkIndividualNoticeModal(true);
  }, [selectedIndividualStudents]);

  // 일괄 노트 작성 모달 닫기
  const handleCloseBulkIndividualNoticeModal = useCallback(() => {
    setShowBulkIndividualNoticeModal(false);
    setBulkIndividualNoticeContent('');
  }, []);

  // 일괄 노트 테이블 반영
  const handleApplyBulkIndividualNotice = useCallback(() => {
    if (bulkIndividualNoticeContent.trim() === '') {
      alert('내용을 입력해주세요.');
      return;
    }

    if (selectedIndividualStudents.size === 0) {
      alert('학생을 선택해주세요.');
      return;
    }

    // 선택된 학생들의 입력필드에 텍스트 반영
    const updatedDrafts = { ...individualNoticeDrafts };
    selectedIndividualStudents.forEach(studentId => {
      updatedDrafts[studentId] = bulkIndividualNoticeContent;
    });
    setIndividualNoticeDrafts(updatedDrafts);
    
    alert('테이블에 반영되었습니다.');
    handleCloseBulkIndividualNoticeModal();
  }, [bulkIndividualNoticeContent, selectedIndividualStudents, individualNoticeDrafts, handleCloseBulkIndividualNoticeModal]);

  // 새 공지 노트 작성 시작 (노트북과 동일한 방식)
  const handleNewNotice = () => {
    // 오늘 날짜로 초기화
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayString = `${year}-${month}-${day}`;
    setSelectedDate(todayString);
    
    // 입력 필드 초기화
    if (noticeTab === 'GeneralNotice') {
      setNoticeAllContent('');
      // fontSize는 localStorage에서 로드한 값 유지
      // 새 노트 모드이므로 원본 데이터 초기화
      setOriginalNoticeContent('');
      setOriginalNoticeDate('');
      // 편집 중인 노트 ID를 null로 설정하여 <> 탐색 가능하도록 함
      setEditingNoticeId(null);
    } else {
      // 개별공지: 새 노트 모드로 초기화
      setIndividualNoticeDrafts({});
      setNoticeIndFontSize(10);
      // 수정 모드 초기화 (날짜별 조회로 로드한 경우 수정 모드가 활성화되어 있을 수 있음)
      setEditingIndividualNoticeNotes([]);
    }
  };

  // 새 노트 작성 시작
  const handleNewNote = () => {
    // 오늘 날짜로 초기화
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayString = `${year}-${month}-${day}`;
    setSelectedDate(todayString);
    
    // 현재 시간에 맞는 타임 태그 자동 선택
    selectCurrentTimeTag();
    
    // 메모 입력 필드 초기화
    setNoteContent('');
    
    // 관련멤버 선택 초기화
    setRelatedStudents([]);
    
    // fontSize는 localStorage에서 관리하므로 초기화하지 않음
    
    // 편집 중인 노트 ID 초기화 (새 노트 모드)
    setEditingNoteId(null);
    
    // 원본 데이터 초기화
    setOriginalNoteContent('');
    setOriginalNoteDate('');
  };

  // 공지 노트 목록 로드 (탐색용)
  const loadAllNoticeNotes = useCallback(async () => {
    if (!user || !currentClass || noticeTab !== 'GeneralNotice') return;
    
    try {
      const notes = await boardService.getGeneralNoticeNotes(user.id, currentClass.id, normalizedCurrentMemberGroup);
      const convertedNotes = notes.map((note) => ({
        id: `general-notice-${getCreatedAtMillis(note.createdAt) ?? Date.now()}`,
        content: note.content,
        categoryId: '전체공지노트',
        noticeScope: (note as any).scope || 'all',
        relatedStudentIds: [],
        date: note.date || '',
        timeTagName: undefined,
        fontSize: note.fontSize,
        createdAt: note.createdAt,
        updatedAt: note.updatedAt,
      }));
      setAllNoticeNotes(convertedNotes);
      // 전체공지노트가 없을 때 editingNoticeId 초기화
      if (convertedNotes.length === 0) {
        setEditingNoticeId(null);
      }
    } catch (error) {
      console.error('❌ 공지 노트 목록 로드 실패:', error);
    }
  }, [user, currentClass, noticeTab, getCreatedAtMillis, normalizedCurrentMemberGroup]);

  // 이전 공지 노트로 이동 (< 버튼)
  const handlePreviousNotice = useCallback(async () => {
    if (!user || !currentClass || noticeTab !== 'GeneralNotice') return;
    
    // allNoticeNotes가 비어있으면 먼저 로드
    if (allNoticeNotes.length === 0) {
      try {
      const notes = await boardService.getGeneralNoticeNotes(user.id, currentClass.id, normalizedCurrentMemberGroup);
        const convertedNotes = notes.map((note) => ({
          id: `general-notice-${getCreatedAtMillis(note.createdAt) ?? Date.now()}`,
          content: note.content,
          categoryId: '전체공지노트',
        noticeScope: (note as any).scope || 'all',
          relatedStudentIds: [],
          date: note.date || '',
          timeTagName: undefined,
          // fontSize는 UI에서 localStorage로 관리하므로 매핑하지 않음
          createdAt: note.createdAt,
          updatedAt: note.updatedAt,
        }));
        convertedNotes.sort((a, b) => {
          const am = getCreatedAtMillis(a.createdAt) ?? 0;
          const bm = getCreatedAtMillis(b.createdAt) ?? 0;
          return bm - am;
        });
        setAllNoticeNotes(convertedNotes);
        
        if (convertedNotes.length === 0) return;
        
        // editingNoticeId가 null이면 첫 번째 노트를 로드
        if (!editingNoticeId) {
          loadNoticeToBoard(convertedNotes[0]);
          return;
        }
        
        const currentIndex = convertedNotes.findIndex(note => note.id === editingNoticeId);
        if (currentIndex === -1) {
          // 현재 노트를 찾을 수 없으면 첫 번째 노트를 로드
          loadNoticeToBoard(convertedNotes[0]);
          return;
        }
        const previousIndex = currentIndex - 1;
        if (previousIndex >= 0) {
          const previousNote = convertedNotes[previousIndex];
          loadNoticeToBoard(previousNote);
        }
      } catch (error) {
        console.error('❌ 공지 노트 로드 실패:', error);
        showToastMessage('공지 노트를 불러오는데 실패했습니다.');
      }
      return;
    }
    
    if (allNoticeNotes.length === 0) return;

    const scopedNotes = allNoticeNotes.filter((n: any) => (n.noticeScope || 'all') === generalNoticeScope);
    if (scopedNotes.length === 0) return;
    
    // editingNoticeId가 null이면 첫 번째 노트를 로드
    if (!editingNoticeId) {
      loadNoticeToBoard(scopedNotes[0]);
      return;
    }
    
    const currentIndex = scopedNotes.findIndex((note: any) => note.id === editingNoticeId);
    if (currentIndex === -1) {
      // 현재 노트를 찾을 수 없으면 첫 번째 노트를 로드
      loadNoticeToBoard(scopedNotes[0]);
      return;
    }
    
    const previousIndex = currentIndex - 1;
    if (previousIndex >= 0) {
      const previousNote = scopedNotes[previousIndex];
      loadNoticeToBoard(previousNote);
    }
  }, [allNoticeNotes, editingNoticeId, noticeTab, loadNoticeToBoard, user, currentClass, getCreatedAtMillis, showToastMessage, generalNoticeScope, normalizedCurrentMemberGroup]);

  // 다음 공지 노트로 이동 (> 버튼)
  const handleNextNotice = useCallback(async () => {
    if (!user || !currentClass || noticeTab !== 'GeneralNotice') return;
    
    // allNoticeNotes가 비어있으면 먼저 로드
    if (allNoticeNotes.length === 0) {
      try {
        const notes = await boardService.getGeneralNoticeNotes(user.id, currentClass.id, normalizedCurrentMemberGroup);
        const convertedNotes = notes.map((note) => ({
          id: `general-notice-${getCreatedAtMillis(note.createdAt) ?? Date.now()}`,
          content: note.content,
          categoryId: '전체공지노트',
          noticeScope: (note as any).scope || 'all',
          relatedStudentIds: [],
          date: note.date || '',
          timeTagName: undefined,
          // fontSize는 UI에서 localStorage로 관리하므로 매핑하지 않음
          createdAt: note.createdAt,
          updatedAt: note.updatedAt,
        }));
        convertedNotes.sort((a, b) => {
          const am = getCreatedAtMillis(a.createdAt) ?? 0;
          const bm = getCreatedAtMillis(b.createdAt) ?? 0;
          return bm - am;
        });
        setAllNoticeNotes(convertedNotes);
        
        if (convertedNotes.length === 0) return;
        
        // editingNoticeId가 null이면 첫 번째 노트를 로드
        if (!editingNoticeId) {
          loadNoticeToBoard(convertedNotes[0]);
          return;
        }
        
        const currentIndex = convertedNotes.findIndex(note => note.id === editingNoticeId);
        if (currentIndex === -1) {
          // 현재 노트를 찾을 수 없으면 첫 번째 노트를 로드
          loadNoticeToBoard(convertedNotes[0]);
          return;
        }
        const nextIndex = currentIndex + 1;
        if (nextIndex < convertedNotes.length) {
          const nextNote = convertedNotes[nextIndex];
          loadNoticeToBoard(nextNote);
        }
      } catch (error) {
        console.error('❌ 공지 노트 로드 실패:', error);
        showToastMessage('공지 노트를 불러오는데 실패했습니다.');
      }
      return;
    }
    
    if (allNoticeNotes.length === 0) return;

    const scopedNotes = allNoticeNotes.filter((n: any) => (n.noticeScope || 'all') === generalNoticeScope);
    if (scopedNotes.length === 0) return;
    
    // editingNoticeId가 null이면 첫 번째 노트를 로드
    if (!editingNoticeId) {
      loadNoticeToBoard(scopedNotes[0]);
      return;
    }
    
    const currentIndex = scopedNotes.findIndex((note: any) => note.id === editingNoticeId);
    if (currentIndex === -1) {
      // 현재 노트를 찾을 수 없으면 첫 번째 노트를 로드
      loadNoticeToBoard(scopedNotes[0]);
      return;
    }
    
    const nextIndex = currentIndex + 1;
    if (nextIndex < scopedNotes.length) {
      const nextNote = scopedNotes[nextIndex];
      loadNoticeToBoard(nextNote);
    }
  }, [allNoticeNotes, editingNoticeId, noticeTab, loadNoticeToBoard, user, currentClass, getCreatedAtMillis, showToastMessage, generalNoticeScope, normalizedCurrentMemberGroup]);

  // 개별공지: 이전 날짜별 공지로 이동 (< 버튼) - 더 최신 데이터
  const handlePreviousDateBasedNotice = useCallback(async () => {
    if (!user || !currentClass || noticeTab !== 'IndividualNotice') return;
    
    // dateBasedIndividualNotices가 비어있으면 먼저 로드
    let notices = dateBasedIndividualNotices;
    if (notices.length === 0) {
      const loadedNotices = await loadDateBasedIndividualNotices();
      if (!loadedNotices || loadedNotices.length === 0) {
        return;
      }
      notices = loadedNotices;
    }
    
    if (notices.length === 0) return;
    
    // 전체공지와 동일하게: 새 노트 상태에서는 첫 번째 노트를 로드
    if (editingIndividualNoticeNotes.length === 0) {
      loadDateBasedNoticeToBoard(notices[0], true); // replaceMode: true로 덮어쓰기
      setCurrentDateBasedIndex(0);
      return;
    }
    
    // 전체공지와 동일하게: 인덱스 감소 = 더 최신 데이터
    // 인덱스 0이 가장 최신이므로, 인덱스를 감소시키면 더 최신으로 이동
    // 첫 번째(인덱스 0)에서는 이동하지 않음 (전체공지와 동일)
    const previousIndex = currentDateBasedIndex - 1;
    if (previousIndex >= 0) {
      setCurrentDateBasedIndex(previousIndex);
      const previousNotice = notices[previousIndex];
      if (previousNotice) {
        loadDateBasedNoticeToBoard(previousNotice, true); // replaceMode: true로 덮어쓰기
      }
    }
  }, [user, currentClass, noticeTab, dateBasedIndividualNotices, currentDateBasedIndex, editingIndividualNoticeNotes, loadDateBasedIndividualNotices, loadDateBasedNoticeToBoard]);

  // 개별공지: 다음 날짜별 공지로 이동 (> 버튼) - 더 과거 데이터
  const handleNextDateBasedNotice = useCallback(async () => {
    if (!user || !currentClass || noticeTab !== 'IndividualNotice') return;
    
    // dateBasedIndividualNotices가 비어있으면 먼저 로드
    let notices = dateBasedIndividualNotices;
    if (notices.length === 0) {
      const loadedNotices = await loadDateBasedIndividualNotices();
      if (!loadedNotices || loadedNotices.length === 0) {
        return;
      }
      notices = loadedNotices;
    }
    
    if (notices.length === 0) return;
    
    // 전체공지와 동일하게: 새 노트 상태에서는 첫 번째 노트를 로드
    if (editingIndividualNoticeNotes.length === 0) {
      loadDateBasedNoticeToBoard(notices[0], true); // replaceMode: true로 덮어쓰기
      setCurrentDateBasedIndex(0);
      return;
    }
    
    // 전체공지와 동일하게: 인덱스 증가 = 더 과거 데이터
    // 인덱스 0이 가장 최신이므로, 인덱스를 증가시키면 더 과거로 이동
    // 마지막에서는 이동하지 않음 (전체공지와 동일)
    const nextIndex = currentDateBasedIndex + 1;
    if (nextIndex < notices.length) {
      setCurrentDateBasedIndex(nextIndex);
      const nextNotice = notices[nextIndex];
      if (nextNotice) {
        loadDateBasedNoticeToBoard(nextNotice, true); // replaceMode: true로 덮어쓰기
      }
    }
  }, [user, currentClass, noticeTab, dateBasedIndividualNotices, currentDateBasedIndex, editingIndividualNoticeNotes, loadDateBasedIndividualNotices, loadDateBasedNoticeToBoard]);

  // 공지 노트 목록 로드 (탭 변경 시)
  useEffect(() => {
    if (showNoticeForm && noticeTab === 'GeneralNotice') {
      loadAllNoticeNotes();
    }
  }, [showNoticeForm, noticeTab, loadAllNoticeNotes]);

  // 노트 선택 시 보드에 데이터 로드
  const handleSelectNote = async (note: any) => {
    if (!user || !currentClass) return;
    
    try {
      // 전체/그룹공지 노트인 경우 노트북 노트와 동일하게 처리 (모달 닫고 데이터 로드)
      if (noteListCategory === '전체공지노트' || noteListCategory === '그룹공지노트') {
        // 공지 탭이 'GeneralNotice'가 아니면 먼저 설정
        if (noticeTab !== 'GeneralNotice') {
          setNoticeTab('GeneralNotice');
        }

        // 선택한 노트의 스코프(전체/그룹)도 함께 반영
        if ((note.noticeScope || 'all') === 'group') {
          setGeneralNoticeScope('group');
        } else {
          setGeneralNoticeScope('all');
        }
        
        // 전체공지노트를 보드에 로드 (noticeTab이 변경된 후 호출)
        setNoticeAllContent(note.content || '');
        // fontSize는 localStorage에서 로드한 값 사용 (노트 데이터에서 로드하지 않음)
        setEditingNoticeId(note.id);
        editingNoticeIdRef.current = note.id; // ref도 업데이트
        // 원본 데이터 저장 (변경사항 비교용)
        setOriginalNoticeContent(note.content || '');
        setOriginalNoticeDate(note.date || '');
        // 날짜도 함께 설정
        if (note.date) {
          setSelectedDate(note.date);
        }
        
        // 노트 목록 모달 닫기
        setShowNoteListModal(false);
        
        // 공지 폼이 열려있지 않으면 열기
        if (!showNoticeForm) {
          setShowNoticeForm(true);
        }
        
        return;
      }
      
      // 노트북 노트인 경우 기존 로직
      // 날짜 설정
      setSelectedDate(note.date || selectedDate);
      
      // 타임 태그 설정
      if (note.timeTagName && availableTimeTags.length > 0) {
        const parsed = parseTimeTagLabel(note.timeTagName);
        const matchedTimeTag = availableTimeTags.find(tag => tag.name === parsed.name);
        if (matchedTimeTag) {
          setSelectedTimeTag(matchedTimeTag);
          const days = Array.isArray(matchedTimeTag.dayOfWeek) ? matchedTimeTag.dayOfWeek : [matchedTimeTag.dayOfWeek];
          const dayToSet = days.includes(parsed.day) ? parsed.day : (days[0] || '일');
          setSelectedDayOfWeek(dayToSet);
        } else {
          setSelectedTimeTag(null);
          setSelectedDayOfWeek(['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]);
        }
      } else {
        setSelectedTimeTag(null);
        setSelectedDayOfWeek(['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]);
      }
      
      // 카테고리 설정
      if (note.category) {
        setSelectedCategory(note.category);
      }
      
      // 메모 내용 설정
      setNoteContent(note.content || '');
      
      // 관련멤버 설정
      setRelatedStudents(note.relatedStudents || []);
      
      // fontSize는 localStorage에서 관리하므로 초기화하지 않음
      
      // 편집 중인 노트 ID 설정
      setEditingNoteId(note.id);
      
      // 노트 목록 모달 닫기
      setShowNoteListModal(false);
      
      // 노트북 폼이 열려있지 않으면 열기
      if (!showNotebookForm) {
        setShowNotebookForm(true);
      }
    } catch (error) {
      console.error('❌ 노트 로드 실패:', error);
      alert('노트를 불러오는데 실패했습니다.');
    }
  };

  // 노트를 보드에 로드하는 헬퍼 함수
  const loadNoteToBoard = useCallback((note: any) => {
    if (!note) return;
    
    // 노트 데이터를 보드에 로드
    setSelectedDate(note.date || selectedDate);
    
    // 타임 태그 설정
    if (note.timeTagName && availableTimeTags.length > 0) {
      const parsed = parseTimeTagLabel(note.timeTagName);
      const matchedTimeTag = availableTimeTags.find(tag => tag.name === parsed.name);
      if (matchedTimeTag) {
        setSelectedTimeTag(matchedTimeTag);
        const days = Array.isArray(matchedTimeTag.dayOfWeek) ? matchedTimeTag.dayOfWeek : [matchedTimeTag.dayOfWeek];
        const dayToSet = days.includes(parsed.day) ? parsed.day : (days[0] || '일');
        setSelectedDayOfWeek(dayToSet);
      } else {
        setSelectedTimeTag(null);
        setSelectedDayOfWeek(['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]);
      }
    } else {
      setSelectedTimeTag(null);
      setSelectedDayOfWeek(['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]);
    }
    
    // 카테고리 설정
    if (note.category) {
      setSelectedCategory(note.category);
    }
    
    // 메모 내용 설정
    setNoteContent(note.content || '');
    // fontSize는 localStorage에서 로드한 값 사용 (노트 데이터에서 로드하지 않음)
    setEditingNoteId(note.id);
    // 원본 데이터 저장 (변경사항 비교용)
    setOriginalNoteContent(note.content || '');
    setOriginalNoteDate(note.date || '');
    
    // 관련멤버 설정
    setRelatedStudents(note.relatedStudents || []);
    
    // 편집 중인 노트 ID 설정
    setEditingNoteId(note.id);
    
    // 노트북 폼이 열려있지 않으면 열기
    if (!showNotebookForm) {
      setShowNotebookForm(true);
    }
  }, [availableTimeTags, selectedDate, showNotebookForm]);

  // 카테고리 클릭 시 노트 로드 및 표시
  // - 오늘 날짜의 노트가 있으면 오늘 날짜의 가장 최근 노트를 보드에 표시
  // - 오늘 날짜 노트가 없으면 오늘 날짜의 새 노트를 보드에 표시하고, 목록에는 기존 노트들을 그대로 유지
  const loadCategoryNotes = useCallback(async (categoryName: string) => {
    console.log(`🖱️ 카테고리 클릭: ${categoryName}`);
    console.log(`📊 categories 배열:`, categories);
    if (!user || !currentClass) {
      console.log('❌ 사용자 또는 클래스 정보 없음');
      return;
    }

    try {
      // 카테고리 이름에서 ID로 변환
      const category = categories.find(cat => cat.name === categoryName);
      console.log(`🔍 찾은 카테고리:`, category);
      const categoryId = category?.id || categoryName;
      console.log(`🔄 카테고리 변환: ${categoryName} -> ${categoryId}`);

      // 해당 카테고리의 최신 노트들을 로드 (탐색용)
      const notes = await boardService.loadRecentNotesForCategory(user.id, currentClass.id, categoryId);
      console.log(`📋 로드된 노트 수: ${notes.length}`, notes);
      setAllNotes(notes);
      setNoteListCategory(categoryName); // 표시용으로는 이름 사용
      setSelectedCategory(categoryName); // UI 표시용도 업데이트
      setCurrentPage(1);
      setDeletedNoteIds(new Set()); // 노트 목록 로드 시 삭제 표시 초기화
      
      // 오늘 날짜 문자열 (YYYY-MM-DD)
      const todayStr = new Date().toISOString().slice(0, 10);

      // 노트 중에서 오늘 날짜에 작성된 것들만 필터링
      const todayNotes = notes.filter(note => note.date === todayStr);

      if (todayNotes.length > 0) {
        // 오늘 날짜 노트가 있으면, 그 중 가장 최근 노트를 보드에 로드
        const latestTodayNote = todayNotes[0]; // loadRecentNotesForCategory가 이미 최신 순으로 정렬되어 있다고 가정
        console.log(`🎯 오늘(${todayStr}) 노트 로드 시작: ${latestTodayNote.id}`);
        loadNoteToBoard(latestTodayNote);
        setEditingNoteId(latestTodayNote.id);
        setCurrentNoteIndex(notes.findIndex(n => n.id === latestTodayNote.id)); // 목록 인덱스 동기화
        console.log(`✅ ${categoryName} 카테고리 오늘 노트 로드 및 표시: ${latestTodayNote.content.substring(0, 30)}...`);
      } else {
        // 오늘 날짜 노트가 없으면, 오늘 날짜의 새 노트를 보드에 표시 (빈 내용)
        console.log(`📅 ${categoryName} 카테고리에 오늘(${todayStr}) 노트가 없어 새 노트 생성 상태로 표시`);
        setNoteContent('');
        setEditingNoteId(null);
        setCurrentNoteIndex(0);
        setRelatedStudents([]);
      }
    } catch (error) {
      console.error('❌ 카테고리 노트 로드 실패:', error);
      alert('노트 목록을 불러오는데 실패했습니다.');
    }
  }, [user, currentClass, categories, loadNoteToBoard]);

  // 잠긴 노트북 카테고리 열기: 노트 전용 잠금 비밀번호 확인 후 노트 로드
  const handleNotebookLockSubmit = useCallback(async () => {
    if (!notebookLockPassword.trim()) {
      setNotebookLockError('잠금 비밀번호를 입력해 주세요.');
      return;
    }
    if (!notebookLockCategory) {
      setNotebookLockError('잠금 정보를 찾을 수 없습니다. 다시 시도해 주세요.');
      return;
    }
    setNotebookLockSubmitting(true);
    setNotebookLockError('');
    try {
      // 노트 비밀번호가 설정되어 있지 않은 잠금 카테고리(이전 방식)는 환경설정에서 재설정 필요
      if (!notebookLockCategory.lockPasswordHash) {
        setNotebookLockError('이 카테고리는 노트 비밀번호가 설정되어 있지 않습니다. 기본 환경설정에서 잠금 해제 후 노트 비밀번호를 설정해 주세요.');
        return;
      }

      const inputHash = await hashLockPassword(notebookLockPassword.trim());
      if (inputHash !== notebookLockCategory.lockPasswordHash) {
        setNotebookLockError('비밀번호가 올바르지 않습니다.');
        return;
      }

      setShowNotebookLockModal(false);
      setNotebookLockCategory(null);
      setNotebookLockPassword('');
      setNotebookLockError('');
      await loadCategoryNotes(notebookLockCategory.name);
    } catch (err: any) {
      console.error('노트북 잠금 비밀번호 확인 실패:', err);
      setNotebookLockError('잠금 비밀번호 확인 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setNotebookLockSubmitting(false);
    }
  }, [notebookLockCategory, notebookLockPassword, loadCategoryNotes]);

  // 노트 임시 삭제 표시/해제
  const handleToggleDeleteNote = useCallback((noteId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // NoteListItem의 onClick 이벤트 방지
    setDeletedNoteIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  }, []);

  // 개별공지 노트 임시 삭제 표시/해제
  const handleToggleDeleteIndividualNotice = useCallback((noteId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // NoteListItem의 onClick 이벤트 방지
    setDeletedIndividualNoticeIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  }, []);

  // 개별공지 삭제 확인 모달 열기
  const handleOpenDeleteIndividualNoticeConfirm = useCallback(() => {
    if (deletedIndividualNoticeIds.size === 0) return;
    setShowDeleteIndividualNoticeConfirmModal(true);
  }, [deletedIndividualNoticeIds.size]);

  // 개별공지 노트 수정 모드 활성화
  const handleStartEditIndividualNotice = useCallback((noteId: string, currentContent: string, e: React.MouseEvent) => {
    e.stopPropagation(); // NoteListItem의 onClick 이벤트 방지
    // 수정 모드 전환 전 원래 확장 상태 저장
    setOriginalExpandedStateBeforeEdit(expandedIndividualNoticeId);
    setEditingIndividualNoticeItemId(noteId);
    setEditingIndividualNoticeContent(currentContent);
    // 수정 모드 활성화 시 확장 상태도 활성화
    setExpandedIndividualNoticeId(noteId);
  }, [expandedIndividualNoticeId]);

  // 개별공지 노트 수정 취소
  const handleCancelEditIndividualNotice = useCallback(() => {
    setEditingIndividualNoticeItemId(null);
    setEditingIndividualNoticeContent('');
    // 수정 모드가 닫힐 때 원래 확장 상태로 복원
    setExpandedIndividualNoticeId(originalExpandedStateBeforeEdit);
    setOriginalExpandedStateBeforeEdit(null);
  }, [originalExpandedStateBeforeEdit]);

  // 개별공지 노트 수정 저장
  const handleSaveEditIndividualNotice = useCallback(async () => {
    if (!user || !currentClass || !selectedIndividualStudent || !editingIndividualNoticeItemId) return;
    
    // 원래 확장 상태 저장 (목록 새로고침 전에)
    const originalExpanded = originalExpandedStateBeforeEdit;
    
    try {
      // noteId에서 타임스탬프 추출 (individual-notice-{studentId}-{timestamp})
      const match = editingIndividualNoticeItemId.match(/individual-notice-[\w-]+-(\d+)/);
      if (!match) {
        alert('노트 정보를 찾을 수 없습니다.');
      return;
    }
      
      const timestamp = parseInt(match[1], 10);
      const note = individualNoticeList.find(n => n.id === editingIndividualNoticeItemId);
      if (!note) {
        alert('노트를 찾을 수 없습니다.');
        return;
      }
      
      await boardService.updateIndividualNoticeNoteForStudent(
        user.id,
        currentClass.id,
        selectedIndividualStudent.id,
        timestamp,
        editingIndividualNoticeContent,
        noticeIndFontSize,
        note.date || '',
        normalizedCurrentMemberGroup
      );
      
      // 목록 새로고침
      await loadIndividualNoticesForStudent(selectedIndividualStudent);
      
      // 수정 모드 종료 및 원래 확장 상태로 복원
      setEditingIndividualNoticeItemId(null);
      setEditingIndividualNoticeContent('');
      setExpandedIndividualNoticeId(originalExpanded);
      setOriginalExpandedStateBeforeEdit(null);
      
      showToastMessage('개별공지가 수정되었습니다.');
    } catch (error) {
      console.error('❌ 개별공지 노트 수정 실패:', error);
      alert('개별공지 노트 수정에 실패했습니다.');
      // 에러 발생 시에도 원래 확장 상태로 복원
      setExpandedIndividualNoticeId(originalExpanded);
      setOriginalExpandedStateBeforeEdit(null);
    }
  }, [user, currentClass, selectedIndividualStudent, editingIndividualNoticeItemId, editingIndividualNoticeContent, noticeIndFontSize, individualNoticeList, loadIndividualNoticesForStudent, showToastMessage, originalExpandedStateBeforeEdit, normalizedCurrentMemberGroup]);

  // 개별공지 삭제 확인 모달에서 삭제 실행
  const handleDeleteIndividualNotices = useCallback(async () => {
    if (!user || !currentClass || !selectedIndividualStudent || deletedIndividualNoticeIds.size === 0) return;
    
    setShowDeleteIndividualNoticeConfirmModal(false);

    try {
      // noteId에서 타임스탬프 추출하여 배열로 만들기
      const timestamps: number[] = [];
      const noteIdsArray = Array.from(deletedIndividualNoticeIds);
      for (const noteId of noteIdsArray) {
        // noteId에서 타임스탬프 추출 (individual-notice-{studentId}-{timestamp})
        const match = noteId.match(/individual-notice-[\w-]+-(\d+)/);
        if (match) {
          const timestamp = parseInt(match[1]);
          timestamps.push(timestamp);
        }
      }
      
      // 여러 개를 한 번에 삭제
      if (timestamps.length > 0) {
        await boardService.deleteIndividualNoticeNotes(
          user.id,
          currentClass.id,
          selectedIndividualStudent.id,
          timestamps,
          normalizedCurrentMemberGroup
        );
      }
      
      // 삭제 표시 초기화
      const deletedCount = deletedIndividualNoticeIds.size;
      setDeletedIndividualNoticeIds(new Set());
      
      // 목록 다시 로드
      const notes = await boardService.getIndividualNoticeNotesForStudent(
        user.id,
        currentClass.id,
        selectedIndividualStudent.id,
        normalizedCurrentMemberGroup
      );
      const convertedNotes = sortNoticesByDateDesc(
        notes.map((note) => ({
          id: `individual-notice-${selectedIndividualStudent.id}-${getCreatedAtMillis(note.createdAt) ?? Date.now()}`,
          content: note.content,
          categoryId: '개별공지노트',
          relatedStudentIds: [],
          date: note.date || '',
          timeTagName: undefined,
          // fontSize는 UI에서 localStorage로 관리하므로 매핑하지 않음
          createdAt: note.createdAt,
          updatedAt: note.updatedAt,
        }))
      );
      setIndividualNoticeList(convertedNotes);
      setIndividualNoticePage(1);
      
      // 학생 노트 수 다시 로드
      if (classMembers && classMembers.length > 0) {
        const counts: Record<string, number> = {};
        await Promise.all(
          classMembers.map(async (member) => {
            try {
              const notes = await boardService.getIndividualNoticeNotesForStudent(
                user.id,
                currentClass.id,
                member.id,
                normalizedCurrentMemberGroup
              );
              counts[member.id] = notes.length;
            } catch (error) {
              console.error(`❌ ${member.name} 노트 수 로드 실패:`, error);
              counts[member.id] = 0;
            }
          })
        );
        setStudentNoticeCounts(counts);
      }
      
      showToastMessage(`${deletedCount}개의 개별공지 노트가 삭제되었습니다.`);
    } catch (error) {
      console.error('❌ 개별공지 노트 삭제 실패:', error);
      alert('개별공지 노트 삭제에 실패했습니다.');
    }
  }, [user, currentClass, selectedIndividualStudent, deletedIndividualNoticeIds, sortNoticesByDateDesc, classMembers, showToastMessage, getCreatedAtMillis, normalizedCurrentMemberGroup]);

  // 개별공지 멤버 모달: PDF 다운로드 (페이지네이션 무시, 전체 목록 포함)
  const handleSaveIndividualNoticeAsPdf = useCallback(async () => {
    if (!selectedIndividualStudent) return;

    try {
      // 전체 목록을 쓰는 내보내기용 컨테이너를 쓰려면 먼저 렌더 유도
      if (individualNoticeList.length > 0) {
        setIsExportingIndividualNotice(true);
        await new Promise(resolve => setTimeout(resolve, 400));
      }

      const content = (individualNoticeList.length > 0 ? individualNoticeExportContainerRef.current : individualNoticeListModalBodyRef.current) as HTMLDivElement | null;
      if (!content) {
        setIsExportingIndividualNotice(false);
        return;
      }

      const html2canvasModule = await import('html2canvas');
      const html2canvas = (html2canvasModule as any).default;
      const jsPDFModule = await import('jspdf');
      const jsPDF = (jsPDFModule as any).default;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableWidth = pdfWidth - margin * 2;

      const dateHeader = content.querySelector('[data-pdf-element="header"]') as HTMLElement;
      const rows = content.querySelectorAll('[data-pdf-element="row"]');

      const lightModeContainer = document.createElement('div');
      lightModeContainer.setAttribute('data-theme', 'light');
      lightModeContainer.style.position = 'absolute';
      lightModeContainer.style.left = '-9999px';
      lightModeContainer.style.top = '0';
      lightModeContainer.style.width = content.offsetWidth + 'px';
      document.body.appendChild(lightModeContainer);

      let currentY = margin;
      try {
        if (dateHeader) {
          const clonedHeader = dateHeader.cloneNode(true) as HTMLElement;
          lightModeContainer.appendChild(clonedHeader);
          await new Promise(resolve => setTimeout(resolve, 100));
          const headerCanvas = await html2canvas(clonedHeader, { scale: 2, useCORS: true, logging: false } as any);
          const headerWidth = headerCanvas.width / 2;
          const headerHeight = headerCanvas.height / 2;
          const headerRatio = usableWidth / headerWidth;
          const scaledHeaderHeight = headerHeight * headerRatio;
          pdf.addImage(headerCanvas.toDataURL('image/png'), 'PNG', margin, currentY, usableWidth, scaledHeaderHeight);
          currentY += scaledHeaderHeight + 5;
        }
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i] as HTMLElement;
          const clonedRow = row.cloneNode(true) as HTMLElement;
          lightModeContainer.appendChild(clonedRow);
          await new Promise(resolve => setTimeout(resolve, 50));
          const rowCanvas = await html2canvas(clonedRow, { scale: 2, useCORS: true, logging: false } as any);
          const rowWidth = rowCanvas.width / 2;
          const rowHeight = rowCanvas.height / 2;
          const rowRatio = usableWidth / rowWidth;
          const scaledRowHeight = rowHeight * rowRatio;
          if (currentY + scaledRowHeight > pdfHeight - margin) {
            pdf.addPage();
            currentY = margin;
          }
          pdf.addImage(rowCanvas.toDataURL('image/png'), 'PNG', margin, currentY, usableWidth, scaledRowHeight);
          currentY += scaledRowHeight + 3;
        }
        const fileName = `개별공지_${selectedIndividualStudent.number ?? ''}번_${selectedIndividualStudent.name || '멤버'}_${new Date().toLocaleDateString('ko-KR')}.pdf`;
        pdf.save(fileName);
      } finally {
        if (lightModeContainer.parentNode) document.body.removeChild(lightModeContainer);
        setIsExportingIndividualNotice(false);
      }
    } catch (error) {
      console.error('PDF 저장 실패:', error);
      setIsExportingIndividualNotice(false);
      alert('PDF 저장에 실패했습니다.');
    }
  }, [selectedIndividualStudent, individualNoticeList.length]);

  // 개별공지 멤버 모달: 프린트 (페이지네이션 무시, 전체 목록 포함)
  const handlePrintIndividualNotice = useCallback(async () => {
    try {
      if (individualNoticeList.length > 0) {
        setIsExportingIndividualNotice(true);
        await new Promise(resolve => setTimeout(resolve, 400));
      }

      const content = (individualNoticeList.length > 0 ? individualNoticeExportContainerRef.current : individualNoticeListModalBodyRef.current) as HTMLDivElement | null;
      if (!content) {
        setIsExportingIndividualNotice(false);
        return;
      }

      const html2canvasModule = await import('html2canvas');
      const html2canvas = (html2canvasModule as any).default;

      const dateHeader = content.querySelector('[data-pdf-element="header"]') as HTMLElement;
      const rows = content.querySelectorAll('[data-pdf-element="row"]');
      const images: string[] = [];
      const lightModeContainer = document.createElement('div');
      lightModeContainer.setAttribute('data-theme', 'light');
      lightModeContainer.style.position = 'absolute';
      lightModeContainer.style.left = '-9999px';
      lightModeContainer.style.top = '0';
      lightModeContainer.style.width = content.offsetWidth + 'px';
      document.body.appendChild(lightModeContainer);

      try {
        if (dateHeader) {
          const clonedHeader = dateHeader.cloneNode(true) as HTMLElement;
          lightModeContainer.appendChild(clonedHeader);
          await new Promise(resolve => setTimeout(resolve, 100));
          const headerCanvas = await html2canvas(clonedHeader, { scale: 2, useCORS: true, logging: false } as any);
          images.push(headerCanvas.toDataURL('image/png'));
        }
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i] as HTMLElement;
          const clonedRow = row.cloneNode(true) as HTMLElement;
          lightModeContainer.appendChild(clonedRow);
          await new Promise(resolve => setTimeout(resolve, 50));
          const rowCanvas = await html2canvas(clonedRow, { scale: 2, useCORS: true, logging: false } as any);
          images.push(rowCanvas.toDataURL('image/png'));
        }
      } finally {
        if (lightModeContainer.parentNode) document.body.removeChild(lightModeContainer);
        setIsExportingIndividualNotice(false);
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('팝업 차단을 해제해주세요.');
        return;
      }
      const imagesHtml = images.map(img => `<img src="${img}" />`).join('');
      printWindow.document.write(`
        <html>
          <head>
            <title>개별공지</title>
            <style>
              @media print { body { margin: 0; padding: 10mm; background: white; } img { max-width: 100%; height: auto; page-break-inside: avoid; margin-bottom: 3mm; } }
              body { margin: 0; padding: 20px; background: white; }
              img { max-width: 100%; height: auto; display: block; margin-bottom: 8px; }
            </style>
          </head>
          <body>${imagesHtml}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    } catch (error) {
      console.error('프린트 실패:', error);
      setIsExportingIndividualNotice(false);
      alert('프린트에 실패했습니다.');
    }
  }, [individualNoticeList.length]);

  // 날짜별 개별공지 삭제 확인 모달 열기
  const handleOpenDeleteDateBasedConfirm = useCallback(() => {
    if (deletedDateBasedNoticeKeys.size === 0) return;
    setShowDeleteDateBasedConfirmModal(true);
  }, [deletedDateBasedNoticeKeys.size]);

  // 날짜별 개별공지 삭제
  const handleDeleteDateBasedNotices = useCallback(async () => {
    if (!user || !currentClass || deletedDateBasedNoticeKeys.size === 0) return;
    
    setShowDeleteDateBasedConfirmModal(false);
    
    try {
      console.log('🗑️ 날짜별 개별공지 삭제 시작:', {
        deletedKeys: Array.from(deletedDateBasedNoticeKeys),
        totalNotices: dateBasedIndividualNotices.length
      });
      
      // 삭제할 날짜별 목록의 모든 노트 수집
      const notesToDelete: Array<{ studentId: string; timestamp: number }> = [];
      
      dateBasedIndividualNotices.forEach((dateNotice) => {
        if (deletedDateBasedNoticeKeys.has(dateNotice.groupKey)) {
          console.log(`📋 삭제할 그룹 발견: ${dateNotice.groupKey}, 노트 수: ${dateNotice.notes.length}`);
          dateNotice.notes.forEach((note) => {
            const timestamp = getCreatedAtMillis(note.createdAt);
            if (timestamp) {
              notesToDelete.push({
                studentId: note.studentId,
                timestamp,
              });
            } else {
              console.warn(`⚠️ 타임스탬프 추출 실패:`, note);
            }
          });
        }
      });
      
      console.log(`📊 수집된 삭제 대상 노트: ${notesToDelete.length}개`);
      
      // 학생별로 그룹화하여 삭제
      const studentGroups = new Map<string, number[]>();
      notesToDelete.forEach(({ studentId, timestamp }) => {
        if (!studentGroups.has(studentId)) {
          studentGroups.set(studentId, []);
        }
        studentGroups.get(studentId)!.push(timestamp);
      });
      
      console.log(`👥 학생별 그룹 수: ${studentGroups.size}명`);
      studentGroups.forEach((timestamps, studentId) => {
        console.log(`  - ${studentId}: ${timestamps.length}개 노트`);
      });
      
      // 모든 학생의 노트를 한 번에 삭제 (문서를 한 번만 읽고 한 번에 업데이트)
      await boardService.deleteMultipleIndividualNoticeNotes(
        user.id,
        currentClass.id,
        studentGroups,
        normalizedCurrentMemberGroup
      );
      console.log(`✅ 모든 노트 삭제 완료`);
      
      // 삭제 표시 초기화
      const deletedCount = deletedDateBasedNoticeKeys.size;
      setDeletedDateBasedNoticeKeys(new Set());
      
      // 목록 다시 로드
      await loadDateBasedIndividualNotices();
      
      // 학생 노트 수 다시 로드
      if (classMembers && classMembers.length > 0) {
        const counts: Record<string, number> = {};
        await Promise.all(
          classMembers.map(async (member) => {
            try {
              const notes = await boardService.getIndividualNoticeNotesForStudent(
                user.id,
                currentClass.id,
                member.id,
                normalizedCurrentMemberGroup
              );
              counts[member.id] = notes.length;
            } catch (error) {
              console.error(`❌ ${member.name} 노트 수 로드 실패:`, error);
              counts[member.id] = 0;
            }
          })
        );
        setStudentNoticeCounts(counts);
      }
      
      showToastMessage(`${deletedCount}개의 날짜별 개별공지가 삭제되었습니다.`);
    } catch (error) {
      console.error('❌ 날짜별 개별공지 삭제 실패:', error);
      alert('날짜별 개별공지 삭제에 실패했습니다.');
    }
  }, [user, currentClass, deletedDateBasedNoticeKeys, dateBasedIndividualNotices, getCreatedAtMillis, loadDateBasedIndividualNotices, classMembers, showToastMessage, normalizedCurrentMemberGroup]);

  // 삭제 확인 모달 열기
  const handleOpenDeleteConfirm = useCallback(() => {
    if (deletedNoteIds.size === 0) return;
    setShowDeleteConfirmModal(true);
  }, [deletedNoteIds.size]);

  // 삭제 확인 모달에서 삭제 실행
  const handleDeleteNotes = useCallback(async () => {
    if (!user || !currentClass || deletedNoteIds.size === 0) return;
    
    setShowDeleteConfirmModal(false);

    try {
      if (noteListCategory === '전체공지노트' || noteListCategory === '그룹공지노트') {
        // 전체공지 노트 여러 개 삭제 (race condition 방지 위해 일괄 처리)
        const timestamps: number[] = [];
        const noteIdsArray = Array.from(deletedNoteIds);
        for (const noteId of noteIdsArray) {
          const match = noteId.match(/general-notice-(\d+)/);
          if (match) {
            timestamps.push(parseInt(match[1]));
          }
        }
        if (timestamps.length > 0) {
          const targetGroups =
            noteListCategory === '전체공지노트' ? availableMemberGroups : [normalizedCurrentMemberGroup];
          for (const g of targetGroups) {
            await boardService.deleteGeneralNoticeNotes(user.id, currentClass.id, timestamps, g);
          }
        }
      } else {
        // 노트북 노트 삭제
        // Promise.all로 동시에 삭제하면 같은 note 문서를 동시에 updateDoc 하며 race condition이 생겨
        // 1개만 삭제되는 경우가 있어 순차 삭제로 처리
        const noteIdsArray = Array.from(deletedNoteIds);
        for (const noteId of noteIdsArray) {
          await boardService.deleteNote(user.id, currentClass.id, noteId);
        }
      }
      
      // 삭제 표시 초기화
      const deletedCount = deletedNoteIds.size;
      setDeletedNoteIds(new Set());
      
      // 목록 다시 로드
      if (noteListCategory === '전체공지노트' || noteListCategory === '그룹공지노트') {
        const scope = noteListCategory === '그룹공지노트' ? 'group' : 'all';
        const notes = await boardService.getGeneralNoticeNotes(user.id, currentClass.id, normalizedCurrentMemberGroup);
        const filteredNotes = notes.filter((note) => ((note as any).scope || 'all') === scope);
        const convertedNotes = filteredNotes.map((note) => ({
          id: `general-notice-${getCreatedAtMillis(note.createdAt) ?? Date.now()}`,
          content: note.content,
          categoryId: noteListCategory,
          noticeScope: (note as any).scope || 'all',
          relatedStudentIds: [],
          date: note.date || '',
          timeTagName: undefined,
          // fontSize는 UI에서 localStorage로 관리하므로 매핑하지 않음
          createdAt: note.createdAt,
          updatedAt: note.updatedAt,
        }));
        setAllNotes(convertedNotes);
      } else if (noteListCategory) {
        const notes = await boardService.getNotes(user.id, currentClass.id, noteListCategory);
        setAllNotes(notes);
      }
        
      // 현재 페이지 조정
      const totalPages = Math.ceil(allNotes.length / NOTES_PER_PAGE);
        if (currentPage > totalPages && totalPages > 0) {
          setCurrentPage(totalPages);
        } else if (totalPages === 0) {
          setCurrentPage(1);
      }
      
      showToastMessage(`${deletedCount}개의 노트가 삭제되었습니다.`);
    } catch (error) {
      console.error('❌ 노트 삭제 실패:', error);
      showToastMessage('노트 삭제에 실패했습니다.');
    }
  }, [user, currentClass, deletedNoteIds, noteListCategory, currentPage, allNotes.length, getCreatedAtMillis, showToastMessage, availableMemberGroups, normalizedCurrentMemberGroup]);

  // 다음 노트 로드 (> 버튼)
  const handleLoadNextNote = useCallback(() => {
    if (allNotes.length === 0 || currentNoteIndex >= allNotes.length - 1) return;

    const nextIndex = currentNoteIndex + 1;
    const nextNote = allNotes[nextIndex];

    loadNoteToBoard(nextNote);
    setEditingNoteId(nextNote.id);
    setCurrentNoteIndex(nextIndex);

    console.log(`➡️ 다음 노트로 이동: ${nextNote.content.substring(0, 30)}... (${nextIndex + 1}/${allNotes.length})`);
  }, [allNotes, currentNoteIndex, loadNoteToBoard]);

  // 이전 노트 로드 (< 버튼)
  const handleLoadPrevNote = useCallback(() => {
    if (allNotes.length === 0 || currentNoteIndex <= 0) return;

    const prevIndex = currentNoteIndex - 1;
    const prevNote = allNotes[prevIndex];

    loadNoteToBoard(prevNote);
    setEditingNoteId(prevNote.id);
    setCurrentNoteIndex(prevIndex);

    console.log(`⬅️ 이전 노트로 이동: ${prevNote.content.substring(0, 30)}... (${prevIndex + 1}/${allNotes.length})`);
  }, [allNotes, currentNoteIndex, loadNoteToBoard]);


  // 메인 페이지용 날짜 표시 컴포넌트
  const CustomDateDisplay = ({ selectedDate, onDateChange }: { selectedDate: string; onDateChange: (date: string) => void }) => {
    const [editingField, setEditingField] = useState<'year' | 'month' | 'day' | null>(null);
    const [tempValue, setTempValue] = useState('');
    
    const dateObj = new Date(selectedDate);
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    
    const handleFieldClick = (field: 'year' | 'month' | 'day') => {
      setEditingField(field);
      if (field === 'year') setTempValue(year.toString());
      else if (field === 'month') setTempValue(month.toString());
      else if (field === 'day') setTempValue(day.toString());
    };
    
    const handleKeyPress = (e: React.KeyboardEvent, field: 'year' | 'month' | 'day') => {
      if (e.key === 'Enter') {
        handleSave(field);
      } else if (e.key === 'Escape') {
        setEditingField(null);
        setTempValue('');
      }
    };
    
    const handleBlur = (field: 'year' | 'month' | 'day') => {
      handleSave(field);
    };
    
    const handleSave = (field: 'year' | 'month' | 'day') => {
      const value = parseInt(tempValue);
      
      if (isNaN(value)) {
        setEditingField(null);
        setTempValue('');
        return;
      }
      
      let newDate = '';
      if (field === 'year') {
        newDate = `${value}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      } else if (field === 'month') {
        if (value < 1 || value > 12) {
          setEditingField(null);
          setTempValue('');
          return;
        }
        newDate = `${year}-${value.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      } else if (field === 'day') {
        if (value < 1 || value > 31) {
          setEditingField(null);
          setTempValue('');
          return;
        }
        newDate = `${year}-${month.toString().padStart(2, '0')}-${value.toString().padStart(2, '0')}`;
      }
      
      // 유효한 날짜인지 확인
      const testDate = new Date(newDate);
      if (testDate.getFullYear() === value || (field !== 'year' && testDate.getMonth() + 1 === (field === 'month' ? value : month)) || (field === 'day' && testDate.getDate() === value)) {
        onDateChange(newDate);
      }
      
      setEditingField(null);
      setTempValue('');
    };
    
    return (
      <DateDisplayContainer>
        <CalendarIcon onClick={() => setShowCalendarModal(true)}>
          <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
            <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
            <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
            <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </CalendarIcon>
        <DateDisplayText>
          <DateYearRow>
            {editingField === 'year' ? (
              <DateInput
                $fieldType="year"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'year')}
                onBlur={() => handleBlur('year')}
                autoFocus
              />
            ) : (
              <DatePart onClick={() => handleFieldClick('year')}>{year}년</DatePart>
            )}
          </DateYearRow>
          <DateMonthDayRow>
            {editingField === 'month' ? (
              <DateInput
                $fieldType="month"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'month')}
                onBlur={() => handleBlur('month')}
                autoFocus
              />
            ) : (
              <DatePart onClick={() => handleFieldClick('month')}>{month}월</DatePart>
            )}
            {editingField === 'day' ? (
              <DateInput
                $fieldType="day"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'day')}
                onBlur={() => handleBlur('day')}
                autoFocus
              />
            ) : (
              <DatePart onClick={() => handleFieldClick('day')}>{day}일</DatePart>
            )}
          </DateMonthDayRow>
        </DateDisplayText>
      </DateDisplayContainer>
    );
  };

  return (
    <BoardContainer>
      <ButtonContainer>
        <ButtonContentRow>
          {/* 날짜 버튼, 타임 태그 버튼 */}
          <DateTimeButtonGroup>
            <CustomDateDisplay
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
            />
            <TimeTagSelectButton
              onClick={() => setShowTimeTagSelectModal(true)}
              title="타임 태그 선택"
            >
              <TimeTagIcon>
                {selectedTimeTag ? (selectedDayOfWeek || selectedTimeTag.dayOfWeek) : '#'}
              </TimeTagIcon>
              <TimeTagInfo>
                {selectedTimeTag ? (
                  <>
                    <TimeTagName>
                      {selectedTimeTag.name}
                    </TimeTagName>
                    <TimeTagTime>{selectedTimeTag.startTime} ~ {selectedTimeTag.endTime}</TimeTagTime>
                  </>
                ) : (
                  <>
                    <TimeTagName>타임 태그</TimeTagName>
                    <TimeTagTime>선택 안됨</TimeTagTime>
                  </>
                )}
              </TimeTagInfo>
            </TimeTagSelectButton>
          </DateTimeButtonGroup>

          {/* 원형 버튼들 (호버 시 타이틀 표시) */}
          <CircularButtonGroup>
            <BoardCircularTooltipWrapper data-tooltip="공지">
              <CircularButton
                onClick={() => {
                  setShowNoticeForm(true);
                  setShowNotebookForm(false);
                  setShowDiceForm(false);
                }}
                aria-label="공지"
              >
                <img src="/alram.svg" alt="공지" />
              </CircularButton>
            </BoardCircularTooltipWrapper>
            <BoardCircularTooltipWrapper data-tooltip="노트북">
              <CircularButton
                onClick={() => {
                  setShowNotebookForm(true);
                  setShowNoticeForm(false);
                  setShowDiceForm(false);
                }}
                aria-label="노트북"
              >
                <img src="/Notebook.svg" alt="노트북" />
              </CircularButton>
            </BoardCircularTooltipWrapper>
            <BoardCircularTooltipWrapper data-tooltip="타이머">
              <CircularButton
                onClick={() => onTimerModalOpen?.()}
                aria-label="타이머"
              >
                <img src="/Timer.svg" alt="타이머" />
              </CircularButton>
            </BoardCircularTooltipWrapper>
            <BoardCircularTooltipWrapper data-tooltip="뽑기">
              <CircularButton
                onClick={() => {
                  if (onDiceModalOpen) {
                    onDiceModalOpen();
                    return;
                  }
                  setShowDiceForm(true);
                  setShowNoticeForm(false);
                  setShowNotebookForm(false);
                }}
                aria-label="뽑기"
              >
                <img src="/Dice.svg" alt="뽑기" />
              </CircularButton>
            </BoardCircularTooltipWrapper>
          </CircularButtonGroup>
        </ButtonContentRow>
        <ButtonContainerDivider />
      </ButtonContainer>

      {/* 달력 모달 */}
      {showCalendarModal && (
        <CalendarModalOverlay onClick={() => setShowCalendarModal(false)}>
          <CalendarModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>날짜 선택</h3>
              <CloseButton onClick={() => setShowCalendarModal(false)}>×</CloseButton>
            </ModalHeader>
            <CalendarComponent
              selectedDate={selectedDate}
              onDateSelect={(date) => {
                setSelectedDate(date);
                setShowCalendarModal(false);
              }}
            />
          </CalendarModalContent>
        </CalendarModalOverlay>
      )}

      {/* 공지 노트 (페이지 내 표시) */}
      {showNoticeForm && (
        <NoticeFormSection>
          {/* 1행: 전체공지노트/개별공지노트 카테고리 버튼 + 날짜 표시 (데스크탑: 한 행, 모바일: 분리) */}
          <CategoryRow>
            <CategoryButtonsGroup>
              <CategoryButtonWrapper data-active={noticeTab === 'GeneralNotice' && generalNoticeScope === 'all'}>
                <CategoryButton
                  $isActive={noticeTab === 'GeneralNotice' && generalNoticeScope === 'all'}
                  onClick={() => {
                    setNoticeTab('GeneralNotice');
                    setGeneralNoticeScope('all');
                  }}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  전체공지
                </CategoryButton>
              </CategoryButtonWrapper>
              <CategoryButtonWrapper data-active={noticeTab === 'GeneralNotice' && generalNoticeScope === 'group'}>
                <CategoryButton
                  $isActive={noticeTab === 'GeneralNotice' && generalNoticeScope === 'group'}
                  onClick={() => {
                    setNoticeTab('GeneralNotice');
                    setGeneralNoticeScope('group');
                  }}
                  title={`현재 선택 그룹(${normalizedCurrentMemberGroup})에만 저장`}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  그룹공지
                </CategoryButton>
              </CategoryButtonWrapper>
              <CategoryButtonWrapper data-active={noticeTab === 'IndividualNotice'}>
                <CategoryButton
                  $isActive={noticeTab === 'IndividualNotice'}
                  onClick={() => setNoticeTab('IndividualNotice')}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  개별공지
                </CategoryButton>
              </CategoryButtonWrapper>
            </CategoryButtonsGroup>
            
            {/* 네비게이션 버튼 그룹 (오른쪽 정렬) */}
            {noticeTab === 'GeneralNotice' && (
              <NavButtonsGroup>
                <CategoryListButton
                  type="button"
                  title={generalNoticeScope === 'group' ? '그룹공지 목록 보기' : '전체공지 목록 보기'}
                  onClick={() => openGeneralNoticeListModal(generalNoticeScope)}
                >
                  <img src="/list.svg" alt="공지 목록" />
                </CategoryListButton>
                {/* 이전 공지 노트 버튼 */}
                <CategoryNavButton
                  onClick={handlePreviousNotice}
                  disabled={
                    allNoticeNotes.length === 0 || 
                    (!!editingNoticeId && allNoticeNotes.findIndex(note => note.id === editingNoticeId) === 0)
                  }
                  title="이전 공지 노트 (더 최근)"
                >
                  &lt;
                </CategoryNavButton>
                
                {/* 다음 공지 노트 버튼 */}
                <CategoryNavButton
                  onClick={handleNextNotice}
                  disabled={
                    allNoticeNotes.length === 0 ||
                    (!!editingNoticeId && allNoticeNotes.findIndex(note => note.id === editingNoticeId) === allNoticeNotes.length - 1)
                  }
                  title="다음 공지 노트 (더 과거)"
                >
                  &gt;
                </CategoryNavButton>

                {/* 새 노트 만들기 버튼 */}
                <CategoryNavButton
                  onClick={handleNewNotice}
                  disabled={isNoticeLoading}
                  title="새 공지 노트 만들기"
                  style={{
                    background: 'var(--color-accent-strong)',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    borderWidth: '2px'
                  }}
                >
                  +
                </CategoryNavButton>
              </NavButtonsGroup>
            )}

            {/* 개별공지 네비게이션 버튼 그룹 (오른쪽 정렬) - 날짜별 조회 */}
            {noticeTab === 'IndividualNotice' && (
              <NavButtonsGroup>
                <CategoryListButton
                  type="button"
                  title="개별공지 목록 보기"
                  onClick={openIndividualStudentPickModal}
                >
                  <img src="/list.svg" alt="개별공지 목록" />
                </CategoryListButton>
                {/* 이전 날짜별 공지 버튼 (< 버튼) - 더 최신 데이터 */}
                <CategoryNavButton
                  onClick={handlePreviousDateBasedNotice}
                  disabled={
                    dateBasedIndividualNotices.length === 0 || 
                    (editingIndividualNoticeNotes.length > 0 && currentDateBasedIndex === 0)
                  }
                  title="이전 날짜별 공지 (더 최근)"
                >
                  &lt;
                </CategoryNavButton>
                
                {/* 다음 날짜별 공지 버튼 (> 버튼) - 더 과거 데이터 */}
                <CategoryNavButton
                  onClick={handleNextDateBasedNotice}
                  disabled={
                    dateBasedIndividualNotices.length === 0 ||
                    (editingIndividualNoticeNotes.length > 0 && currentDateBasedIndex === dateBasedIndividualNotices.length - 1)
                  }
                  title="다음 날짜별 공지 (더 과거)"
                >
                  &gt;
                </CategoryNavButton>

                {/* 새 노트 만들기 버튼 */}
                <CategoryNavButton
                  onClick={handleNewNotice}
                  disabled={isNoticeLoading}
                  title="새 공지 노트 만들기"
                  style={{
                    background: 'var(--color-accent-strong)',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    borderWidth: '2px'
                  }}
                >
                  +
                </CategoryNavButton>
              </NavButtonsGroup>
            )}
            
            {/* 데스크탑: 날짜 표시를 같은 행에 중앙 정렬 */}
            {(noticeTab === 'GeneralNotice' || noticeTab === 'IndividualNotice') && (
              <NoticeDateDisplay className="desktop-date">
                {(() => {
                  try {
                    const dateObj = new Date(selectedDate);
                    const month = dateObj.getMonth() + 1;
                    const day = dateObj.getDate();
                    const dayOfWeek = dateObj.getDay();
                    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
                    return `${month}월 ${day}일 (${weekdays[dayOfWeek]})`;
                  } catch {
                    return '';
                  }
                })()}
              </NoticeDateDisplay>
            )}
            {/* 모바일: 날짜 표시를 같은 행에 포함 (768px 이하) */}
            {(noticeTab === 'GeneralNotice' || noticeTab === 'IndividualNotice') && (
              <NoticeDateDisplay className="mobile-date">
                {(() => {
                  try {
                    const dateObj = new Date(selectedDate);
                    const month = dateObj.getMonth() + 1;
                    const day = dateObj.getDate();
                    const dayOfWeek = dateObj.getDay();
                    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
                    return `${month}월 ${day}일 (${weekdays[dayOfWeek]})`;
                  } catch {
                    return '';
                  }
                })()}
              </NoticeDateDisplay>
            )}
          </CategoryRow>


          {/* 2행: 입력 영역 */}
          {noticeTab === 'GeneralNotice' ? (
            <NoteTextArea
              value={noticeAllContent}
              onChange={(e) => setNoticeAllContent(e.target.value)}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
                  e.stopPropagation();
                }
              }}
              placeholder="공지 내용을 입력하세요..."
              disabled={false}
              style={{
                fontSize: `${noticeAllFontSize}px`,
              }}
            />
          ) : (
            <>
              {/* 번호/자리 토글 버튼 */}
              <ViewModeToggleContainer>
                <ViewModeToggleButton 
                  $isSeatMode={viewMode === 'seat'} 
                  $isDisabled={false}
                  onClick={async () => {
                    if (viewMode === 'number') {
                      // 자리 모드로 전환
                      await loadSeatArrangementData();
                      setViewMode('seat');
                    } else {
                      // 번호 모드로 전환
                      setViewMode('number');
                      setSeatArrangementError('');
                    }
                  }}
                  tabIndex={-1}
                >
                  <ViewModeToggleOption $isActive={viewMode === 'number'} $isDisabled={false}>번호</ViewModeToggleOption>
                  <ViewModeToggleOption $isActive={viewMode === 'seat'} $isDisabled={false}>자리</ViewModeToggleOption>
                </ViewModeToggleButton>
              </ViewModeToggleContainer>

              {/* 자리 모드일 때 자리배치대로 배치 */}
              {viewMode === 'seat' && seatArrangementError ? (
                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', padding: '20px' }}>{seatArrangementError}</div>
              ) : viewMode === 'seat' && seatArrangementData && seatArrangementData.length > 0 ? (
                (() => {
                  // 자리 모드 그리드 (개별공지): 상단/하단의 "완전히 비어있는 행"만 trim
                  // - 블럭 사이의 빈 행은 유지해야 하므로, min~max 범위는 연속 행으로 유지
                  // - 180도 회전이 적용된 뒤의 좌표를 기준으로 trim 해야 함
                  const GRID_TRACKS = 16; // 0..15
                  const ROTATE_MAX_INDEX = 14; // 기존 로직 유지 (0..14 좌표를 180도 회전)

                  const usedRows = new Set<number>();
                  const usedCols = new Set<number>();
                  let minUsedRow = Number.POSITIVE_INFINITY;
                  let maxUsedRow = Number.NEGATIVE_INFINITY;

                  seatArrangementData.forEach((seatStudent: any) => {
                    const seatRow = typeof seatStudent?.seatRow === 'number' ? seatStudent.seatRow : 0;
                    const seatCol = typeof seatStudent?.seatCol === 'number' ? seatStudent.seatCol : 0;

                    const rotatedRow = isSeatRotated ? (ROTATE_MAX_INDEX - seatRow) : seatRow;
                    const rotatedCol = isSeatRotated ? (ROTATE_MAX_INDEX - seatCol) : seatCol;

                    // 학생 블럭은 2x2 를 차지한다고 가정 (현재 구현과 동일: span 2)
                    const occupiedRows = [rotatedRow, rotatedRow + 1];
                    const occupiedCols = [rotatedCol, rotatedCol + 1];

                    occupiedRows.forEach((r) => {
                      if (r < 0 || r >= GRID_TRACKS) return;
                      usedRows.add(r);
                      minUsedRow = Math.min(minUsedRow, r);
                      maxUsedRow = Math.max(maxUsedRow, r);
                    });

                    occupiedCols.forEach((c) => {
                      if (c < 0 || c >= GRID_TRACKS) return;
                      usedCols.add(c);
                    });
                  });

                  // 안전장치: 데이터가 이상한 경우 trim 없이 전체 사용
                  const effectiveMinRow = Number.isFinite(minUsedRow) ? minUsedRow : 0;
                  const effectiveMaxRow = Number.isFinite(maxUsedRow) ? maxUsedRow : (GRID_TRACKS - 1);

                  const trimmedStartRow = Math.max(0, Math.min(effectiveMinRow, GRID_TRACKS - 1));
                  const trimmedEndRow = Math.max(0, Math.min(effectiveMaxRow, GRID_TRACKS - 1));
                  const rowOffset = trimmedStartRow;

                  // 동적 그리드 템플릿 생성 (상/하단 빈행 trim 적용)
                  const gridTemplateRows = Array.from(
                    { length: Math.max(1, trimmedEndRow - trimmedStartRow + 1) },
                    (_, idx) => {
                      const rowIndex = trimmedStartRow + idx;
                      return usedRows.has(rowIndex) ? '1fr' : '0.5fr';
                    }
                  ).join(' ');

                  // 열은 기존과 동일하게 전체를 유지 (요청사항은 상/하단 "행" trim)
                  const gridTemplateColumns = Array.from(
                    { length: GRID_TRACKS },
                    (_, i) => (usedCols.has(i) ? '1fr' : '0.5fr')
                  ).join(' ');

                  return (
                    <>
                      <SeatModeScrollArea>
                        <SeatModeGridWrapper>
                          <IndividualNoticeSeatGrid
                            $gridTemplateColumns={gridTemplateColumns}
                            $gridTemplateRows={gridTemplateRows}
                          >
                            {seatArrangementData.map((seatStudent: any) => {
                              const member = classMembers.find(m => m.id === seatStudent.studentId);
                              if (!member) return null;

                              // 180도 회전 좌표 계산 (PresentationPage와 동일한 방식)
                              const rotatedRow = isSeatRotated 
                                ? 14 - seatStudent.seatRow
                                : seatStudent.seatRow;
                              const rotatedCol = isSeatRotated 
                                ? 14 - seatStudent.seatCol
                                : seatStudent.seatCol;
                              
                              // gridRow/gridColumn은 1-based이므로 +1
                              const rotatedGridRow = (rotatedRow - rowOffset) + 1;
                              const rotatedGridCol = rotatedCol + 1;

                              return (
                                <IndividualNoticeStudentCard
                                  key={seatStudent.studentId}
                                  $hasData={!!(individualNoticeDrafts[seatStudent.studentId] || '').trim()}
                                  $isSelected={selectedIndividualStudents.has(seatStudent.studentId)}
                                  style={{
                                    gridRow: `${rotatedGridRow} / span 2`,
                                    gridColumn: `${rotatedGridCol} / span 2`,
                                  }}
                                >
                                  <IndividualNoticeNoteCardLayout>
                                    <IndividualNoticeStudentHeader>
                                      <IndividualNoticeStudentNumber>
                                        {member.number ? String(member.number).padStart(2, '0') : ''}
                                      </IndividualNoticeStudentNumber>
                                      <IndividualNoticeStudentName>{member.name || '학생'}</IndividualNoticeStudentName>
                                    </IndividualNoticeStudentHeader>

                                    <IndividualNoticeProfileImageArea
                                      onClick={() => handleIndividualStudentSelection(member.id)}
                                    >
                                      <CachedProfileImage
                                        imageId={resolveMemberProfileSource(member, memberProfileDisplayMode)}
                                        alt={member.name || '프로필'}
                                      />
                                      {selectedIndividualStudents.has(member.id) && (
                                        <SelectionIndicator>
                                          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong)"/>
                                            <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                        </SelectionIndicator>
                                      )}
                                    </IndividualNoticeProfileImageArea>

                                    <IndividualNoticeStudentTextArea
                                      value={individualNoticeDrafts[member.id] || ''}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setIndividualNoticeDrafts((prev) => ({ ...(prev || {}), [member.id]: v }));
                                      }}
                                      placeholder="멤버에 대한 공지 내용을 입력하세요..."
                                      disabled={isNoticeLoading}
                                    />
                                  </IndividualNoticeNoteCardLayout>
                                </IndividualNoticeStudentCard>
                              );
                            })}
                          </IndividualNoticeSeatGrid>
                        </SeatModeGridWrapper>
                      </SeatModeScrollArea>
                      <RotateButtonContainer>
                        <RotateButton onClick={() => setIsSeatRotated(!isSeatRotated)} title="180도 회전">
                          <RotateIconWrapper>
                            <RotateArrowIcon>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 4v16M12 4l-4 4M12 4l4 4M12 20l-4-4M12 20l4-4" />
                              </svg>
                            </RotateArrowIcon>
                            <RotateNumberIcon>180</RotateNumberIcon>
                          </RotateIconWrapper>
                        </RotateButton>
                      </RotateButtonContainer>
                    </>
                  );
                })()
              ) : viewMode === 'number' ? (
                <>
                  <IndividualNoticeStudentsGrid $alignment={gridAlignment}>
              {classMembers.map((m) => (
                <IndividualNoticeStudentCard
                  key={m.id}
                  $hasData={!!(individualNoticeDrafts[m.id] || '').trim()}
                  $isSelected={selectedIndividualStudents.has(m.id)}
                >
                  <IndividualNoticeNoteCardLayout>
                    <IndividualNoticeStudentHeader>
                      <IndividualNoticeStudentNumber>
                        {m.number ? String(m.number).padStart(2, '0') : ''}
                      </IndividualNoticeStudentNumber>
                      <IndividualNoticeStudentName>{m.name || '학생'}</IndividualNoticeStudentName>
                    </IndividualNoticeStudentHeader>

                    <IndividualNoticeProfileImageArea
                      onClick={() => handleIndividualStudentSelection(m.id)}
                    >
                      <CachedProfileImage
                        imageId={resolveMemberProfileSource(m, memberProfileDisplayMode)}
                        alt={m.name || '프로필'}
                      />
                      {selectedIndividualStudents.has(m.id) && (
                        <SelectionIndicator>
                          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong)"/>
                            <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </SelectionIndicator>
                      )}
                    </IndividualNoticeProfileImageArea>

                    <IndividualNoticeStudentTextArea
                      value={individualNoticeDrafts[m.id] || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setIndividualNoticeDrafts((prev) => ({ ...(prev || {}), [m.id]: v }));
                      }}
                      placeholder="멤버에 대한 공지 내용을 입력하세요..."
                      disabled={isNoticeLoading}
                    />
                  </IndividualNoticeNoteCardLayout>
                </IndividualNoticeStudentCard>
              ))}
            </IndividualNoticeStudentsGrid>
                  
                  {/* 그리드 정렬 토글 - 번호 모드일 때만 표시 */}
                  <GridAlignmentContainer>
                    <GridAlignmentToggle 
                      onClick={() => setGridAlignment(prev => prev === 'left' ? 'center' : 'left')}
                      tabIndex={-1}
                      title={gridAlignment === 'left' ? '중앙 정렬로 변경' : '왼쪽 정렬로 변경'}
                    >
                      <GridAlignmentOption $isActive={gridAlignment === 'left'}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6h18M3 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </GridAlignmentOption>
                      <GridAlignmentOption $isActive={gridAlignment === 'center'}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6h18M6 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </GridAlignmentOption>
                    </GridAlignmentToggle>
                  </GridAlignmentContainer>
                </>
              ) : null}
            </>
          )}

          {/* 3행: 글자 크기 버튼, 저장 버튼 (노트북과 동일) */}
          <ActionButtonRow>
            {/* 글자 크기 버튼 (GeneralNotice만) */}
            {noticeTab === 'GeneralNotice' && (
              <FontSizeButtonRow>
                <ActionButton onClick={handleNoticeDecreaseFontSize} disabled={isNoticeLoading}>
                  글자 작게
                </ActionButton>
                <ActionButton onClick={handleNoticeIncreaseFontSize} disabled={isNoticeLoading}>
                  글자 크게
                </ActionButton>
              </FontSizeButtonRow>
            )}
            
            {/* 개별공지노트: 전체선택, 선택취소, 일괄 노트 작성 버튼 */}
            {noticeTab === 'IndividualNotice' && (
              <>
                <ActionButton 
                  onClick={handleSelectAllIndividualStudents}
                  disabled={isNoticeLoading}
                >
                  전체선택
                </ActionButton>
                <ActionButton 
                  onClick={handleDeselectAllIndividualStudents}
                  disabled={isNoticeLoading || selectedIndividualStudents.size === 0}
                >
                  선택취소
                </ActionButton>
                <ActionButton 
                  onClick={handleOpenBulkIndividualNoticeModal}
                  disabled={selectedIndividualStudents.size === 0}
                  style={{ marginRight: 'auto' }}
                >
                  일괄 노트 작성
                </ActionButton>
              </>
            )}
            
            {/* 저장 버튼과 새 노트 버튼 */}
            <SaveAndNewButtonRow>
              <SaveButton 
                onClick={handleSaveNotice} 
                disabled={
                  isNoticeLoading || 
                  isNoticeSaving || 
                  (noticeTab === 'GeneralNotice' && !!editingNoticeId && editingNoticeId !== 'new' && !hasGeneralNoticeChanges)
                }
              >
                {isNoticeSaving 
                  ? '저장 중...' 
                  : (noticeTab === 'GeneralNotice' && editingNoticeId && editingNoticeId !== 'new' 
                      ? '수정하기' 
                      : (noticeTab === 'IndividualNotice' && editingIndividualNoticeNotes.length > 0
                          ? '수정하기'
                          : '저장하기'))
                }
              </SaveButton>
              <NewNoteButton onClick={handleNewNotice} disabled={isNoticeLoading}>
                +새노트
              </NewNoteButton>
            </SaveAndNewButtonRow>
          </ActionButtonRow>
        </NoticeFormSection>
      )}

      {/* 일괄 노트 작성 모달 */}
      {showBulkIndividualNoticeModal && (
        <BulkIndividualNoticeModal>
          <BulkIndividualNoticeModalOverlay onClick={handleCloseBulkIndividualNoticeModal} />
          <BulkIndividualNoticeModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>일괄 노트 작성</h3>
              <CloseButton onClick={handleCloseBulkIndividualNoticeModal}>×</CloseButton>
            </ModalHeader>
            
            <BulkIndividualNoticeFormGroup>
              <BulkIndividualNoticeTextArea
                value={bulkIndividualNoticeContent}
                onChange={(e) => setBulkIndividualNoticeContent(e.target.value)}
                placeholder="공지 내용을 입력하세요"
              />
            </BulkIndividualNoticeFormGroup>

            <BulkIndividualNoticeModalFooter>
              <BulkIndividualNoticeCloseButton onClick={handleCloseBulkIndividualNoticeModal}>
                닫기
              </BulkIndividualNoticeCloseButton>
              <BulkIndividualNoticeApplyButton onClick={handleApplyBulkIndividualNotice}>
                테이블반영
              </BulkIndividualNoticeApplyButton>
            </BulkIndividualNoticeModalFooter>
          </BulkIndividualNoticeModalContent>
        </BulkIndividualNoticeModal>
      )}

      {/* 메모 입력 폼 */}
      {showNotebookForm && (
        <NotebookFormSection>
          {/* 1행: 카테고리 */}
          {categories.length > 0 ? (
            <CategoryRow>
              {/* 카테고리 버튼 그룹 (왼쪽 정렬) */}
              <CategoryButtonsGroup>
                {categories.map((category, index) => {
                  const isActive = selectedCategory === category.name;
                  // 플랜에 따른 노트북 카테고리 제한 확인
                  const userLimits = user ? getLicenseLimits(user.userType) : getLicenseLimits('free');
                  const maxCategories = userLimits.maxNoteCategories || 5;
                  const isDisabled = index >= maxCategories;

                  return (
                    <CategoryButtonWrapper key={category.id} data-active={isActive}>
                      <SpringLines $isActive={isActive}>
                        <SpringLine $isActive={isActive} />
                        <SpringLine $isActive={isActive} />
                        <SpringLine $isActive={isActive} />
                        <SpringLine $isActive={isActive} />
                      </SpringLines>
                      <CategoryButton
                        $isActive={isActive}
                        $isDisabled={isDisabled}
                        onClick={async () => {
                          if (isDisabled) {
                            // 비활성화된 카테고리 클릭 시 이용권 제한 안내
                            const userLimits = user ? getLicenseLimits(user.userType) : getLicenseLimits('free');
                            const maxCategories = userLimits.maxNoteCategories || 5;
                            setLicenseLimitMessage(`현재 플랜에서는 최대 ${maxCategories}개 노트북 카테고리까지만 사용할 수 있습니다.\n더 많은 노트북 카테고리를 사용하려면 이용권을 업그레이드하세요.`);
                            setShowLicenseLimitModal(true);
                            return;
                          }
                          // 잠긴 카테고리는 비밀번호 모달 표시
                          if (category.locked) {
                            setNotebookLockCategory(category);
                            setNotebookLockPassword('');
                            setNotebookLockError('');
                            setShowNotebookLockModal(true);
                            return;
                          }
                          setSelectedCategory(category.name);
                          // 카테고리 선택 시 노트 목록만 로드 (보드 반영하지 않음)
                          await loadCategoryNotes(category.name);
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {category.locked && (
                            <img
                              src="/lock.svg"
                              alt="잠금"
                              style={{ width: 14, height: 14, marginRight: 0, marginLeft: -3, filter: 'invert(1)' }}
                            />
                          )}
                          {category.name}
                        </span>
                      </CategoryButton>
                    </CategoryButtonWrapper>
                  );
                })}
              </CategoryButtonsGroup>
              
              {/* 네비게이션 버튼 그룹 (오른쪽 정렬) */}
              <NavButtonsGroup>
                {/* 노트 목록 버튼 */}
                <CategoryListButton
                  type="button"
                  title="노트 목록 보기"
                  onClick={async () => {
                    if (!user || !currentClass) return;
                    if (!selectedCategory) {
                      alert('먼저 노트북 카테고리를 선택해 주세요.');
                      return;
                    }
                    const category = categories.find(cat => cat.name === selectedCategory);
                    if (!category) {
                      alert('선택한 카테고리를 찾을 수 없습니다.');
                      return;
                    }
                    try {
                      const notes = await boardService.loadRecentNotesForCategory(user.id, currentClass.id, category.id);
                      setAllNotes(notes);
                      setNoteListCategory(category.name);
                      setCurrentPage(1);
                      setDeletedNoteIds(new Set());
                      setShowNoteListModal(true);
                    } catch (error) {
                      console.error('❌ 노트 목록 로드 실패:', error);
                      alert('노트 목록을 불러오는데 실패했습니다.');
                    }
                  }}
                >
                  <img src="/list.svg" alt="노트 목록" />
                </CategoryListButton>

                {/* 이전 노트 버튼 */}
                <CategoryNavButton
                  onClick={handleLoadPrevNote}
                  disabled={currentNoteIndex <= 0} // 첫 번째 노트이면 비활성화
                  title="이전 노트"
                >
                  &lt;
                </CategoryNavButton>

                {/* 다음 노트 버튼 */}
                <CategoryNavButton
                  onClick={handleLoadNextNote}
                  disabled={currentNoteIndex >= allNotes.length - 1} // 마지막 노트이면 비활성화
                  title="다음 노트"
                >
                  &gt;
                </CategoryNavButton>

                {/* 새 노트 버튼 */}
                <CategoryNavButton
                  onClick={handleNewNote}
                  title="새 노트 작성"
                >
                  +
                </CategoryNavButton>
              </NavButtonsGroup>
              {/* 날짜(요일) 표시 - 공지 섹션과 동일 위치/크기 */}
              <NoticeDateDisplay className="desktop-date">
                {(() => {
                  try {
                    const dateObj = new Date(selectedDate);
                    const month = dateObj.getMonth() + 1;
                    const day = dateObj.getDate();
                    const dayOfWeek = dateObj.getDay();
                    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
                    return `${month}월 ${day}일 (${weekdays[dayOfWeek]})`;
                  } catch {
                    return '';
                  }
                })()}
              </NoticeDateDisplay>
              <NoticeDateDisplay className="mobile-date">
                {(() => {
                  try {
                    const dateObj = new Date(selectedDate);
                    const month = dateObj.getMonth() + 1;
                    const day = dateObj.getDate();
                    const dayOfWeek = dateObj.getDay();
                    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
                    return `${month}월 ${day}일 (${weekdays[dayOfWeek]})`;
                  } catch {
                    return '';
                  }
                })()}
              </NoticeDateDisplay>
            </CategoryRow>
          ) : categoriesLoaded ? (
            <EmptyCategoryBlock>
              생성된 노트북이 없습니다. 기본 환경설정에서 노트북 카테고리를 설정해주세요.
            </EmptyCategoryBlock>
          ) : null}

          {/* 2행: 관련멤버 태그 */}
          {/* 3행: 텍스트 입력 필드 */}
          <NoteTextArea
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            onMouseDown={(e) => {
              // 마우스 다운 이벤트 전파 허용
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              // Ctrl+A (Cmd+A on Mac) 전체 선택 허용
              if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
                e.stopPropagation();
                // 기본 동작 허용 (전체 선택)
              }
            }}
            placeholder="메모를 입력하세요..."
            style={{ fontSize: `${fontSize}px` }}
          />

          {/* 4행: 관련멤버 선택, 글자 크기 버튼, 저장 버튼 */}
          <ActionButtonRow>
            {/* 관련멤버 선택 버튼 */}
            <RelatedStudentButton 
              onClick={(e) => {
                // 스크롤 컨테이너나 멤버 태그가 아닌 빈 공간 클릭 시에만 모달 열기
                const target = e.target as HTMLElement;
                if (!target.closest('.related-student-scroll') && 
                    !target.closest('.student-tag') &&
                    !target.closest('.add-student-tag')) {
                  setShowStudentTagModal(true);
                }
              }}
            >
              <RelatedStudentScrollContainer 
                className="related-student-scroll"
                onClick={(e) => {
                  // 스크롤 컨테이너 빈 공간 클릭 시 모달 열기
                  if (e.target === e.currentTarget) {
                    setShowStudentTagModal(true);
                  }
                }}
              >
                {(() => {
                  const totalMembers = classMembers.length;

                  // 모든 멤버가 선택된 경우
                  if (relatedStudents.length > 0 && relatedStudents.length === totalMembers) {
                    return <AddStudentTag>모두</AddStudentTag>;
                  }

                  // 선택된 멤버가 없는 경우
                  if (relatedStudents.length === 0) {
                    return <AddStudentTag>+ 관련 멤버 선택</AddStudentTag>;
                  }

                  // 저장된 relatedStudents 정보를 직접 사용 (모든 그룹의 멤버 정보 포함)
                  
                  // 모든 선택된 멤버를 그룹별로 묶어서 표시 (가로 스크롤로 탐색 가능)
                  const groupedByGroup = relatedStudents.reduce((acc, student) => {
                    const groupName = student.groupName || '기타';
                    if (!acc[groupName]) {
                      acc[groupName] = [];
                    }
                    acc[groupName].push(student);
                    return acc;
                  }, {} as Record<string, typeof relatedStudents>);

                  return (
                    <>
                      {Object.entries(groupedByGroup).map(([groupName, students]) => (
                        <React.Fragment key={groupName}>
                          <GroupNameLabel>{groupName}:</GroupNameLabel>
                          {students.map(student => (
                            <StudentTag key={student.id} className="student-tag">
                              {student.number}번 {student.name}
                            </StudentTag>
                          ))}
                        </React.Fragment>
                      ))}
                      <AddStudentTag>+ 멤버 추가</AddStudentTag>
                    </>
                  );
                })()}
              </RelatedStudentScrollContainer>
            </RelatedStudentButton>
            
            {/* 글자 크기 버튼 */}
            <FontSizeButtonRow>
              <ActionButton onClick={handleDecreaseFontSize}>
                글자 작게
              </ActionButton>
              <ActionButton onClick={handleIncreaseFontSize}>
                글자 크게
              </ActionButton>
            </FontSizeButtonRow>
            
            {/* 저장 버튼과 새 노트 버튼 (모바일에서는 한 행에 등분, 데스크탑에서는 함께 오른쪽 정렬) */}
            <SaveAndNewButtonRow>
              <SaveButton 
                onClick={handleSaveNote} 
                disabled={!!(isSaving || (editingNoteId && !hasNoteChanges))}
              >
                {isSaving 
                  ? (editingNoteId ? '수정 중...' : '저장 중...') 
                  : (editingNoteId ? '수정하기' : '저장하기')}
              </SaveButton>
              <NewNoteButton onClick={handleNewNote}>
                +새노트
              </NewNoteButton>
            </SaveAndNewButtonRow>
          </ActionButtonRow>
        </NotebookFormSection>
      )}

      {/* 주사위 섹션 */}
      {showDiceForm && (
        <DiceFormSection>
          {/* 시작 번호 / 끝 번호 입력 */}
          <NumberInputRow>
            <NumberInputGroup>
              <NumberInputWrapper>
                <NumberInputLabel>시작</NumberInputLabel>
                <NumberInput
                  type="number"
                  value={startNumber}
                  onChange={(e) => handleStartNumberChange(parseInt(e.target.value) || 1)}
                  onFocus={(e) => e.target.select()}
                  min={1}
                  max={999}
                />
                <NumberInputContainer>
                  <IconButton onClick={handleResetStartNumber} title="초기화">
                    <ResetIcon src="/reset.svg" alt="초기화" />
                  </IconButton>
                  <IncrementButton onClick={handleDecrementStart} disabled={startNumber <= 1}>
                    -
                  </IncrementButton>
                  <IncrementButton onClick={handleIncrementStart} disabled={startNumber >= 999 || startNumber >= endNumber}>
                    +
                  </IncrementButton>
                </NumberInputContainer>
              </NumberInputWrapper>
            </NumberInputGroup>
            
            <NumberInputGroup>
              <NumberInputWrapper>
                <NumberInputLabel>끝</NumberInputLabel>
                <NumberInput
                  type="number"
                  value={endNumber}
                  onChange={(e) => handleEndNumberChange(parseInt(e.target.value) || 30)}
                  onFocus={(e) => e.target.select()}
                  min={startNumber}
                  max={999}
                />
                <NumberInputContainer>
                  <IconButton onClick={handleResetEndNumber} title="초기화">
                    <ResetIcon src="/reset.svg" alt="초기화" />
                  </IconButton>
                  <IncrementButton onClick={handleDecrementEnd} disabled={endNumber <= startNumber}>
                    -
                  </IncrementButton>
                  <IncrementButton onClick={handleIncrementEnd} disabled={endNumber >= 999}>
                    +
                  </IncrementButton>
                </NumberInputContainer>
              </NumberInputWrapper>
            </NumberInputGroup>
          </NumberInputRow>

          {/* 번호 뽑기 / 프로필 뽑기 / 팀 나누기 버튼 */}
          <DiceActionButtonRow>
            <ActionButton 
              onClick={() => setSelectedDiceTab('number')}
              style={{
                background: selectedDiceTab === 'number' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                borderColor: selectedDiceTab === 'number' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.45)'
              }}
            >
              <span className="button-text-full">번호 뽑기</span>
              <span className="button-text-short">번호</span>
            </ActionButton>
            <ActionButton 
              onClick={() => setSelectedDiceTab('profile')}
              style={{
                background: selectedDiceTab === 'profile' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                borderColor: selectedDiceTab === 'profile' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.45)'
              }}
            >
              <span className="button-text-full">프로필 뽑기</span>
              <span className="button-text-short">프로필</span>
            </ActionButton>
            <ActionButton 
              onClick={() => setSelectedDiceTab('team')}
              style={{
                background: selectedDiceTab === 'team' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                borderColor: selectedDiceTab === 'team' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.45)'
              }}
            >
              <span className="button-text-full">팀 나누기</span>
              <span className="button-text-short">팀</span>
            </ActionButton>
          </DiceActionButtonRow>

          {/* 탭별 콘텐츠 */}
          {selectedDiceTab && (
            <DiceTabContent>
              {/* 결과 섹션 */}
              <ResultSection>
                <ResultHeader>
                  {/* 연속 뽑기 입력 (팀 나누기 모드에서는 제외) - 왼쪽 정렬 */}
                  <ResultHeaderLeft>
                    {selectedDiceTab !== 'team' && (
                      <ContinuousInputWrapper>
                        <ContinuousLabel>연속</ContinuousLabel>
                        <ContinuousInput
                          type="number"
                          value={continuousCount}
                          onChange={(e) => handleContinuousCountChange(parseInt(e.target.value) || 1)}
                          onFocus={(e) => e.target.select()}
                          min={1}
                          max={100}
                        />
                        <IncrementButton onClick={handleDecrementContinuous} disabled={continuousCount <= 1}>
                          -
                        </IncrementButton>
                        <IncrementButton onClick={handleIncrementContinuous} disabled={continuousCount >= 100}>
                          +
                        </IncrementButton>
                      </ContinuousInputWrapper>
                    )}
                    {/* 모바일에서만 여기에 표시 */}
                    <ResultHeaderRightMobile>
                      {selectedDiceTab === 'number' && (
                        <>
                          {(numberRemainingCount < (endNumber - startNumber + 1)) && (
                            <RemainingCount>
                              남은 수: {numberRemainingCount}
                            </RemainingCount>
                          )}
                          <ResetResultButton onClick={handleResetNumberResult}>초기화</ResetResultButton>
                        </>
                      )}
                      {selectedDiceTab === 'profile' && (
                        <>
                          {(profileRemainingCount < profileAvailableMembers.length) && (
                            <RemainingCount>
                              남은 수: {profileRemainingCount}
                            </RemainingCount>
                          )}
                          <ResetResultButton onClick={handleResetProfileResult}>초기화</ResetResultButton>
                        </>
                      )}
                      {selectedDiceTab === 'team' && (
                        <ResetResultButton onClick={handleResetTeamResult}>초기화</ResetResultButton>
                      )}
                    </ResultHeaderRightMobile>
                  </ResultHeaderLeft>
                  
                  {/* 주사위 버튼 - 중앙 정렬 */}
                  <ResultHeaderCenter>
                    <ResultDiceButton
                      onClick={() => {
                        if (selectedDiceTab === 'number') {
                          handleDrawNumber();
                        } else if (selectedDiceTab === 'profile') {
                          handleDrawProfile();
                        } else if (selectedDiceTab === 'team') {
                          setShowTeamCountModal(true);
                        }
                      }}
                    >
                      <img src="/Dice.svg" alt="주사위" />
                    </ResultDiceButton>
                  </ResultHeaderCenter>
                  
                  {/* 남은 수와 초기화 버튼 - 오른쪽 정렬 */}
                  <ResultHeaderRight>
                    {(selectedDiceTab === 'number' && numberRemainingCount < (endNumber - startNumber + 1)) || 
                     (selectedDiceTab === 'profile' && profileRemainingCount < profileAvailableMembers.length) ? (
                      <RemainingCount>
                        남은 수: {selectedDiceTab === 'number' ? numberRemainingCount : profileRemainingCount}
                      </RemainingCount>
                    ) : null}
                    <ResetResultButton 
                      onClick={() => {
                        if (selectedDiceTab === 'number') {
                          handleResetNumberResult();
                        } else if (selectedDiceTab === 'profile') {
                          handleResetProfileResult();
                        } else if (selectedDiceTab === 'team') {
                          handleResetTeamResult();
                        }
                      }}
                    >
                      초기화
                    </ResetResultButton>
                  </ResultHeaderRight>
                </ResultHeader>
                
                <ResultContent>
                  {/* 번호 뽑기 결과 */}
                  {selectedDiceTab === 'number' && (
                    <>
                      {/* 단일 결과도 그리드 형식으로 통일 */}
                      {(numberResult !== null && numberResults.length === 0) && (
                        <NumberResultsGrid>
                          <NumberResultItem>{numberResult}</NumberResultItem>
                        </NumberResultsGrid>
                      )}
                      {/* 연속 결과 */}
                      {numberResults.length > 0 && (
                        <NumberResultsGrid>
                          {numberResults.map((num, index) => (
                            <NumberResultItem key={index}>{num}</NumberResultItem>
                          ))}
                        </NumberResultsGrid>
                      )}
                    </>
                  )}

                  {/* 프로필 뽑기 결과 */}
                  {selectedDiceTab === 'profile' && (
                    <>
                      {/* 단일 결과도 그리드 형식으로 통일 */}
                      {(profileResult && profileResults.length === 0) && (
                        <ProfileResultsGrid>
                          <ProfileResultCard>
                            <ProfileResultCardImageWrapper>
                              <CachedProfileImage
                                imageId={resolveMemberProfileSource(profileResult, memberProfileDisplayMode)}
                                alt={profileResult.name || '프로필'}
                                fallbackImageId="basic-1"
                              />
                            </ProfileResultCardImageWrapper>
                            <ProfileResultCardInfo>
                              {profileResult.number}번 {profileResult.name}
                            </ProfileResultCardInfo>
                          </ProfileResultCard>
                        </ProfileResultsGrid>
                      )}
                      {/* 연속 결과 */}
                      {profileResults.length > 0 && (
                        <ProfileResultsGrid>
                          {profileResults.map((member, index) => (
                            <ProfileResultCard key={index}>
                              <ProfileResultCardImageWrapper>
                                <CachedProfileImage
                                        imageId={resolveMemberProfileSource(member, memberProfileDisplayMode)}
                                        alt={member.name || '프로필'}
                                        fallbackImageId="basic-0"
                                />
                              </ProfileResultCardImageWrapper>
                              <ProfileResultCardInfo>
                                {member.number}번 {member.name}
                              </ProfileResultCardInfo>
                            </ProfileResultCard>
                          ))}
                        </ProfileResultsGrid>
                      )}
                      <ProfileScrollBottomSpacer />
                    </>
                  )}

                  {/* 팀 나누기 결과 */}
                  {selectedDiceTab === 'team' && teamResult !== null && (
                    <TeamResult $isMeasuring={isTeamBlockMeasuring || !maxTeamBlockWidth}>
                      {teamResult.map((team, teamIndex) => (
                        <TeamBlock 
                          key={teamIndex}
                          ref={(el) => {
                            teamBlockRefs.current[teamIndex] = el;
                          }}
                          style={maxTeamBlockWidth ? { width: `${maxTeamBlockWidth}px`, maxWidth: '100%' } : undefined}
                        >
                          <TeamTitle>팀 {teamIndex + 1}</TeamTitle>
                          <TeamMembersGrid>
                            {team.map((member: ClassMember) => (
                              <TeamMemberCard key={member.id}>
                                <TeamMemberImage>
                                  <CachedProfileImage
                                        imageId={resolveMemberProfileSource(member, memberProfileDisplayMode)}
                                        alt={member.name || '프로필'}
                                        fallbackImageId="basic-0"
                                  />
                                </TeamMemberImage>
                                <TeamMemberInfo>
                                  {member.number}번 {member.name}
                                </TeamMemberInfo>
                              </TeamMemberCard>
                            ))}
                          </TeamMembersGrid>
                        </TeamBlock>
                      ))}
                    </TeamResult>
                  )}
                </ResultContent>
              </ResultSection>
            </DiceTabContent>
          )}
        </DiceFormSection>
      )}

      {/* 팀 수 입력 모달 */}
      {showTeamCountModal && (
        <TeamCountModalOverlay onClick={() => setShowTeamCountModal(false)}>
          <TeamCountModalContent onClick={(e) => e.stopPropagation()}>
            <TeamCountModalHeader>
              <h3>팀 수 설정</h3>
              <CloseModalButton onClick={() => setShowTeamCountModal(false)}>×</CloseModalButton>
            </TeamCountModalHeader>
            
            <TeamCountInputWrapper>
              <TeamCountLabel>팀 수 (최대 {maxTeamCount}팀)</TeamCountLabel>
              <TeamCountInput
                type="number"
                value={teamCount}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value) && value >= 2 && value <= maxTeamCount) {
                    setTeamCount(value);
                  }
                }}
                min={2}
                max={maxTeamCount}
                placeholder={`2~${maxTeamCount} 사이의 숫자`}
              />
              <TeamCountButtonContainer>
                <TeamCountIncrementButton onClick={handleDecrementTeamCount} disabled={teamCount <= 2}>
                  -
                </TeamCountIncrementButton>
                <TeamCountIconButton onClick={handleResetTeamCount} title="초기화">
                  <img src="/reset.svg" alt="초기화" />
                </TeamCountIconButton>
                <TeamCountIncrementButton onClick={handleIncrementTeamCount} disabled={teamCount >= maxTeamCount}>
                  +
                </TeamCountIncrementButton>
              </TeamCountButtonContainer>
            </TeamCountInputWrapper>
            
            <TeamCountButtonRow>
              <TeamCountButton $variant="secondary" onClick={() => setShowTeamCountModal(false)}>
                취소
              </TeamCountButton>
              <TeamCountButton $variant="primary" onClick={handleDivideTeams}>
                확인
              </TeamCountButton>
            </TeamCountButtonRow>
          </TeamCountModalContent>
        </TeamCountModalOverlay>
      )}

      {/* 관련 멤버 선택 모달 */}
      <StudentTagSelectModal
        isOpen={showStudentTagModal}
        onClose={() => setShowStudentTagModal(false)}
        onConfirm={(selectedStudents) => {
          // StudentTagSelectModal에서 이미 변환된 학생 정보 사용
          setRelatedStudents(selectedStudents);
          setShowStudentTagModal(false);
        }}
        allStudents={classMembers}
        primaryStudentId=""
        initialSelectedIds={relatedStudents.map(s => s.id)}
        currentClass={currentClass}
        user={user}
        userId={user?.id}
        classId={currentClass?.id}
        forceLoadAllGroups={true} // 모든 그룹 멤버 로드 강제
        currentGroup={currentMemberGroup} // 현재 선택된 그룹 전달
      />

      {/* 타임 태그 선택 모달 */}
      <TimeTagSelectModal
        isOpen={showTimeTagSelectModal}
        onClose={() => setShowTimeTagSelectModal(false)}
        availableTimeTags={availableTimeTags}
        selectedTimeTag={selectedTimeTag}
        selectedDayOfWeek={selectedDayOfWeek}
        onTimeTagSelect={(timeTag) => {
          setSelectedTimeTag(timeTag);
          if (timeTag) {
            const days = Array.isArray(timeTag.dayOfWeek) ? timeTag.dayOfWeek : [timeTag.dayOfWeek];
            const dayToSelect = days.includes(selectedDayOfWeek as any) ? selectedDayOfWeek : days[0];
            setSelectedDayOfWeek(dayToSelect);
          }
          setShowTimeTagSelectModal(false);
        }}
        onDayOfWeekChange={setSelectedDayOfWeek}
      />

      {/* 노트 목록 모달 */}
      {showNoteListModal && (
        <NoteListModalOverlay onClick={() => setShowNoteListModal(false)}>
          <NoteListModalContent onClick={(e) => e.stopPropagation()}>
            <NoteListHeader>
              <h3>
                {noteListCategory === '전체공지노트'
                  ? '전체공지'
                  : noteListCategory === '그룹공지노트'
                    ? '그룹공지'
                    : noteListCategory === '개별공지노트'
                      ? '개별공지'
                      : `${noteListCategory} 노트`}
                목록
              </h3>
              <CloseButton onClick={() => setShowNoteListModal(false)}>×</CloseButton>
            </NoteListHeader>
            
            {/* 페이지네이션 버튼 (목록 상단, 중앙 정렬) */}
            {allNotes.length > 0 && (() => {
              const totalPages = Math.ceil(allNotes.length / NOTES_PER_PAGE);
              
              if (totalPages > 1) {
                return (
                  <PaginationButtonsGroup style={{ marginBottom: '16px', justifyContent: 'center' }}>
                    <PaginationButton
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15 18 9 12 15 6"></polyline>
                      </svg>
                    </PaginationButton>
                    <PaginationInfo>{currentPage} / {totalPages}</PaginationInfo>
                    <PaginationButton
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </PaginationButton>
                  </PaginationButtonsGroup>
                );
              }
              return null;
            })()}
            
            <NoteListModalBody>
              {allNotes.length === 0 ? (
                <EmptyNoteList>
                  {noteListCategory === '전체공지노트'
                    ? '전체공지가 없습니다.'
                    : noteListCategory === '그룹공지노트'
                      ? '그룹공지가 없습니다.'
                      : noteListCategory === '개별공지노트'
                        ? '개별공지가 없습니다.'
                        : '노트가 없습니다.'}
                </EmptyNoteList>
              ) : (
                <>
                  {(() => {
                    // 페이지네이션 계산
                    const startIndex = (currentPage - 1) * NOTES_PER_PAGE;
                    const endIndex = startIndex + NOTES_PER_PAGE;
                    const currentNotes = allNotes.slice(startIndex, endIndex);
                    
                    // 날짜 포맷 함수
                    const formatDate = (dateStr: string) => {
                      if (!dateStr) return '';
                      try {
                        const date = new Date(dateStr);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        return `${year}년 ${month}월 ${day}일`;
                      } catch {
                        return dateStr;
                      }
                    };
                    
                    // 관련멤버 표시 함수 (없으면 null 반환)
                    const formatRelatedStudents = (relatedStudents?: RelatedStudentInfo[]): string | null => {
                      if (!relatedStudents || relatedStudents.length === 0) {
                        return null;
                      }

                      const totalMembers = classMembers.length;

                      // 모든 멤버가 선택된 경우
                      if (relatedStudents.length === totalMembers) {
                        return '모두';
                      }

                      // 1명만 선택된 경우
                      if (relatedStudents.length === 1) {
                        return `${relatedStudents[0].number}번 ${relatedStudents[0].name}`;
                      }

                      // 여러 명 선택된 경우 (최대 1명 표시 후 "외 n명")
                      if (relatedStudents.length > 1) {
                        const firstStudent = relatedStudents[0];
                        const restCount = relatedStudents.length - 1;
                        return `${firstStudent.number}번 ${firstStudent.name} 외 ${restCount}명`;
                      }

                      return null;
                    };
                    
                    return (
                      <>
                        {currentNotes.map((note) => {
                          const relatedStudentsText = formatRelatedStudents(note.relatedStudents || []);
                          
                          const isDeleted = deletedNoteIds.has(note.id);
                          const isExpanded = expandedNoteId === note.id;
                          // 목록 블럭에 모든 텍스트 표시 (말줄임 없음)
                          const contentDisplay = ((note.content || '').toString().trim()) || '(내용 없음)';
                          
                          return (
                            <NoteListItem
                              key={note.id}
                              $isDeleted={isDeleted}
                              $expanded={isExpanded}
                              onClick={() => handleSelectNote(note)}
                            >
                              <NoteListItemDeleteIcon
                                onClick={(e) => handleToggleDeleteNote(note.id, e)}
                                title={isDeleted ? "삭제 취소" : "삭제 표시"}
                              >
                                <TrashIcon viewBox="0 0 24 24" strokeWidth="2">
                                  <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                                </TrashIcon>
                              </NoteListItemDeleteIcon>
                              <NoteListItemHeader>
                                <NoteListItemHeaderRow>
                                  <NoteListItemDate>
                                    {formatDate(note.date)}
                                  </NoteListItemDate>
                                  {(noteListCategory === '전체공지노트' || noteListCategory === '그룹공지노트') && (
                                    <NoticeScopeBadge $scope={(note.noticeScope || 'all') === 'group' ? 'group' : 'all'}>
                                      {(note.noticeScope || 'all') === 'group' ? '그룹' : '전체'}
                                    </NoticeScopeBadge>
                                  )}
                                </NoteListItemHeaderRow>
                                {(note.timeTagName || relatedStudentsText) && (
                                  <NoteListItemHeaderRow>
                                    <NoteListItemHeaderRight>
                                      {note.timeTagName && (
                                        <NoteListItemTimeTag>
                                          {(() => {
                                            const parsed = parseTimeTagLabel(note.timeTagName);
                                            return `[${parsed.day}] ${parsed.name}`;
                                          })()}
                                        </NoteListItemTimeTag>
                                      )}
                                      {relatedStudentsText && (
                                        <NoteListItemStudents>
                                          {relatedStudentsText}
                                        </NoteListItemStudents>
                                      )}
                                    </NoteListItemHeaderRight>
                                  </NoteListItemHeaderRow>
                                )}
                              </NoteListItemHeader>
                              <NoteListItemContent $expanded={true}>
                                {contentDisplay}
                              </NoteListItemContent>
                            </NoteListItem>
                          );
                        })}
                      </>
                    );
                  })()}
                </>
              )}
            </NoteListModalBody>
            
            {/* 삭제 버튼 (스크롤 영역 밖) */}
            {allNotes.length > 0 && (
              <PaginationContainer>
                <div style={{ flex: 1 }} />
                <DeleteButton
                  onClick={handleOpenDeleteConfirm}
                  disabled={deletedNoteIds.size === 0}
                >
                  삭제하기 ({deletedNoteIds.size})
                </DeleteButton>
              </PaginationContainer>
            )}
          </NoteListModalContent>
        </NoteListModalOverlay>
      )}

      {/* 개별공지: 학생 선택 모달 */}
      {showIndividualStudentPickModal && (
        <NoteListModalOverlay 
          onClick={() => {
            // 개별 학생 목록 모달이 열려있으면 학생 선택 모달만 닫지 않음
            if (!showIndividualNoticeListModal) {
              setShowIndividualStudentPickModal(false);
            }
          }}
        >
          <NoteListModalContent onClick={(e) => e.stopPropagation()}>
            <NoteListHeader>
              <h3>개별공지</h3>
              <CloseButton onClick={() => {
                setShowIndividualStudentPickModal(false);
                // 학생 선택 모달을 닫을 때 개별 학생 목록 모달도 함께 닫기
                if (showIndividualNoticeListModal) {
                  setShowIndividualNoticeListModal(false);
                }
              }}>×</CloseButton>
            </NoteListHeader>
            
            {/* 탭 버튼 */}
            <div style={{ 
              display: 'flex', 
              borderBottom: '1px solid var(--color-border)',
              marginBottom: '24px',
              paddingBottom: '8px',
              background: 'var(--color-surface)',
            }}>
              <button
                onClick={() => setIndividualNoticeViewMode('date')}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  border: 'none',
                  borderRight: '1px solid var(--color-border)',
                  background: individualNoticeViewMode === 'date' ? 'var(--color-accent-strong)' : 'var(--color-panel)',
                  color: individualNoticeViewMode === 'date' ? '#ffffff' : 'var(--color-text-secondary)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  if (individualNoticeViewMode !== 'date') {
                    e.currentTarget.style.background = 'var(--color-border)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (individualNoticeViewMode !== 'date') {
                    e.currentTarget.style.background = 'var(--color-panel)';
                  }
                }}
              >
                날짜별 조회
              </button>
              <button
                onClick={() => setIndividualNoticeViewMode('student')}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  border: 'none',
                  background: individualNoticeViewMode === 'student' ? 'var(--color-accent-strong)' : 'var(--color-panel)',
                  color: individualNoticeViewMode === 'student' ? '#ffffff' : 'var(--color-text-secondary)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  if (individualNoticeViewMode !== 'student') {
                    e.currentTarget.style.background = 'var(--color-border)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (individualNoticeViewMode !== 'student') {
                    e.currentTarget.style.background = 'var(--color-panel)';
                  }
                }}
              >
                개인별 조회
              </button>
            </div>
            
            <NoteListModalBody>
              {individualNoticeViewMode === 'date' ? (
                // 날짜별 조회 탭
                <>
                  {dateBasedIndividualNotices.length === 0 ? (
                    <EmptyNoteList>날짜별 개별공지가 없습니다.</EmptyNoteList>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* 모든 날짜별 공지 목록 표시 */}
                      {dateBasedIndividualNotices.map((dateNotice, index) => {
                        const isDeleted = deletedDateBasedNoticeKeys.has(dateNotice.groupKey);
              return (
                          <div 
                            key={dateNotice.groupKey}
                            onClick={() => !isDeleted && loadDateBasedNoticeToBoard(dateNotice, true)}
                            style={{
                              padding: '16px',
                              border: `2px solid ${isDeleted ? 'var(--color-status-error)' : 'var(--color-border)'}`,
                              borderRadius: '8px',
                              cursor: isDeleted ? 'default' : 'pointer',
                              transition: 'all 0.2s ease',
                              background: isDeleted ? 'var(--color-surface-highlight)' : 'var(--color-surface)',
                              textAlign: 'center',
                              position: 'relative',
                              opacity: isDeleted ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!isDeleted) {
                                e.currentTarget.style.borderColor = 'var(--color-accent-strong)';
                                e.currentTarget.style.background = 'var(--color-panel)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isDeleted) {
                                e.currentTarget.style.borderColor = 'var(--color-border)';
                                e.currentTarget.style.background = 'var(--color-surface)';
                              }
                            }}
                          >
                            {/* 휴지통 아이콘 */}
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletedDateBasedNoticeKeys(prev => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(dateNotice.groupKey)) {
                                    newSet.delete(dateNotice.groupKey);
                                  } else {
                                    newSet.add(dateNotice.groupKey);
                                  }
                                  return newSet;
                                });
                              }}
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                transition: 'background 0.2s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--color-panel)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                              title={isDeleted ? "삭제 취소" : "삭제 표시"}
                            >
                              <TrashIcon viewBox="0 0 24 24" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                                <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                              </TrashIcon>
                            </div>
                            <div style={{ 
                              fontSize: '18px', 
                              fontWeight: 700, 
                              color: 'var(--color-text-primary)',
                              marginBottom: '8px',
                              paddingRight: '32px', // 휴지통 아이콘 공간 확보
                            }}>
                              {dateNotice.dateFormatted}
                            </div>
                            <div style={{ 
                              fontSize: '14px', 
                              color: 'var(--color-text-secondary)',
                            }}>
                              개별공지 멤버수: {dateNotice.notes.length}명
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* 삭제하기 버튼 (항상 표시, 비활성 상태로 시작) */}
                  {dateBasedIndividualNotices.length > 0 && (
                <PaginationContainer>
                      <div style={{ flex: 1 }} />
                      <DeleteButton
                        onClick={handleOpenDeleteDateBasedConfirm}
                        disabled={deletedDateBasedNoticeKeys.size === 0}
                      >
                        삭제하기 ({deletedDateBasedNoticeKeys.size})
                      </DeleteButton>
                    </PaginationContainer>
                  )}
                </>
              ) : (
                // 개인별 조회 탭 (기존 학생 선택 목록)
                <StudentPickList>
                  {classMembers.map((m) => (
                    <StudentPickItem
                      key={m.id}
                      onClick={async () => {
                        // 학생 선택 모달은 닫지 않고 유지
                        await loadIndividualNoticesForStudent(m);
                      }}
                    >
                      <StudentPickItemContent>
                        {m.number != null && m.number !== '' && (
                          <StudentPickItemNumber>{m.number}번</StudentPickItemNumber>
                        )}
                        <StudentPickItemName>{m.name || '학생'}</StudentPickItemName>
                      </StudentPickItemContent>
                      <StudentNoticeCountBadge>
                        {studentNoticeCounts[m.id] ?? 0}
                      </StudentNoticeCountBadge>
                    </StudentPickItem>
                  ))}
                </StudentPickList>
              )}
            </NoteListModalBody>
          </NoteListModalContent>
        </NoteListModalOverlay>
      )}

      {/* 개별공지: 학생별 목록 모달 */}
      {showIndividualNoticeListModal && selectedIndividualStudent && (
        <>
        <NoteListModalOverlay 
          style={{ zIndex: 2008 }} 
          onClick={() => {
            // 수정 모드가 열려있으면 먼저 닫기
            if (editingIndividualNoticeItemId) {
              handleCancelEditIndividualNotice();
            } else {
              setShowIndividualNoticeListModal(false);
            }
          }}
        >
          <NoteListModalContent 
            style={{ zIndex: 2009 }}
            onClick={(e) => {
              e.stopPropagation();
              // 모달 콘텐츠 영역을 클릭했을 때 수정 모드가 열려있으면 닫기
              // 단, NoteListItem이나 그 내부 요소를 클릭한 경우는 제외
              if (editingIndividualNoticeItemId && !(e.target as HTMLElement).closest('.note-list-item')) {
                handleCancelEditIndividualNotice();
              }
            }}
          >
            <NoteListHeader>
              <h3>
                개별공지 - {selectedIndividualStudent.number ? `${selectedIndividualStudent.number}번 ` : ''}
                {selectedIndividualStudent.name || ''}
              </h3>
              <NoteListModalActions>
                <NoteListModalActionButton onClick={handleSaveIndividualNoticeAsPdf} title="PDF 저장">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                </NoteListModalActionButton>
                <NoteListModalActionButton onClick={handlePrintIndividualNotice} title="프린트">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                  </svg>
                </NoteListModalActionButton>
              <CloseButton onClick={() => {
                // 수정 모드가 열려있으면 먼저 닫기
                if (editingIndividualNoticeItemId) {
                  handleCancelEditIndividualNotice();
                } else {
                  setShowIndividualNoticeListModal(false);
                }
              }}>×</CloseButton>
              </NoteListModalActions>
            </NoteListHeader>

            {/* 페이지네이션 버튼 (목록 상단, 중앙 정렬) */}
            {individualNoticeList.length > 0 && (() => {
              const totalPages = Math.ceil(individualNoticeList.length / NOTES_PER_PAGE);
              if (totalPages > 1) {
              return (
                  <PaginationButtonsGroup style={{ marginBottom: '16px', justifyContent: 'center' }}>
                    <PaginationButton
                      onClick={() => setIndividualNoticePage(prev => Math.max(1, prev - 1))}
                      disabled={individualNoticePage === 1}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15 18 9 12 15 6"></polyline>
                      </svg>
                    </PaginationButton>
                    <PaginationInfo>{individualNoticePage} / {totalPages}</PaginationInfo>
                    <PaginationButton
                      onClick={() => setIndividualNoticePage(prev => Math.min(totalPages, prev + 1))}
                      disabled={individualNoticePage === totalPages}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </PaginationButton>
                  </PaginationButtonsGroup>
                );
              }
              return null;
            })()}

            <NoteListModalBody ref={individualNoticeListModalBodyRef}>
              {individualNoticeList.length === 0 ? (
                <EmptyNoteList>개별공지가 없습니다.</EmptyNoteList>
              ) : (
                <>
                  <IndividualNoticePdfHeader data-pdf-element="header">
                    개별공지 - {selectedIndividualStudent.number != null && selectedIndividualStudent.number !== '' ? `${selectedIndividualStudent.number}번 ` : ''}
                    {selectedIndividualStudent.name || ''}
                  </IndividualNoticePdfHeader>
                  {(() => {
                    const startIndex = (individualNoticePage - 1) * NOTES_PER_PAGE;
                    const endIndex = startIndex + NOTES_PER_PAGE;
                    const currentNotes = individualNoticeList.slice(startIndex, endIndex);
                    const formatDate = (dateStr: string) => {
                      if (!dateStr) return '';
                      try {
                        const date = new Date(dateStr);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        return `${year}년 ${month}월 ${day}일`;
                      } catch {
                        return dateStr;
                      }
                    };
                    return (
                      <>
                        {currentNotes.map((note) => {
                          const isExpanded = expandedIndividualNoticeId === note.id;
                          const isDeleted = deletedIndividualNoticeIds.has(note.id);
                          const isEditing = editingIndividualNoticeItemId === note.id;
                          const fullText = note.content || '';
                          const lines = fullText.split('\n');
                          const titleLine = lines[0] || '';
                          const restContent = lines.slice(1).join('\n');
                          
                          return (
                            <NoteListItem
                              key={note.id}
                              className="note-list-item"
                              data-pdf-element="row"
                              $expanded={isExpanded || isEditing}
                              $isDeleted={isDeleted}
                              onClick={() => {
                                // 다른 목록을 클릭했을 때 수정 모드가 열려있으면 닫기
                                if (editingIndividualNoticeItemId && editingIndividualNoticeItemId !== note.id) {
                                  handleCancelEditIndividualNotice();
                                  // 다른 목록을 확장
                                  setExpandedIndividualNoticeId(note.id);
                                } else if (!isEditing) {
                                  // 수정 모드가 아닐 때만 확장/축소 토글
                                  if (expandedIndividualNoticeId === note.id) {
                                    setExpandedIndividualNoticeId(null);
                                  } else {
                                    setExpandedIndividualNoticeId(note.id);
                                  }
                                }
                              }}
                            >
                              <NoteListItemDeleteIcon
                                onClick={(e) => handleToggleDeleteIndividualNotice(note.id, e)}
                                title={isDeleted ? "삭제 취소" : "삭제 표시"}
                                disabled={isEditing}
                              >
                                <TrashIcon viewBox="0 0 24 24" strokeWidth="2">
                                  <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                                </TrashIcon>
                              </NoteListItemDeleteIcon>
                              <NoteListItemEditIcon
                                onClick={(e) => handleStartEditIndividualNotice(note.id, fullText, e)}
                                title="수정"
                                disabled={isEditing}
                              >
                                <EditIcon viewBox="0 0 24 24" strokeWidth="2" fill="none">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
                                </EditIcon>
                              </NoteListItemEditIcon>
                              <NoteListItemHeader>
                                <NoteListItemHeaderRow>
                                  <NoteListItemDate>
                                    {formatDate(note.date)}
                                  </NoteListItemDate>
                                  {noteListCategory === '전체공지노트' && (
                                    <NoticeScopeBadge $scope={(note.noticeScope || 'all') === 'group' ? 'group' : 'all'}>
                                      {(note.noticeScope || 'all') === 'group' ? '그룹' : '전체'}
                                    </NoticeScopeBadge>
                                  )}
                                </NoteListItemHeaderRow>
                              </NoteListItemHeader>
                              {isEditing ? (
                                <>
                                  <NoteListItemEditTextarea
                                    value={editingIndividualNoticeContent}
                                    onChange={(e) => setEditingIndividualNoticeContent(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="개별공지 내용을 입력하세요"
                                  />
                                  <NoteListItemEditButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSaveEditIndividualNotice();
                                    }}
                                    disabled={editingIndividualNoticeContent.trim() === fullText.trim()}
                                  >
                                    수정하기
                                  </NoteListItemEditButton>
                                </>
                              ) : (
                                <>
                                  <NoteListItemContent $expanded={true}>
                                    {titleLine || '(내용 없음)'}
                                  </NoteListItemContent>
                                  {restContent ? (
                                    <NoteListItemFull>{restContent}</NoteListItemFull>
                                  ) : null}
                                </>
                              )}
                            </NoteListItem>
                          );
                        })}
                      </>
                    );
                  })()}
                </>
              )}
            </NoteListModalBody>
            
            {/* 삭제 버튼 (스크롤 영역 밖) */}
            {individualNoticeList.length > 0 && (
              <PaginationContainer>
                <div style={{ flex: 1 }} />
                  <DeleteButton
                  onClick={handleOpenDeleteIndividualNoticeConfirm}
                  disabled={deletedIndividualNoticeIds.size === 0}
                  >
                  삭제하기 ({deletedIndividualNoticeIds.size})
                  </DeleteButton>
                </PaginationContainer>
            )}
          </NoteListModalContent>
        </NoteListModalOverlay>

        {/* PDF/프린트용 전체 목록 컨테이너 (화면 밖, 전체 페이지 캡처용) */}
        {isExportingIndividualNotice && individualNoticeList.length > 0 && (
          <div
            ref={individualNoticeExportContainerRef}
            data-theme="light"
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '800px',
              zIndex: -1,
              background: 'var(--color-surface)',
              padding: '24px',
            }}
          >
            <IndividualNoticePdfHeader data-pdf-element="header">
              개별공지 - {selectedIndividualStudent.number != null && selectedIndividualStudent.number !== '' ? `${selectedIndividualStudent.number}번 ` : ''}
              {selectedIndividualStudent.name || ''}
            </IndividualNoticePdfHeader>
            {individualNoticeList.map((note) => {
              const fullText = note.content || '';
              const lines = fullText.split('\n');
              const titleLine = lines[0] || '';
              const restContent = lines.slice(1).join('\n');
              const formatDate = (dateStr: string) => {
                if (!dateStr) return '';
                try {
                  const date = new Date(dateStr);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  return `${year}년 ${month}월 ${day}일`;
                } catch {
                  return dateStr;
                }
              };
              return (
                <NoteListItem
                  key={note.id}
                  data-pdf-element="row"
                  $expanded={true}
                  $isDeleted={false}
                  style={{ cursor: 'default' }}
                >
                  <NoteListItemHeader>
                    <NoteListItemHeaderRow>
                      <NoteListItemDate>{formatDate(note.date)}</NoteListItemDate>
                    </NoteListItemHeaderRow>
                  </NoteListItemHeader>
                  <NoteListItemContent $expanded={true}>
                    {titleLine || '(내용 없음)'}
                  </NoteListItemContent>
                  {restContent ? (
                    <NoteListItemFull>{restContent}</NoteListItemFull>
                  ) : null}
                </NoteListItem>
              );
            })}
          </div>
        )}
        </>
      )}

      {/* 개별공지 삭제 확인 모달 */}
      {showDeleteIndividualNoticeConfirmModal && (
        <DeleteConfirmModalOverlay 
          style={{ zIndex: 2010 }}
          onClick={() => setShowDeleteIndividualNoticeConfirmModal(false)}
        >
          <DeleteConfirmModalContent 
            style={{ zIndex: 2011 }}
            onClick={(e) => e.stopPropagation()}
          >
            <DeleteConfirmModalHeader>
              <h3>개별공지 노트 삭제 확인</h3>
              <CloseButton onClick={() => setShowDeleteIndividualNoticeConfirmModal(false)}>×</CloseButton>
            </DeleteConfirmModalHeader>
            <DeleteConfirmModalBody>
              <p>선택한 {deletedIndividualNoticeIds.size}개의 개별공지 노트를 삭제하시겠습니까?</p>
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginTop: '8px' }}>
                이 작업은 되돌릴 수 없습니다.
              </p>
            </DeleteConfirmModalBody>
            <DeleteConfirmModalFooter>
              <DeleteConfirmCancelButton onClick={() => setShowDeleteIndividualNoticeConfirmModal(false)}>
                취소
              </DeleteConfirmCancelButton>
              <DeleteConfirmDeleteButton onClick={handleDeleteIndividualNotices}>
                삭제하기
              </DeleteConfirmDeleteButton>
            </DeleteConfirmModalFooter>
          </DeleteConfirmModalContent>
        </DeleteConfirmModalOverlay>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirmModal && (
        <DeleteConfirmModalOverlay onClick={() => setShowDeleteConfirmModal(false)}>
          <DeleteConfirmModalContent onClick={(e) => e.stopPropagation()}>
            <DeleteConfirmModalHeader>
              <h3>노트 삭제 확인</h3>
              <CloseButton onClick={() => setShowDeleteConfirmModal(false)}>×</CloseButton>
            </DeleteConfirmModalHeader>
            <DeleteConfirmModalBody>
              <p>선택한 {deletedNoteIds.size}개의 노트를 삭제하시겠습니까?</p>
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginTop: '8px' }}>
                이 작업은 되돌릴 수 없습니다.
              </p>
            </DeleteConfirmModalBody>
            <DeleteConfirmModalFooter>
              <DeleteConfirmCancelButton onClick={() => setShowDeleteConfirmModal(false)}>
                취소
              </DeleteConfirmCancelButton>
              <DeleteConfirmDeleteButton onClick={handleDeleteNotes}>
                삭제하기
              </DeleteConfirmDeleteButton>
            </DeleteConfirmModalFooter>
          </DeleteConfirmModalContent>
        </DeleteConfirmModalOverlay>
      )}

      {/* 날짜별 개별공지 삭제 확인 모달 */}
      {showDeleteDateBasedConfirmModal && (
        <DeleteConfirmModalOverlay onClick={() => setShowDeleteDateBasedConfirmModal(false)}>
          <DeleteConfirmModalContent onClick={(e) => e.stopPropagation()}>
            <DeleteConfirmModalHeader>
              <h3>개별공지 삭제 확인</h3>
              <CloseButton onClick={() => setShowDeleteDateBasedConfirmModal(false)}>×</CloseButton>
            </DeleteConfirmModalHeader>
            <DeleteConfirmModalBody>
              <p>선택한 {deletedDateBasedNoticeKeys.size}개의 날짜별 공지를 삭제하시겠습니까?</p>
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginTop: '8px' }}>
                이 작업은 되돌릴 수 없습니다.
              </p>
            </DeleteConfirmModalBody>
            <DeleteConfirmModalFooter>
              <DeleteConfirmCancelButton onClick={() => setShowDeleteDateBasedConfirmModal(false)}>
                취소
              </DeleteConfirmCancelButton>
              <DeleteConfirmDeleteButton onClick={handleDeleteDateBasedNotices}>
                삭제하기
              </DeleteConfirmDeleteButton>
            </DeleteConfirmModalFooter>
          </DeleteConfirmModalContent>
        </DeleteConfirmModalOverlay>
      )}

      {/* 타이머 모달 */}
      
      {/* 라이센스 제한 모달 */}
      <LicenseLimitModal
        isOpen={showLicenseLimitModal}
        message={licenseLimitMessage}
        onClose={() => setShowLicenseLimitModal(false)}
        onUpgrade={() => {
          setShowLicenseLimitModal(false);
          onLicenseClick?.();
        }}
      />

      {/* 노트북 잠금 카테고리 열기 - 노트 잠금 비밀번호 입력 모달 */}
      {showNotebookLockModal && notebookLockCategory && (
        <NotebookLockModalOverlay onClick={() => !notebookLockSubmitting && (setShowNotebookLockModal(false), setNotebookLockCategory(null), setNotebookLockPassword(''), setNotebookLockError(''))}>
          <NotebookLockModalContent onClick={(e) => e.stopPropagation()}>
            <NotebookLockModalTitle>잠금 비밀번호 입력</NotebookLockModalTitle>
            <NotebookLockModalDesc>
              &quot;{notebookLockCategory.name}&quot; 카테고리는 잠금 설정되어 있습니다. 이 노트의 잠금 비밀번호를 입력해 주세요.
            </NotebookLockModalDesc>
            <NotebookLockModalInput
              type="password"
              value={notebookLockPassword}
              onChange={(e) => { setNotebookLockPassword(e.target.value); setNotebookLockError(''); }}
              placeholder="잠금 비밀번호"
              autoComplete="off"
              disabled={notebookLockSubmitting}
              onKeyDown={(e) => e.key === 'Enter' && handleNotebookLockSubmit()}
            />
            {notebookLockError ? <NotebookLockModalError>{notebookLockError}</NotebookLockModalError> : null}
            <NotebookLockModalButtons>
              <NotebookLockModalButton
                type="button"
                onClick={() => {
                  setShowNotebookLockModal(false);
                  setNotebookLockCategory(null);
                  setNotebookLockPassword('');
                  setNotebookLockError('');
                }}
                disabled={notebookLockSubmitting}
              >
                취소
              </NotebookLockModalButton>
              <NotebookLockModalButton $primary type="button" onClick={handleNotebookLockSubmit} disabled={notebookLockSubmitting || !notebookLockPassword.trim()}>
                {notebookLockSubmitting ? '확인 중...' : '확인'}
              </NotebookLockModalButton>
            </NotebookLockModalButtons>
          </NotebookLockModalContent>
        </NotebookLockModalOverlay>
      )}
      
      {/* 토스트 메시지 */}
      {showToast && (
        <ToastOverlay>
          <ToastMessage>
            {toastMessage}
          </ToastMessage>
        </ToastOverlay>
      )}
    </BoardContainer>
  );
};

// 토스트 메시지 스타일
const ToastOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ToastMessage = styled.div`
  background: #ffffff;
  color: #212529;
  padding: 20px 32px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease-out;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

export default BoardPage;
